import MaturixStrengthUnit from '../../enums/MaturixStrengthUnit'
import MaturixTemperatureUnit from '../../enums/MaturixTemperatureUnit'
import { TagType } from '../../enums/TagType'
import { getBandTitleByTagType } from '../../utils/TagHelper'
import { brandName, brandShortName } from '../brand'
import LanguageTreeType from '../LanguageTreeType'
import countries from './countries'
import timezones from './timezones'

const ending = (x: number, single: string = '', plural: string = 's') =>
  x === 1 ? single : plural

const deliveries = (x: number) => `Livraison${ending(x)}`
const plans = (x: number) => `Plan${ending(x)}`
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const workflows = (x: number) => `Workflow${ending(x, '', 's')}`
const users = (x: number) => `Utilisateur${ending(x)}`
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const activities = (x: number) => `Activité${ending(x)}`
const sensors = (x: number) => `Capteur${ending(x)}`
const struxTagsCount = (x: number) => `StruxTag${ending(x)}`
const equipment = (x: number) => `article${ending(x)} d'équipement`
const materials = (x: number) => `matériau${ending(x, '', 'x')}`

const xHours = (x: number) => `${x} heure${ending(x)}`
const xUsers = (x: number) => `${x} utilisateur${ending(x)}`
const xFiles = (x: number) => `${x} fichier${ending(x)}`
const xDates = (x: number) => `${x} date${ending(x)}`
const xForms = (x: number) => `${x} Formulaire${ending(x)}`
const xFields = (x: number) => `${x} champ${ending(x)}`
const xFieldsAdded = (x: number) => `${x} champ${ending(x)} ajouté${ending(x)}`
const xProjects = (x: number) => `${x} projet${ending(x)}`
const xProperties = (x: number) => `${x} Propriété${ending(x)}`

const FrenchTree: LanguageTreeType = {
  _brandName: brandName,
  _brandShortName: brandShortName,
  label: 'Libellé',
  avatar: 'Avatar',
  ok: 'OK',
  alert: 'Alerte',
  warning: 'Avertissement',
  cancel: 'Annuler',
  bulkAddOptions: "Ajout groupé d'options",
  dropdownOptions: 'Options de la liste déroulante',
  optionLabel: "Libellé de l'option",
  selectAllOptions: 'Sélectionner toutes les options',
  hierarchyOptionsIntro:
    "Créez une liste d'options pouvant être sélectionnées par les utilisateurs. Créez des listes hiérarchiques en imbriquant des options sous des parents.",
  cantChangeNestingChecking:
    "Impossible de modifier l'imbrication pour l'instant — vérification en cours des options utilisées par des enregistrements. Réessayez dans un instant.",
  optionUsedCannotNest:
    'Cette option est utilisée par des enregistrements existants et ne peut pas devenir un parent. Seules les options sans enregistrement peuvent contenir des options imbriquées.',
  cantRestoreExceedsDepth:
    'Impossible de restaurer — imbriquer ces options ensemble dépasserait la profondeur maximale.',
  restoredDeletedOptions: (labels: string, count: number) =>
    `${
      count === 1
        ? 'Option précédemment supprimée'
        : 'Options précédemment supprimées'
    } restaurée${count === 1 ? '' : 's'} : ${labels}`,
  savesAs: 'Enregistré sous',
  alreadyUsedBy: (label: string) => `Déjà utilisé par « ${label} »`,
  useValue: (value: string) => `Utiliser ${value}`,
  matchesDeletedOptionWillRestore: (label: string) =>
    `Correspond à l'option supprimée « ${label} » — elle sera restaurée`,
  restoreAndApply: 'Restaurer et appliquer',
  collapseOption: "Réduire l'option",
  expandOption: "Développer l'option",
  pickedUpOption: (label: string) => `Option ${label} saisie.`,
  optionIsOver: (active: string, over: string) =>
    `L'option ${active} est au-dessus de ${over}.`,
  optionDroppedOver: (active: string, over: string) =>
    `L'option ${active} a été déposée sur ${over}.`,
  optionDropped: (label: string) => `L'option ${label} a été déposée.`,
  reorderingOptionCancelled: (label: string) =>
    `Le réordonnancement de l'option ${label} a été annulé.`,
  optionsSelected: (count: number) =>
    `${count} option${count === 1 ? '' : 's'} sélectionnée${
      count === 1 ? '' : 's'
    }`,
  indent: 'Indenter',
  outdent: 'Désindenter',
  reorderOption: (label: string) => `Réordonner ${label}`,
  selectOption: (label: string) => `Sélectionner ${label}`,
  bulkAddOptionEnterEachLine: 'Saisissez chaque option sur sa propre ligne.',
  bulkAddOptionIndentToNest:
    "Mettez une option en retrait pour l'imbriquer sous un parent.",
  bulkAddOptionCopyFromExcel:
    'Copiez-collez des jeux de données entiers depuis Excel',
  seeTips: 'voir les astuces',
  addOptions: 'Ajouter les options',
  copyingAndPastingFromExcel: 'Copier-coller des données depuis Excel',
  bulkImportGuideSubtitle:
    "Trois façons de formater les options lors d'un collage depuis un tableur.",
  flatDatasets: 'Jeux de données plats',
  flatDatasetsHint:
    'Placez chaque option sur une ligne différente avant de copier-coller.',
  hierarchicalDatasetMethod1: 'Jeu de données hiérarchique (Méthode 1)',
  hierarchicalDatasetMethod1Hint:
    'Placez chaque niveau dans une colonne différente et chaque option sur une ligne différente avant de copier.',
  hierarchicalDatasetMethod2: 'Jeu de données hiérarchique (Méthode 2)',
  hierarchicalDatasetMethod2Hint:
    'Comme la Méthode 1, mais les parents peuvent éventuellement être répétés sur chaque ligne.',
  back: 'Retour',
  addOptionBelow: (label: string) =>
    `Ajouter une option en dessous de ${label}`,
  optionCount: (count: number) => `${count} option${count === 1 ? '' : 's'}`,
  optionMatchCount: (matched: number, total: number) =>
    `${matched} sur ${total}`,
  tipXOfY: (current: number, total: number) => `Astuce ${current} sur ${total}`,
  placeHere: 'Placer ici',
  failedToSavePlacement:
    "Impossible d'enregistrer le placement. Veuillez réessayer.",
  placedAtMapCenterDragToAdjust:
    'Placé au centre de la carte — faites glisser pour ajuster',
  notPlacedOnMap: 'Non placé sur la carte',
  placeOnMap: 'Placer sur la carte',
  placedOnLocationDragToAdjust: (name: string) =>
    `Placé sur ${name} — faites glisser pour ajuster`,
  placingMultipleItems: (count: number) =>
    `Placement de ${count} éléments — faites glisser chacun, puis confirmez`,
  addedToPlacementQueue: (remaining: number) =>
    `Ajouté à la file de placement — ${remaining} restants`,
  open: 'Ouvrir',
  openWorkflowForm: 'Ouvrir le flux de travail',
  minimize: 'Réduire',
  opened: 'Ouvert',
  close: 'Fermer',
  closed: 'Fermé',
  contacts: 'Contacts',
  loading: 'Chargement',
  language: 'Langue',
  notSpecified: 'Non spécifié',
  default: 'Par défaut',
  done: 'Terminé',
  save: 'Sauvegarder',
  apply: 'Appliquer',
  edit_verb: 'Éditer',
  merge_verb: 'Fusionner',
  link_verb: 'Lier',
  re_link_verb: 'Re-lier',
  unlink_verb: 'Dissocier',
  unlinking: 'Déconnexion',
  linkToX: (x: string) => `Lier à ${x}`,
  linkedToX: (x: string) => `Lié à ${x}`,
  savingEllipsis: 'Enregistrement...',
  details: 'Détails',
  delete: 'Supprimer',
  rename: 'Renommer',
  reorder: 'Réordonner',
  renameMapView: 'Renommer la vue de carte',
  mapViewName: 'Nom de la vue',
  deletedField: 'Champ supprimé',
  duplicate: 'Double',
  duplicatedName: (name: string) => `${name} - Copie`,
  initials: 'Initiales',
  invite: 'Inviter',
  search: 'Rechercher',
  selectAll_people: 'Sélectionner tout',
  selectAll_items: 'Sélectionner tout',
  selectNone_people: 'Ne rien sélectionner',
  selectNone_items: 'Ne rien sélectionner',
  clearAll_items: 'Effacer tout',
  clear: 'Effacer',
  uploading: 'Téléchargement',
  noResults: 'Aucun résultat',
  add: 'Ajouter',
  update_verb: 'Mettre à jour',
  updating: 'Mise à jour',
  submitting: 'Soumission',
  error: 'Erreur',
  goBack: 'Retourner',
  confirm: 'Confirmer',
  select: 'Sélectionner',
  selectAll: 'Tout sélectionner',
  selected: 'Sélectionné',
  start_verb: 'Commencer',
  finish_verb: 'Finir',
  followers: 'Abonnés',
  view_verb: 'Voir',
  download: 'Télécharger',
  export: 'Exporter',
  downloadingStatus: {
    starting: 'Démarrage',
    fetching: 'Récupération',
    compiling: 'Compilation',
    generating: 'Génération',
    downloading: 'Téléchargement',
  },
  generatingLink: 'Génération du lien...',
  searchCompany: 'Rechercher une entreprise',
  thereIsNoCompanies: "Il n'y a pas d'entreprises",
  addCompany: 'Ajouter une entreprise',
  locationClosure: 'Fermeture de site',
  ifDeliveryHasTheFollowingTags: 'Si la livraison a les étiquettes suivantes',
  ifEntityHasTheFollowingTags: (entityName: string) =>
    `Si ${entityName} a les étiquettes suivantes`,
  thenDoAction: 'Ensuite, faire une action',
  toUserTeamRoleOrCompany: "À l'utilisateur, l'équipe, le rôle ou l'entreprise",
  addRule: 'Ajouter une règle',
  tag: 'Étiquette',
  shouldDeleteRule: 'Devrait supprimer la règle',
  searchForOrSelectTag: 'Rechercher ou sélectionner une étiquette',
  addTag: 'Ajouter une étiquette',
  addUserTeamCompany: 'Ajouter un utilisateur, une équipe, une entreprise',
  tagCreation: "Création d'étiquette",
  deliveryStatus: 'Statut de livraison',
  sendNotification: 'Envoyer une notification',
  toExternalCalendar: 'Vers un calendrier externe',
  notificationRules: 'Règles de notification',
  mention: 'Mentionner',
  gotToDirectory: 'Aller au répertoire',
  goToProjectHome: "Aller à l'accueil du projet",
  daily: 'Quotidien',
  monSat: 'Lun-Sam',
  d_daysShort: 'j',
  xSecondsAgoShort: (x: number) => `il y a ${x}s`,
  xMinutesAgoShort: (x: number) => `il y a ${x} min`,
  xHoursAgoShort: (x: number) => `il y a ${x} h`,
  xDaysAgoShort: (x: number) => `il y a ${x} j`,
  yesterday: 'Hier',
  today: "Aujourd'hui",
  tomorrow: 'Demain',
  loggingIn: 'Connexion',
  loggingOut: 'Déconnexion',
  loginFailed: 'Échec de la connexion',
  backToLogin: 'Retour à la connexion',
  processingLogin: 'Traitement de la connexion...',
  login: 'Connexion',
  logo: 'Logo',
  procoreLogin: 'Se connecter avec Procore',
  loginWithMicrosoft: 'Se connecter avec Microsoft',
  loginWithGoogle: 'Se connecter avec Google',
  tryAgain: 'Réessayer',
  contactAdminForAccess:
    'Si vous pensez que vous devriez avoir accès, veuillez contacter votre administrateur.',
  ssoSessionExpired: 'Votre session de connexion a expiré. Veuillez réessayer.',
  ssoProviderError:
    "Impossible de terminer l'authentification avec le fournisseur. Veuillez réessayer.",
  ssoInvalidUser:
    'Impossible de récupérer les informations de votre compte. Veuillez réessayer.',
  ssoUserNotFound: "Ce compte n'est pas ajouté à StruxHub.",
  ssoCodeExpired:
    'Votre lien de connexion a expiré. Veuillez vous reconnecter.',
  ssoInternalError:
    "Une erreur inattendue s'est produite. Veuillez réessayer plus tard.",
  ssoProviderNotAllowed:
    "Cette méthode de connexion n'est pas disponible pour votre organisation.",
  ssoUseAllowedMethod:
    "Votre organisation nécessite l'une des méthodes de connexion suivantes :",
  ssoPersonalAccountsNotAllowed:
    'Les comptes personnels ne peuvent pas être utilisés pour se connecter. Veuillez utiliser votre compte professionnel.',
  ssoInitiationFailed:
    'Impossible de démarrer la connexion avec ce fournisseur. Veuillez réessayer ou utiliser une autre méthode de connexion.',
  ssoDomainRequiresMethods: (domain: string) =>
    `Les utilisateurs avec @${domain} doivent utiliser l'une des méthodes de connexion suivantes :`,
  passwordLoginNotAvailableForDomain: (providers: string) =>
    `La connexion par e-mail et mot de passe n'est pas disponible pour votre domaine. Veuillez utiliser : ${providers}`,
  emailAndPassword: 'E-mail et mot de passe',
  noColumnsDefined: 'Aucune colonne définie',
  changePassword: 'Changer le mot de passe',
  setPassword: 'Définir le mot de passe',
  passwordWillBeUsedForAllSites:
    'Ce mot de passe sera utilisé pour tous les sites StruxHub',
  setPasswordToSignBackIn:
    "Votre lien d'invitation est temporaire. Un mot de passe vous permet de vous reconnecter. Vous pouvez aussi le définir plus tard dans votre profil.",
  forgotPassword: 'Mot de passe oublié ?',
  reset: 'Réinitialiser',
  resetPassword: 'Réinitialiser le mot de passe',
  logOut: 'Déconnexion',
  hideAll: 'Cacher tout',
  hideGroup: 'Cacher le groupe',
  hideObject: "Cacher l'objet",
  showAll: 'Montrer tout',
  showGroup: 'Montrer le groupe',
  showObject: "Montrer l'objet",
  contactHelpAtMail: (email: string) => `Contacter l'aide à ` + email,
  contactHelp: "Contacter l'aide",
  userNotFound: 'Utilisateur non trouvé',
  noUserMatchingYourSearch: 'Aucun utilisateur ne correspond à votre recherche',
  allUsersAlreadyAssigned: 'Tous les utilisateurs sont déjà affectés',
  noTagMatchingYourSearch: 'Aucune étiquette ne correspond à votre recherche',
  enterYourEmailBelowAndStruxHubWillSendAResetLink: `Entrez votre email ci-dessous et ${brandName} vous enverra un lien de réinitialisation`,
  pleaseEnterYourPasswordForTheExistingAccountToLinkDifferentProviders:
    'Veuillez saisir votre mot de passe pour le compte existant afin de relier différents fournisseurs:',
  pleaseOpenTheLinkFromTheLetterAgain:
    'Veuillez ouvrir le lien de la lettre à nouveau',
  passwordResetLinkWillBeSentIfAccountExists:
    'Si un compte existe avec cet e-mail, vous recevrez un lien de réinitialisation du mot de passe',
  checkYourEmail: 'Consultez vos e-mails',
  passwordResetLinkSentIfAccountExists: (email: string) =>
    `Si un compte existe pour ${email}, nous vous avons envoyé un lien pour réinitialiser votre mot de passe. Cela peut prendre quelques minutes.`,
  didNotGetItTryAnotherEmail:
    "Vous ne l'avez pas reçu ? Essayez une autre adresse e-mail",
  passwordResetLinkInvalid:
    'Ce lien de réinitialisation du mot de passe est invalide ou a expiré. Demandez-en un nouveau.',
  invalidEmailError: "L'adresse email est mal formatée.",
  userNotFoundError:
    "Aucun enregistrement utilisateur ne correspond à cet identifiant. L'utilisateur a peut-être été supprimé.",
  enterANewPasswordBelow: 'Entrez un nouveau mot de passe ci-dessous',
  setYourPassword: 'Définissez votre mot de passe',
  tooManyUnsuccessfulLoginAttempts:
    'Trop de tentatives de connexion infructueuses.',
  incorrectEmailOrPassword:
    'E-mail ou mot de passe incorrect. Utilisez « Mot de passe oublié ? » pour le réinitialiser.',
  passwordErrors: {
    weakPassword:
      'Le mot de passe doit comporter au moins 8 caractères, des majuscules et des minuscules, un chiffre et un caractère spécial (!@#$%&)',
    doNotMatch: 'Les mots de passe ne correspondent pas',
    empty: 'Le mot de passe ne peut pas être vide',
    invalid:
      "Le mot de passe est invalide ou l'utilisateur n'a pas de mot de passe",
  },
  authFieldErrors: {
    enterYourEmail: 'Entrez votre adresse e-mail',
    enterValidEmail: 'Entrez une adresse e-mail valide',
    enterYourPassword: 'Entrez votre mot de passe',
    enterNewPassword: 'Entrez un mot de passe',
    enterNewPasswordAgain: 'Répétez le mot de passe',
    enterYourPhoneNumber: 'Entrez votre numéro de téléphone',
    enterValidPhoneNumber: 'Entrez un numéro valide',
    enterYourFirstName: 'Entrez votre prénom',
    enterYourLastName: 'Entrez votre nom de famille',
    enterAtLeastXCharacters: (x: number) => `Entrez au moins ${x} caractères`,
  },
  chat: 'Chat',
  activities: 'Activités',
  deliveries: 'Livraisons',
  closures: 'Fermetures',
  noActiveClosures: 'Aucune fermeture active',
  activeClosuresHeading: 'Fermetures actives :',
  logistics: 'Logistique',
  logisticObject: 'Objet logistique',
  site: 'Site',
  siteNews: 'Actualités du chantier',
  home: 'Accueil',
  hub: 'Hub',
  siteLogistics: 'Logistique du site',
  materials: 'Matériaux',
  materialsLegacy: 'Matériaux (legacy)',
  photos: 'Photos',
  analytics: 'Analyses',
  analyticsSetting: 'Configuration des analyses',
  showInReports: 'Afficher dans les rapports',
  dashboards: 'Tableaux de bord',
  widgetDescriptions: {
    workflowsCount:
      'Shows the total number of active workflows in the selected time period. This includes all workflows regardless of their status.',
    companiesCount:
      'Displays the count of unique companies involved in workflows during the selected period. Each company is counted once regardless of how many workflows they participate in.',
    usersCount:
      'Shows the number of unique users associated with workflows in the selected time range.',
    averageDuration:
      'Calculates the average number of days from workflow start date to completion date. Only includes workflows that have both start and end dates.',
    advancedPlanning:
      'Measures the average number of days between when a workflow was requested and when it actually started. Higher values indicate better advance planning.',
    equipmentBookingsWorkflows:
      'Counts how many workflows have equipment bookings attached to them during the selected period.',
    equipmentBookingsHours:
      'Shows the total hours of equipment bookings across all workflows in the selected time range.',
    locationsWorkflows:
      'Shows the distribution of workflows across different locations. Helps identify which locations have the most activity.',
    locationsByType:
      'Displays the breakdown of workflows by location type. Shows the percentage distribution across different workflow types.',
    workflowsByStatus:
      'Visualizes the distribution of workflows across different statuses (Requested, Submitted, Active, Changed, Ended). Helps understand the workflow pipeline.',
    activeVsCompleted:
      'Compares in-progress workflows (Requested, Submitted, Changed, Accepted, Reviewed, Active, OnSite, ToInspect) versus closed workflows (Done, Ended, Passed, Failed, Denied) in the selected period. Draft and deleted workflows are excluded.',
    workflowsByCategory:
      'Shows how workflows are distributed across different categories. Helps identify which workflow categories are most common.',
    workflowsTimeline:
      'Shows the number of workflows started over time. Useful for identifying trends and patterns in workflow creation.',
    recentWorkflows:
      'Table listing the most recent workflows with their status and dates. Provides quick access to the latest workflow activity.',
  },
  dashboard: {
    settings: 'Paramètres du tableau de bord',
    settingsDescription:
      'Activez ou désactivez les widgets. Les widgets sont organisés par catégorie pour une meilleure gestion du tableau de bord.',
    resetLayout: 'Réinitialiser la disposition',
    widgetInfo: 'Informations sur le widget',
    backToWidget: 'Retour au widget',
    downloadChart: 'Télécharger le graphique',
    downloadAsPng: 'Télécharger en PNG',
    downloadAsJpeg: 'Télécharger en JPEG',
    noChange: 'Aucun changement',
    chartType: 'Type de graphique',
    chartTypes: {
      pie: 'Graphique circulaire',
      bar: 'Graphique à barres',
      donut: 'Graphique en anneau',
      line: 'Graphique linéaire',
    },
    chartTypeNotSupported: 'Type de graphique non pris en charge',
    noWorkflowsFound: 'Aucun flux de travail trouvé pour cette période',
    workflowsCreated: 'Flux de travail créés',
    retry: 'Réessayer',
    refresh: 'Actualiser',
    lastUpdated: 'Dernière mise à jour',
    justNow: "à l'instant",
    minutesAgo: (m: number) => `il y a ${m}min`,
    hoursAgo: (h: number) => `il y a ${h}h`,
    widgetTitles: {
      totalWorkflows: 'TOTAL DES FLUX DE TRAVAIL',
      companies: 'ENTREPRISES',
      usersEditors: 'UTILISATEURS',
      averageDuration: 'DURÉE MOYENNE',
      advancedPlanning: 'PLANIFICATION AVANCÉE',
      workflowsTimeline: 'CHRONOLOGIE DES FLUX DE TRAVAIL',
      workflowsByStatus: 'FLUX DE TRAVAIL PAR STATUT',
      activeVsCompleted: 'ACTIFS VS TERMINÉS',
      workflowsByCategory: 'FLUX DE TRAVAIL PAR CATÉGORIE',
      recentWorkflows: 'FLUX DE TRAVAIL RÉCENTS',
      locationsWorkflows: 'EMPLACEMENTS (FLUX DE TRAVAIL)',
      locationsByType: 'EMPLACEMENTS (PAR TYPE)',
      equipmentBookings: "RÉSERVATIONS D'ÉQUIPEMENT",
      equipmentHours: "HEURES D'ÉQUIPEMENT",
    },
    widgetLabels: {
      activeWorkflowsInPeriod:
        'Flux de travail actifs dans la période sélectionnée',
      uniqueCompaniesInvolved: 'Entreprises uniques impliquées',
      uniqueUsersEditing: 'Utilisateurs uniques dans les flux de travail',
      daysFromStartToCompletion: "Jours du début à l'achèvement",
      timeRequestedBeforeStart: 'Temps demandé avant la date de début',
      workflowsWithEquipment: 'Flux de travail avec équipement',
      totalEquipmentBookingHours:
        "Total des heures de réservation d'équipement",
    },
    sections: {
      keyMetrics: {
        title: 'Métriques clés',
        description: 'Indicateurs de performance principaux',
      },
      planningEfficiency: {
        title: 'Efficacité de planification',
        description: 'Métriques de planification du travail',
      },
      workflowStatus: {
        title: 'Statut des flux de travail',
        description: 'État actuel et progression',
      },
      workflowAnalytics: {
        title: 'Analytique des flux de travail',
        description: 'Distribution et tendances',
      },
      recentActivity: {
        title: 'Activité récente',
        description: 'Dernières mises à jour des flux de travail',
      },
      locationAnalytics: {
        title: 'Analytique des emplacements',
        description: 'Où le travail se déroule',
      },
      equipmentResources: {
        title: 'Équipements et ressources',
        description: 'Utilisation des ressources',
      },
    },
  },
  reports: 'Rapports',
  filedToGenerateReport: 'Échec de la génération du rapport',
  objectProperties: "Propriétés de l'objet",
  formProperties: 'Propriétés du formulaire',
  createNewForm: 'Créer un nouveau formulaire',
  formNameLabel: 'Nom du formulaire',
  untitled: 'Sans titre',
  newFormButton: 'Nouveau formulaire',
  selectEquipmentForm: "Sélectionner un formulaire d'équipement",
  selectMaterialForm: 'Sélectionner un formulaire de matériau',
  selectLocationForm: "Sélectionner un formulaire d'emplacement",
  noFormsAvailable: 'Aucun formulaire disponible',
  askAdminToCreateForm:
    "Veuillez demander à un administrateur de créer un formulaire avant d'ajouter des enregistrements.",
  propertiesPlural: 'propriétés',
  createNewEquipment: 'Créer un nouvel équipement',
  createNewMaterial: 'Créer un nouveau matériau',
  createNewLocation: 'Créer un nouvel emplacement',
  createNewStruxTag: 'Créer un nouveau StruxTag',
  equipmentForm: "Formulaire d'équipement",
  materialForm: 'Formulaire de matériau',
  equipmentCreationForm: "Formulaire de création d'équipement",
  materialCreationForm: 'Formulaire de création de matériau',
  anyForm: 'Tout formulaire',
  equipmentPropertiesLabel: "Propriétés de l'équipement",
  materialPropertiesLabel: 'Propriétés du matériau',
  notifications: 'Notifications',
  userSettings: 'Paramètres utilisateur',
  projectSettings: 'Paramètres du projet',
  projectSettingsOnDesktop: 'Paramètres du projet dans la version bureau',
  adminSettings: 'Paramètres administratifs',
  settings: 'Paramètres',
  pinSettingsSidebar: 'Épingler la barre latérale sur toutes les pages',
  unpinSettingsSidebar: 'Désépingler la barre latérale',
  messages: 'Messages',
  themeDark: 'Thème sombre',
  themeLight: 'Thème clair',
  signIn: 'Se connecter',
  signInWithSms: 'Se connecter par SMS',
  signUp: "S'inscrire",
  signedInAs: 'Connecté en tant que',
  account: 'Compte',
  profile: 'Profil',
  editor: 'Éditeur',
  editProfile: 'Modifier le profil',
  myPortal: 'Mon portail',
  portalSidebar: {
    projects: 'Projets',
    navigation: 'Navigation du portail',
    accountMenu: 'Menu du compte',
    editProfile: 'Modifier le profil',
    openNavigation: 'Ouvrir la navigation',
    closeNavigation: 'Fermer la navigation',
    expandSidebar: 'Développer la barre latérale',
    collapseSidebar: 'Réduire la barre latérale',
    signOut: 'Déconnexion',
    reportABug: 'Signaler un bug',
  },
  portalProjects: {
    view: 'Vue du projet',
    gridView: 'Vue en grille',
    mapView: 'Vue cartographique',
    welcomeBack: (name: string) => `Bon retour, ${name} 👋`,
    chooseProject: 'Choisissez un projet pour commencer',
    openingProject: (name: string) => `Ouverture de ${name}…`,
    couldNotOpenProject: "Impossible d'ouvrir le projet",
    loadFailedFor: (tenants: string) =>
      `Impossible de charger les projets de ${tenants}.`,
    someProjectsMissing:
      'Certains de vos projets peuvent manquer dans cette liste.',
    loadingProblem: "C'est un problème de chargement, pas un compte vide.",
    loadingProjects: 'Chargement des projets…',
    noProjectsMatchSearch: 'Aucun projet ne correspond à votre recherche.',
    noProjectsMatchFilters: 'Aucun projet ne correspond à vos filtres.',
    noProjectsLoaded: "Aucun projet n'a pu être chargé.",
    noProjectsYet: "Vous n'avez pas encore de projets.",
    loadingMap: 'Chargement de la carte…',
    mapUnavailable:
      'Carte indisponible — pas de configuration Mapbox sur cet hôte.',
    mapDescription:
      "Chaque épingle est à l'emplacement réel du Projet et porte son logo — appuyez pour ouvrir.",
    lastOpened: (when: string) => `Ouvert pour la dernière fois ${when}`,
    daysAgo: (n: number) => `il y a ${n} jours`,
    oneWeekAgo: 'il y a 1 semaine',
    weeksAgo: (n: number) => `il y a ${n} semaines`,
    loadingPortal: 'Chargement du portail…',
    lastOpenedLabel: 'Dernier accès',
    loadFailed:
      'Impossible de charger vos projets. Vérifiez votre connexion et réessayez.',
  },
  portalProfile: {
    profileSettings: 'Param\u00e8tres du profil',
    personalData: 'Donn\u00e9es personnelles',
    accountData: 'Donn\u00e9es du compte',
    profilePicture: 'Photo de profil',
    upload: 'T\u00e9l\u00e9verser',
    uploading: 'T\u00e9l\u00e9versement\u2026',
    removing: 'Suppression\u2026',
    firstName: 'Pr\u00e9nom',
    lastName: 'Nom',
    jobTitle: 'Poste',
    phoneNumber: 'Num\u00e9ro de t\u00e9l\u00e9phone',
    email: 'Courriel',
    password: 'Mot de passe',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    enterCurrentPassword: 'Entrez le mot de passe actuel',
    minimumCharacters: 'Minimum 8 caract\u00e8res',
    reEnterPassword: 'Ressaisissez le mot de passe',
    passwordsMismatch: 'Les mots de passe ne correspondent pas.',
    change: 'Modifier',
    updatePassword: 'Mettre \u00e0 jour le mot de passe',
    saveChanges: 'Enregistrer les modifications',
    saving: 'Enregistrement\u2026',
    backToPortal: 'Retour au Portail',
    tenant: 'Locataire',
    errorLoadProfile: 'Impossible de charger votre profil. Veuillez réessayer.',
    errorSaveProfile:
      "Impossible d'enregistrer vos modifications. Veuillez réessayer.",
    errorUploadAvatar:
      'Impossible de télécharger votre photo. Veuillez réessayer.',
    errorRemoveAvatar:
      'Impossible de supprimer votre photo. Veuillez réessayer.',
    errorChangePassword: 'Échec du changement de mot de passe.',
    currentPasswordIncorrect: 'Le mot de passe actuel est incorrect.',
    tooManyPasswordAttempts:
      'Trop de tentatives. Veuillez patienter quelques minutes et réessayer.',
    phoneOnlySignInMethod:
      'Votre numéro de téléphone est votre seul moyen de connexion : il ne peut pas être supprimé.',
    phoneInUse: 'Ce numéro de téléphone est déjà utilisé par un autre compte.',
    invalidPhone: 'Saisissez un numéro de téléphone valide.',
    invalidProfile:
      'Certaines informations sont invalides. Vérifiez vos saisies et réessayez.',
    firstNameRequired: 'Le prénom est obligatoire.',
    setPassword: 'Définir un mot de passe',
    projectCoverAlt: (name: string) => `Couverture de ${name}`,
    struxHubVersion: (v: string) => `StruxHub version : ${v}`,
    fileTooLarge: (maxMB: number) =>
      `Le fichier est trop volumineux. La taille maximale est de ${maxMB} Mo.`,
    phoneNotVerifiedYet: 'Ce numéro de téléphone n’est pas encore vérifié',
    verify: 'Vérifier',
    remove: 'Supprimer',
    addPhoneNumber: 'Ajouter un numéro de téléphone',
    changePhoneNumber: 'Modifier le numéro de téléphone',
    newNumber: 'Nouveau numéro',
    codeSmsNotice:
      'Nous enverrons un code à 6 chiffres par SMS à ce numéro. Des frais de messagerie et de données peuvent s’appliquer.',
    sendCode: 'Envoyer le code',
    sendingCode: 'Envoi\u2026',
    enterTheCode: 'Saisissez le code',
    verificationCode: 'Code de vérification',
    codeSentTo: (phone: string) => `Envoyé au ${phone}.`,
    wrongNumber: 'Mauvais numéro ?',
    edit: 'Modifier',
    resendCodeIn: (time: string) => `Renvoyer le code dans ${time}`,
    resendCode: 'Renvoyer le code',
    sendNewCode: 'Envoyer un nouveau code',
    verifyingCode: 'Vérification\u2026',
    phoneNumberVerified: 'Numéro de téléphone vérifié.',
    phoneAlreadyVerified: 'Ce numéro est déjà vérifié.',
    removePhoneTitle: 'Supprimer le numéro de téléphone',
    removePhoneQuestion: (phone: string) =>
      `Supprimer le ${phone} de votre profil ?`,
    invalidCode: (triesLeft: number) =>
      `Ce code est incorrect. ${triesLeft} ${ending(triesLeft, 'essai restant', 'essais restants')}.`,
    codeExpired: 'Ce code a expiré. Demandez-en un nouveau.',
    verificationPhoneInUse:
      'Ce numéro est déjà utilisé par un autre compte. Contactez le support s’il vous appartient.',
    invalidMobilePhone: 'Saisissez un numéro de mobile valide.',
    tooManyCodesRequested: (minutes: number) =>
      `Trop de codes demandés. Réessayez dans ${minutes} ${ending(minutes, 'minute', 'minutes')}.`,
    tooManyCodesRequestedHours: (hours: number) =>
      `Trop de codes demandés. Réessayez dans ${hours} ${ending(hours, 'heure', 'heures')}.`,
    smsUnavailable:
      'Nous n’avons pas pu envoyer de SMS pour le moment. Réessayez plus tard.',
    unsupportedDestination:
      'Les SMS vers ce pays ne sont pas encore pris en charge.',
    errorPhoneVerification:
      'Impossible de vérifier votre numéro de téléphone. Veuillez réessayer.',
    qrCode: 'Code QR',
    qrCodeDescription:
      'Utilisez ce code aux scanners de tous vos projets StruxHub.',
  },
  portalProjectRequest: {
    title: 'Demande de nouveau projet',
    description:
      'Pour créer un nouveau projet, envoyez une demande à l’aide du formulaire ci-dessous',
    firstName: 'Prénom',
    lastName: 'Nom',
    jobTitle: 'Poste',
    workEmail: 'E-mail professionnel',
    phoneNumber: 'Numéro de téléphone',
    projectName: 'Nom du projet',
    additionalDetails: 'Informations complémentaires',
    submit: 'Envoyer',
    submitting: 'Envoi…',
    fieldRequired: 'Ce champ est obligatoire.',
    invalidEmail: 'Saisissez une adresse e-mail valide.',
    successTitle: 'Demande envoyée',
    successDescription:
      'Votre demande de projet a bien été envoyée. Nous allons l’examiner et revenir vers vous rapidement.',
    errorSubmit: 'Impossible d’envoyer votre demande. Veuillez réessayer.',
    errorInvalid:
      'Certaines informations sont invalides. Veuillez vérifier le formulaire.',
    errorTooManyRequests:
      'Trop de demandes. Veuillez patienter un moment et réessayer.',
  },
  preferences: 'Préférences',
  administration: 'Administration',
  projectDetails: 'Détails du projet',
  integrations: 'Intégrations',
  userDirectory: 'Annuaire des utilisateurs',
  team: 'Équipe',
  crew: 'Équipe',
  teams: 'Équipes',
  defaultTeams: 'Équipes par défaut',
  presentations: 'Présentations',
  tracker: 'Suivi',
  uploadSchedule: 'Téléverser le planning',
  filterBy: 'Filtrer par',
  filterByMe: "Filtrer par 'Moi'",
  filterByMyCompany: "Filtrer par 'Mon entreprise'",
  filter: 'Filtre',
  filters: 'Filtres',
  createNewFilter: 'Créer un nouveau filtre',
  addScheduleData: 'Ajouter des données de planning',
  editScheduleData: 'Modifier les données de planning',
  struxhubObject: `Objet ${brandName}`,
  selectObject: 'Sélectionner un objet',
  selectWorkForm: 'Sélectionner un formulaire de travail',
  hierarchies: 'Hiérarchies',
  dataToSchedule: 'Données à planifier',
  searchUserOrGroup: 'Rechercher un utilisateur ou un groupe',
  tool: 'Outil',
  workflow: 'Workflow',
  workflowDetails: 'Détails du Workflow',
  workflows: 'Workflows',
  inactiveWorkflowTypeTooltip:
    "Ce type de workflow n'est pas disponible pour la création.",
  workflowConfigurator: 'Configurateur de workflow',
  manageItIn: 'Gérez-le dans le',
  manageInWorkflowConfiguratorOnDesktop:
    'Gérez-le dans le Configurateur de workflow sur la version Bureau',
  fields: 'Champs',
  submissions: 'Soumissions',
  fieldsSelected: 'champs sélectionnés',
  struxHubFields: 'Champs StruxHub',
  primaryWorkflowFields: 'Principaux',
  secondaryWorkflowFields: 'Secondaires',
  structureFields: 'Structure',
  contentFields: 'Contenu',
  primaryWorkflowFieldsDescription:
    "Les Champs Primaires de Workflow définissent les informations essentielles qui caractérisent l'enregistrement et sont utilisées dans tout StruxHub pour afficher, gérer et contrôler chaque Workflow. Cela inclut les détails principaux comme la Date et l'Heure, l'Emplacement, l'Entreprise, l'Équipement et le Matériel, ainsi que les Utilisateurs de Workflow basés sur les rôles tels que l'Utilisateur Responsable (le propriétaire principal du Workflow), le Demandeur et les Contacts Supplémentaires. Chaque Champ Primaire de Workflow ne peut être ajouté qu'une seule fois",
  secondaryWorkflowFieldsDescription:
    'Ajoute un contexte de support et des relations utilisées dans tout StruxHub pour clarifier, organiser et compléter les détails principaux du Workflow',
  struxHubFieldsDescription:
    'Champs natifs de StruxHub qui apparaissent sur toute la plateforme',
  structureFieldsDescription:
    'Éléments de mise en page utilisés pour organiser un Workflow',
  contentFieldsDescription:
    'Texte instructif affiché aux utilisateurs. Ne collecte pas de données',
  customFieldsDescription:
    'Champs de saisie génériques pour capturer des données non couvertes par les Champs StruxHub',
  goToStep: "Aller à l'étape",
  goToWorkflow: 'Aller au Workflow',
  sitemapSetup: 'Configuration du plan de site',
  templateUpdating: 'Mise à jour du modèle',
  selectExistingWorkflow: 'Sélectionner le Workflow existant',
  createNewWorkflow: 'Créer un nouveau Workflow',
  enterWorkflowType: 'Entrer le type de Workflow',
  basedOnWorkflow: 'Basé sur',

  publishTo: 'Publier sur',
  geoReferencing: 'Géo-référencement',
  tags: 'Étiquettes',
  permittedCompanies: 'Entreprises autorisées',
  notPermitted: 'Non autorisé',
  hoursOfOperation: "Heures d'ouverture",
  inheritedHoursDescription:
    'Héritées des heures de travail du projet. Gérez-les dans',
  inheritedClosuresDescription:
    'Héritées des fermetures du projet. Gérez-les dans',
  outsideOperatingHours: "En dehors des heures d'ouverture",
  dateClosures: 'Fermetures par date',
  showInMap: 'Afficher sur les cartes',
  showInMapTooltip:
    "Afficher sur les cartes :\nActivé : l'objet ressource peut être affiché sur les vues cartographiques et y être rendu visible ou masqué.\nDésactivé : l'objet ressource n'est pas disponible sur les cartes.",
  resourceShownInMaps:
    "L'objet ressource peut être affiché sur les vues cartographiques et y être rendu visible ou masqué.",
  resourceHiddenFromMaps:
    "L'objet ressource n'est pas disponible sur les cartes.",
  workflowTypes: 'Types de workflow',
  workflowResource: 'WR',
  workflowResourceTooltip:
    "WR (Workflow Resource) :\nActivé : l'objet ressource peut être réservé et affecté à un Workflow.\nDésactivé : l'objet ressource n'est pas disponible à la réservation.",
  workflowResourceBulk: 'Ressource de workflow',
  resourceNotAvailableForBooking:
    "L'objet ressource n'est pas disponible à la réservation.",
  resourceAvailableForBooking:
    "L'objet ressource peut être réservé et affecté à un Workflow.",
  maxBookingDuration: 'Durée maximale de réservation',
  maxOverlapping: 'Chevauchement maximal',
  maxFormBookingDurationDays: (duration, unit) =>
    `Max Booking Duration: La durée maximale de réservation pour ce Workflow est de ${duration} ${unit}`,
  objects: 'Objets',
  image: 'Image',
  groupBy: 'Regrouper par',
  basemap: 'Carte de base',
  newView: 'Créer une nouvelle vue',
  addView: 'Ajouter une vue',
  savedViews: 'Vues enregistrées',
  createNewView: 'Créer une nouvelle vue',
  createNewWhiteboard: 'Créer un nouveau tableau blanc',
  mapFileUpload: {
    uploadMapFile: 'Télécharger un fichier de carte',
    uploadWhiteboard: 'Télécharger un tableau blanc',
    replaceThisPlan: 'Remplacer ce plan',
    withThisOne: '... par celui-ci.',
    dragMapHere: 'Glissez la carte ici',
    dragMapFileHere: 'Glissez le fichier de carte ici',
    acceptedExtensions: (extensions: string) => `(${extensions} acceptés)`,
    selectPage: 'Sélectionner la page',
    planName: 'Nom du plan',
    whiteboardName: 'Nom du tableau blanc',
    planUploaded: 'Plan téléchargé',
    mapFileUploaded: 'Fichier de carte téléchargé',
    basemapUploaded: 'Carte de base téléchargée',
    uploadingPlan: 'Téléchargement du plan',
    uploadingBasemap: 'Téléchargement de la carte de base',
    tilesetLimits: (maxNumber: number, maxSize: string) =>
      `Vous pouvez télécharger jusqu'à ${maxNumber} jeux de tuiles de ${maxSize} maximum chacun.`,
    tooManyTilesets: 'Vous avez téléchargé trop de jeux de tuiles',
    fileTooLarge: 'Le fichier téléchargé est trop volumineux',
    tilesetNameTooLong: (maxLength: number) =>
      `Nom du jeu de tuiles trop long. Taille maximale : ${maxLength} caractères`,
    invalidFileName:
      'Le nom du fichier ne peut contenir que des lettres, des chiffres et les symboles « . », « - » et « _ »',
  },
  mapExport: {
    exportMap: 'Exporter la carte',
    output: 'Sortie',
    map: 'Carte',
    frameTag: 'Zone exportée · faites glisser la carte pour ajuster',
    backToSavedView: 'Restaurer le cadrage enregistré',
    restoreViewportReason:
      'Disponible après un déplacement ou un zoom de la carte.',
    framing: 'Cadrage',
    previewReduced: 'Affiché plus petit que la taille réelle',
    background: 'Fond',
    blank: 'Vide',
    basemapLabels: 'Étiquettes du fond de carte',
    notOnBlank: 'Pas sur fond vide',
    buildings3d: 'Bâtiments 3D',
    streetOnly: 'Rue uniquement',
    angle: 'Angle',
    topDown: 'Vue de dessus',
    tilted: 'Inclinée',
    toScale: "À l'échelle",
    notToScale: 'Sans échelle',
    topDownBody: (deg: number) =>
      `Aplatie depuis l'inclinaison enregistrée de ${deg}°, donc à l'échelle.`,
    tiltedBody: 'Une image, pas un plan : ni échelle ni renvois.',
    whatYouLose: 'Ce que vous perdez',
    tiltTradeOffsTitle: 'Un export incliné est une image, pas un plan',
    tiltTradeOffs: {
      noScale: "Pas d'échelle. Ne mesurez pas dessus.",
      mapLabelsOnly: 'Les étiquettes de la carte elle-même, sans renvois.',
      farSofter: 'La partie éloignée est moins nette et plus petite.',
      northCentreSlower:
        "La flèche du nord n'est exacte qu'au centre, et l'export prend plus de temps.",
      buildingsHide: 'Les bâtiments 3D peuvent masquer des épingles.',
    },
    format: 'Format',
    png: 'PNG',
    pdf: 'PDF',
    useFor: 'Pour',
    useForChoices: {
      print: 'Impression',
      screen: 'Écran',
    },
    paperSize: 'Format du papier',
    paperOption: (paper: string, size: string) => `${paper} · ${size}`,
    screenShape: 'Proportions',
    screenShapes: {
      '16x9': '16:9 Panoramique',
      '16x10': '16:10',
      '4x3': '4:3',
    },
    closestToMap: 'Proche de votre carte',
    tooLargeForDevice: 'Trop grand pour cet appareil',
    dpiReadout: (dpi: number) => `${dpi} ppp`,
    pixelReadout: (width: number, height: number) => `${width} × ${height} px`,
    approxFileSize: (mb: number) => `environ ${mb} Mo`,
    approxFileSizeRange: (low: number, high: number) =>
      `environ ${low}–${high} Mo`,
    widerThanPaper: (paper: string) => `Plus large que ${paper}`,
    standardQuality: 'Standard',
    highQuality: 'Haute',
    screenStandardHint: (width: number, height: number) =>
      `${width} × ${height} px : idéal pour l'e-mail, la messagerie et les présentations.`,
    screenHighHint: (width: number, height: number) =>
      `${width} × ${height} px : reste net au zoom.`,
    screenHighExceedsDevice: 'Haute est trop grande pour cet appareil.',
    paperStandardHint: (dpi: number) =>
      `${dpi} ppp : idéal pour les imprimantes de bureau.`,
    paperHighHint: (dpi: number) =>
      `${dpi} ppp : impressions nettes en grand format.`,
    highQualityExceedsDevice:
      'Haute est trop grande pour cet appareil à ce format.',
    screenImageryPointer: 'Le satellite est plus net en grand format.',
    useSharpestPaper: (useFor: string, paper: string, quality: string) =>
      `Passer en ${useFor} · ${paper} · ${quality}`,
    letter: 'Lettre US',
    a4: 'A4',
    tabloid: 'Tabloïd',
    a3: 'A3',
    a2: 'A2',
    a1: 'A1',
    archD: 'ARCH D',
    a0: 'A0',
    archA: 'ARCH A',
    archB: 'ARCH B',
    ansiC: 'ANSI C',
    archC: 'ARCH C',
    ansiD: 'ANSI D',
    archE1: 'ARCH E1',
    ansiE: 'ANSI E',
    archE: 'ARCH E',
    paperGroups: {
      imperial: 'ANSI et ARCH',
      metric: 'ISO',
    },
    moreSizes: (family: string) => `Plus de formats (${family})`,
    withFamiliarName: (standard: string, name: string) =>
      `${standard} (${name})`,
    orientation: 'Orientation',
    landscape: 'Paysage',
    portrait: 'Portrait',
    millimetres: (width: number, height: number) => `${width} × ${height} mm`,
    inches: (width: number, height: number) =>
      `${width.toLocaleString('fr')} × ${height.toLocaleString('fr')} po`,
    pageElements: 'Titre et légende',
    pageElementsHint: 'Titre, date, légende, échelle et flèche du nord.',
    pageElementsHintTilted:
      'Titre, date et flèche du nord, mention Sans échelle.',
    northLetter: 'N',
    clusterLegend: 'Workflows',
    clusterLegendKeyed: 'Workflows · voir les renvois',
    keyTagLegend: 'Voir les renvois',
    truePositionLegend: 'Position réelle',
    metres: (length: number) => `${length.toLocaleString('fr')} m`,
    kilometres: (length: number) => `${length.toLocaleString('fr')} km`,
    miles: (length: number) => `${length.toLocaleString('fr')} mi`,
    mapFiles: 'Fichiers de carte',
    mapFileList: 'Liste des fichiers de carte',
    mapFilesOutsideFrame: (count: number) =>
      `${count} autre${ending(count)} fichier${ending(count)} de carte de cette vue ${ending(count, 'est', 'sont')} hors du cadre`,
    shapes: 'Formes',
    markers: 'Icônes',
    layerLabels: 'Étiquettes',
    pins: 'Épingles',
    markups: 'Annotations',
    needsPins: 'Exige les épingles',
    needsMarkups: 'Exige les annotations',
    readability: 'Lisibilité',
    optimized: 'Optimisé',
    asOnMap: 'Comme la carte',
    readabilityHints: {
      optimized:
        'Taille adaptée au papier et à la distance, sans chevauchement.',
      asOnMap:
        'Épingles, icônes et étiquettes gardent leur taille sur la carte.',
      tilted: 'Optimisé exige la vue de dessus : la carte garde ses tailles.',
    },
    overallSize: 'Taille globale',
    easyToReadFrom: 'Facile à lire à',
    readZones: {
      inHand: 'En main ou sur un bureau',
      upClose: 'De près',
      acrossRoom: 'De loin',
    },
    standardFor: (name: string) => `Standard : ${name}`,
    standardAt: (name: string, distance: string) =>
      `Standard ${name} : ${distance}`,
    aboutDistance: (distance: string) => `env. ${distance}`,
    readFromValueText: (distance: string, zone: string) =>
      `${distance}, ${zone}`,
    adjustByType: 'Ajuster par type',
    adjustByTypeNote: (isPrint: boolean): string =>
      isPrint
        ? 'En plus de la distance de lecture.'
        : 'Par rapport à la taille globale.',
    percent: (value: number) => `${value} %`,
    centimetres: (length: number) => `${length.toLocaleString('fr')} cm`,
    feet: (length: number) => `${length.toLocaleString('fr')} pi`,
    customized: 'Personnalisé',
    textKinds: {
      workflowPin: 'Étiquettes des Workflows',
      location: 'Étiquettes des emplacements',
      markup: "Étiquettes d'annotation",
      markupText: "Texte d'annotation",
      key: 'Renvois et numéros',
    },
    points: (pt: number) => `${pt.toLocaleString('fr')} pt`,
    textKindValueText: (percent: number, pt: number) =>
      `${percent.toLocaleString('fr')} pour cent, environ ${pt.toLocaleString('fr')} points`,
    atFloorHint: 'Déjà la plus petite taille lisible à cette distance.',
    atFloorScreenHint: 'Déjà la plus petite taille lisible.',
    atFitLimitHint: 'La plus grande taille qui tient sur ce format.',
    atFitLimitScreenHint: 'La plus grande taille qui tient dans cet export.',
    needsLabelKey: 'Exige Renvois',
    symbolKinds: {
      workflowPin: 'Épingles des Workflows',
      location: 'Icônes des emplacements',
    },
    printedMillimetres: (length: number) => `${length.toLocaleString('fr')} mm`,
    printedInches: (length: number) => `${length.toLocaleString('fr')} po`,
    symbolValueText: (percent: number, length: string) =>
      `${percent.toLocaleString('fr')} pour cent, environ ${length}`,
    key: 'Renvois',
    keyExplainer:
      'Les étiquettes qui ne tiennent pas reçoivent un numéro ou une lettre, listés dans les renvois.',
    keyAllFit:
      'Toutes les étiquettes tiennent sur la carte : aucun renvoi nécessaire.',
    keyOffHint: (count: number) =>
      `Activez pour lister ${count === 1 ? "l'étiquette masquée" : `les ${count} étiquettes masquées`} dans le fichier.`,
    keyOnHint: {
      besideMap: () => 'À côté de la carte dans le fichier',
      besidePage: (paper: string) =>
        `À côté de la page : le PNG est plus large que ${paper}. Un PDF les met en page 2.`,
      pageTwo: () => 'En page 2 du PDF',
    },
    keyViewList: 'Voir la liste',
    keySummaryTitle: (labels: number) =>
      labels === 1 ? "Où va l'étiquette" : `Où vont les ${labels} étiquettes`,
    labelFates: {
      onMap: 'Sur la carte',
      inKey: 'Dans les renvois',
      notListed: 'Non listés, renvois pleins',
      asCounts: 'Nombre seul, dans les disques noirs',
      notShown: 'Omis',
    },
    markupLeftOff: "Étiquettes d'annotation omises",
    densityWarning: (isPaper: boolean) =>
      isPaper
        ? 'Trop chargé : zoomez, réduisez le texte ou choisissez un papier plus grand.'
        : 'Trop chargé : zoomez ou réduisez le texte.',
    keyGroupHeader: (count: number) =>
      `${count} ${count === 1 ? 'Workflow' : 'Workflows'}`,
    keyGroupHeaderTrimmed: (count: number, listed: number) =>
      `${count} Workflows, ${listed} listés`,
    keyOverflowRow: (count: number) => `+${count} de plus, non listés`,
    previewKeyBadge: {
      besideMap: (labels: number) =>
        `Renvois à côté de la carte · ${labels} ${labels === 1 ? 'étiquette' : 'étiquettes'}`,
      besidePage: (labels: number) =>
        `Renvois à côté de la page · ${labels} ${labels === 1 ? 'étiquette' : 'étiquettes'}`,
      pageTwo: (labels: number) =>
        `Renvois en page 2 · ${labels} ${labels === 1 ? 'étiquette' : 'étiquettes'}`,
    },
    exportProgress: "Progression de l'export",
    exportCancelled: 'Export annulé.',
    shareMap: 'Partager la carte',
    createImage: "Créer l'image",
    createPdf: 'Créer le PDF',
    mapReady: (details: string) => `Carte prête · ${details}`,
    keepScreenOpen:
      "Gardez cet écran ouvert jusqu'à ce que le fichier soit prêt.",
    shareFailed: 'Impossible de partager le fichier. Réessayez.',
    desktopExportHint: (tool: string) =>
      `Personnalisez papier, couches et étiquettes dans ${tool} sur ordinateur.`,
    phases: {
      preparing: 'Préparation…',
      loadingTiles: 'Chargement des tuiles…',
      placingLabels: 'Placement des étiquettes…',
      rendering: 'Rendu…',
      encoding: 'Enregistrement du fichier…',
    },
    errors: {
      tilesTimeout: 'La carte a mis trop de temps à charger. Réessayez.',
      bufferClamped:
        "Cet appareil n'a pas pu rendre la taille complète. Choisissez une taille plus petite.",
      zoomTooDeep: 'Dézoomez pour exporter à cette taille.',
      exceedsDevice:
        'Trop grand pour cet appareil. Choisissez une taille plus petite ou la qualité Standard.',
      exceedsDeviceScreen:
        'Trop grand pour cet appareil. Choisissez la qualité Standard.',
      interrupted: "La fenêtre a changé de taille pendant l'export. Réessayez.",
      pageReloaded: "La page s'est rechargée pendant l'export. Réessayez.",
      failed: "L'export a échoué. Réessayez.",
    },
    phoneErrors: {
      bufferClamped:
        "Ce téléphone a manqué de mémoire pour la carte. Fermez d'autres applications et réessayez.",
      exceedsDevice: 'Trop grand pour ce téléphone.',
      exceedsDeviceScreen: 'Trop grand pour ce téléphone.',
      interrupted:
        "L'écran a pivoté ou changé de taille pendant l'export. Réessayez.",
      pageReloaded:
        "Le téléphone a manqué de mémoire et a rechargé la page. Fermez d'autres applications, puis réessayez.",
    },
    shareMapFailed: "Impossible d'ouvrir Partager la carte. Réessayez.",
    actualSize: 'Taille réelle',
    fitPage: 'Ajuster à la page',
    actualSizeHint: 'Faites glisser pour parcourir',
    previewViewMode: 'Taille de l’aperçu',
    actualSizePanLabel:
      'Taille réelle · faites glisser ou utilisez les flèches du clavier pour parcourir, Maj pour aller plus loin',
    actualSizeTooltip:
      'Vérifiez que les étiquettes se lisent à taille réelle. Ne déplace pas la zone exportée.',
    actualSizeTopDownOnly: 'Taille réelle · vue de dessus uniquement',
    fitPageTooltip: 'Réaffiche la page entière, avec le cadrage précédent.',
    keepTabVisible: "Gardez cet onglet visible jusqu'à la fin de l'export.",
  },
  newMapView: 'Nouvelle vue de carte',
  notPublished: 'Non publié',
  applications: 'Applications',
  savedViewsDialog: {
    searchHint: 'Rechercher des vues',
    columns: {
      viewName: 'Afficher le nom',
      filterCount: 'N° de filtres',
      access: 'Partagé avec le projet',
      creator: 'Créé par',
    },
  },
  createNewTag: 'Créer une nouvelle étiquette',
  viewBasemap: 'Voir la carte de base',
  viewBookings: 'Voir les réservations',
  thereIsNoInstanceInTheSelectedCategory:
    "Il n'y a pas d'instance dans la catégorie sélectionnée",
  directory: 'Répertoire',
  name: 'Nom',
  firstName: 'Prénom',
  lastName: 'Nom de famille',
  phoneNumber: 'Téléphone',
  globalRole: 'Intitulé du poste',
  employeeIDNumber: `Numéro d'identification de l'employé`,
  company: 'Entreprise',
  companyAliasLabel: "Entreprise telle qu'informée par l'utilisateur",
  companyOther: 'Autre',
  addXAsNewCompany: (name: string) =>
    `Ajouter « ${name} » comme nouvelle Entreprise`,
  selectOrAddCompany: 'Choisissez ou ajoutez une Entreprise',
  companyDirectory: 'Annuaire des entreprises',
  common: 'Commun',
  myCompany: 'Mon entreprise',
  myCo: 'Mon Co.',
  companyType: "Type d'entreprise",
  classification: 'Classification',
  companyName: "Nom de l'entreprise",
  user: 'Utilisateur',
  users: 'Utilisateurs',
  userTags: 'Étiquettes',
  allUsers: 'Tous les utilisateurs',
  role: 'Rôle',
  projectRole: 'Rôle de projet',
  projectRoles: 'Rôles de projet',
  projectTrades: 'Métiers du projet',
  address: 'Adresse',
  email_noun: 'email',
  phone: 'Téléphone',
  phoneVerification: 'Vérification téléphonique',
  call_verb: 'Appeler',
  email_verb: 'Envoyer un email',
  sendDirectMessageInStruxHub: `Envoyer un message direct dans ${brandName}`,
  password: 'Mot de passe',
  currentPassword: 'Mot de passe actuel',
  newPassword: 'Nouveau mot de passe',
  confirmNewPassword: 'Confirmer le nouveau mot de passe',
  confirmPassword: 'Confirmer le mot de passe',
  group: 'Groupe',
  reportsTo: 'Rapporte à',
  reportOf: (type: string) => `Rapport de ${type}`,
  createReport: 'Créer un rapport',
  deliveryReport: 'Rapport de livraison',
  formReport: 'Rapport de Workflow',
  materialReport: 'Rapport de matériel',
  aReportWillBeGeneratedWithTheAboveFilters:
    'Un rapport sera généré avec les filtres ci-dessus',
  aReportWillBeSentToYourEmailShortly:
    'Un rapport sera envoyé à votre adresse email sous peu',
  reportDetails: 'Détails du rapport',
  workflowReportTitle: 'Rapport de Workflow',
  deliveryReportTitle: 'Rapport de livraison',
  scanStationReportTitle: 'Rapport de station de scan',
  resourceDetails: 'Détails de la ressource',
  officeField: 'Bureau/Champ',
  accountType: 'Type de compte',
  status: 'Statut',
  manufacturer: 'Fabricant',
  fuel_type: 'Type de carburant',
  bid_package: 'Lot',
  assembly: 'Assemblage',
  isDockMaster: 'Est chef de quai',
  isInspector: 'Est inspecteur',
  invalidUser: 'Utilisateur invalide',
  unknownUser: 'Utilisateur inconnu',
  unknownDeletedObject: 'Objet supprimé inconnu',
  ruleProblems: {
    incomplete: 'Règle incomplète, impossible de l’enregistrer.',
    unknownCondition:
      "Cette étiquette renvoie à un élément qui n'existe plus. Supprimez-la ou choisissez-la à nouveau.",
  },
  inviteStatus: "Statut d'invitation",
  inviteStatusValues: {
    Not_Invited: 'Non invité',
    Pending: 'En attente',
    Accepted: 'Accepté',
    Not_Added: 'Non ajouté',
    Added: 'Ajouté',
    Requested: 'Demandé',
    Not_Requested: 'Non demandé',
  },
  accountPosition: 'Position de compte',
  accountPositions: {
    office: 'Bureau',
    field: 'Champ',
    management: 'Gestion',
  },
  fieldMapping: 'Correspondance des champs',
  projectAccessTypeValues: {
    admin: 'Administrateur',
    member: 'Membre',
    owner: 'Propriétaire',
  },
  modalInfoSections: {
    personalInformation: 'Informations personnelles',
    companyInformation: "Informations sur l'entreprise",
    projectInformation: 'Informations sur le projet',
    additionalInformation: 'Informations supplémentaires',
    workflowPermissions: 'Permissions de Workflow',
    companyContactInfo: "Coordonnées de l'entreprise",
    legalInfo: 'Informations légales',
    companyAddress: "Adresse de l'entreprise",
    users: 'Utilisateurs',
    qrCodeAccessAndBadges: 'Accès QR Code et badges',
    globalInfo: `Informations globales (niveau ${brandShortName})`,
  },
  projectWorkflows: {
    deliveryBooking: 'Réservation de livraison',
    deliveryInspection: 'Inspection de livraison',
    scheduleComment: 'Commentaire de planification',
    formsMaster: 'Maître des formulaires',
    scanMaster: 'Maître du scan',
    viewSchedule: 'Voir le planning',
    scheduleUpdating: 'Mettre à jour le planning',
    scheduleUploading: 'Télécharger le planning',
    scheduleNotes: 'Notes sur le planning',
  },
  permissions: {
    none: 'Aucun',
    readOnly: 'Lecture seule',
    standard: 'Standard',
    master: 'Maître',
  },
  userProfileRoleNames: {
    admin: 'Admin du site',
    owner: 'Propriétaire',
    docMaster: 'Maître des documents',
    inspector: 'Inspecteur',
    presentationUser: 'Utilisateur de présentation',
    formsMaster: 'Maître des formulaires',
    scheduleViewing: 'Voir le planning',
    scheduleUpdating: 'Mettre à jour le planning',
    scheduleUploading: 'Télécharger le planning',
    scheduleNotes: 'Notes sur le planning',
  },
  companyTypes: {
    GeneralContractorPrimary: 'Entrepreneur général (principal)',
    GeneralContractorSecondary: 'Entrepreneur général (secondaire)',
    Owner: 'Propriétaire',
    SubContractor: 'Sous-traitant',
    TieredSubContractor: 'Sous-traitant hiérarchisé',
  },
  saveProfile: 'Sauvegarder le profil',
  notificationType: 'Type de notification',
  notificationSettings: 'Paramètres de notification',
  deliveryNotifications: 'Notifications de livraisons',
  scheduleNotifications: 'Notifications de planification',
  trackerNotifications: 'Notifications de suivi',
  logisticsNotifications: 'Notifications de logistique',
  formNotifications: 'Notifications de Workflow',
  scannerNotifications: 'Notifications du scanner',
  iWouldLikeReceiveMy: 'Je souhaite recevoir mes',
  inApp: "Dans l'application",
  sms: 'SMS',
  bySms: 'Par SMS',
  byEmail: 'Par email',
  successfullyUpdated: 'Mise à jour réussie',
  wrongPhoneNumberFormat: 'Format de numéro de téléphone incorrect',
  userProfileDescriptions: {
    name: `Votre nom sera utilisé lorsque vous communiquerez avec d'autres membres de l'équipe dans ${brandName}.`,
    projectRoles:
      'Votre rôle dans le projet est utilisé pour garantir que les notifications et les workflows auxquels vous participez sont pertinents. ' +
      'Une personnalisation supplémentaire est possible dans les Préférences dans les Paramètres.',
    notifications:
      `${brandName} vous enverra des notifications pour le travail dont vous faites partie de l'équipe. ` +
      "Ces notifications apparaissent dans l'application à côté de l'icône de la cloche en haut à droite de la page, " +
      'si vous avez entré votre numéro de téléphone dans votre profil par des messages SMS envoyés à votre téléphone, ' +
      'et certains résumés des notifications peuvent être envoyés à votre adresse email. ' +
      "Vous pouvez désélectionner l'une de ces options ci-dessous pour ne plus recevoir de notifications par ce moyen.",
  },
  whitelistedDomains: 'Domaines autorisés',
  projectAdminSummary: "Résumé de l'administrateur du projet",
  lastUpdated: 'Dernière mise à jour',
  dateByName: (date: string, name: string) => date + ' par ' + name,
  uploadProjectSchedule: 'Téléverser le planning du projet',
  fileUploaded: 'Fichier téléversé : ',
  totalActivities: 'Total des activités',
  totalUsers: 'Total des utilisateurs',
  uploadProcurementPlan: "Téléverser le plan d'approvisionnement",
  bpAssignedToCompanies: (amount: number, totalAmount: number) =>
    amount +
    ' sur ' +
    totalAmount +
    ` packages d'offres assignés aux entreprises.`,
  downloadCurrentValuesAsCsv: (toLink: (aText: string) => any) => [
    toLink('Télécharger'),
    ' les valeurs actuelles en tant que fichier csv.',
  ],
  uploadTeamList: "Téléverser la liste des membres de l'équipe",
  currentTeamMembers: (amount: number) =>
    amount + ` membres actuels de l'équipe.`,
  emptyTagTypes: (tags: TagType[]) =>
    `Certaines des balises de type requises (${tags
      .map(tag => getBandTitleByTagType(tag))
      .toString()}) sont vides pour certaines entreprises. Des valeurs par défaut seront définies pour eux.`,
  adminSummaryDescriptions: {
    projectDetails:
      "Définissez le nom et l'emplacement de votre projet afin de le retrouver facilement dans votre sélecteur de projet",
    projectSchedule: `Téléversez votre Calendrier Maître mis à jour depuis P6 aussi souvent que vous le souhaitez. Ne vous inquiétez pas — vous ne perdrez aucune donnée dans ${brandName}.`,
    procurementPlan:
      "Téléversez votre plan d'approvisionnement mis à jour à mesure que vous intégrez plus de sous-traitants au Projet " +
      "et que vous affinez vos valeurs attendues pour chaque package d'offres.",
    teamList:
      "Téléversez votre liste d'équipe de projet à mesure que de nouveaux membres rejoignent le projet. " +
      "L'inclusion d'informations optionnelles telles que l'affiliation au Département/Équipe interne facilite " +
      "l'attribution des ressources aux Activités.",
  },
  project: 'Projet',
  xProjects,
  projectSingularPlural: 'Projet(s)',
  notificationsRulesSetupDescriptions: {
    helperText1: (entityType: string) =>
      `Une règle enverra des notifications à un ensemble d'utilisateurs pour toutes les futures ` +
      entityType +
      ` avec un ensemble d'étiquettes désignées`,
    helperText2:
      'Les étiquettes peuvent être utilisées seules ou combinées avec des opérateurs booléens. Par exemple :',
  },
  selectProject: 'Sélectionner un projet',
  selectProjectToSync: 'Sélectionner le(s) projet(s) à synchroniser',
  selectBookingTime: 'Sélectionner un horaire de réservation',
  newProject: 'Nouveau projet',
  projectName: 'Nom du projet',
  projectCode: 'Code du projet',
  projectLocation: 'Emplacement du projet',
  projectLogo: 'Logo du projet',
  projectHeaderImage: 'En-tête du projet',
  streetAddress: 'Adresse',
  city: 'Ville',
  state_region: 'État/Région',
  zipCode: 'Code postal',
  country: 'Pays',
  projectTimeSettings: 'Paramètres de temps et calendrier du projet',
  QRCodes: 'Codes QR',
  xQRCode: (name: string) => `Code QR de ${name}`,
  scanStations: 'Stations de numérisation',
  projectAdmins: 'Administrateurs du projet',
  setTheTimeZoneForThisProject: 'Définir le fuseau horaire pour ce projet',
  dateFormat: 'Format de date',
  maturixProjectName: 'Nom du projet Maturix',
  maturixTemperatureUnit: 'Unité de température Maturix',
  maturixStrengthUnit: 'Unité de résistance Maturix',
  maturixStations: 'Stations Maturix',
  undefinedSection: 'Section non définie',
  setTheDateTimeFormatForThisProject:
    'Définir le format de date et heure pour ce projet',
  localTimezone: 'Fuseau horaire local',
  us: 'US',
  international: 'International',
  customProjectClosures: 'Fermetures de projet personnalisées',
  addCustomProjectClosure: 'Ajouter une fermeture de projet personnalisée',
  forTheFuture: "Pour l'avenir",
  projectType: 'Type de projet',
  apps: 'Applications',
  admins: 'Administrateurs',
  theListOfProjectsAdministrators: 'La liste des administrateurs du projet',
  addNew: 'Ajouter un nouvel',
  addNewValue: 'Ajouter',
  addNewAdmin: 'Ajouter un nouvel administrateur',
  addNewContact: 'Ajouter un nouveau contact',
  defaultWorkingHours: 'Heures de travail par défaut',
  timeZone: 'Fuseau horaire',
  everyday: 'Tous les jours',
  holidays: 'Jours fériés',

  struxHubHome: `${brandName} Accueil`,
  struxHubForms: `Workflows de ${brandName}`,
  app: 'Application',
  manageApps: 'Gérer les applications',
  defaultPage: 'Page par défaut',
  appsVisibilityInfo:
    'Afficher ou masquer les applications dans la navigation.',
  defaultPageInfo:
    "Choisissez l'application qui sert de page d'accueil lorsque les utilisateurs reviennent au produit.",
  projectDetailsDescriptions: {
    projectNameError:
      'Le nom ne doit pas être vide et doit contenir uniquement des lettres, des chiffres, des espaces, _ , -',
    projectCodeError:
      'Le code du projet ne peut pas être vide et ne doit contenir aucun caractère spécial. La longueur maximale pour le code du projet est de 5 caractères',

    changeProjectLocationWarning:
      'Votre fuseau horaire de projet a été défini différemment ' +
      "du fuseau horaire à l'emplacement de votre projet",
  },
  integrationsSetupDescriptions: {
    invalidToken: 'Jeton invalide',
  },
  procoreDescription: 'Logiciel de gestion de construction',
  loginToProcore: 'Se connecter à Procore',
  procoreLoginDescription:
    'Pour configurer cette intégration, vous devez être connecté à votre compte Procore.',
  procoreNoCompaniesAccess:
    "Vous n'avez accès à aucune entreprise Procore. Veuillez contacter l'administrateur de votre entreprise.",
  procoreNoAccessibleProjects:
    "Vous n'avez aucun projet accessible dans cette entreprise. Veuillez contacter l'administrateur de votre entreprise.",
  procoreConnectionFailed:
    "Impossible de se connecter à Procore. Veuillez réessayer ou contacter l'administrateur de votre entreprise.",
  procoreUsersWithoutContact: (names: string[]) =>
    `Impossible d'ajouter ${names.join(', ')} : ${names.length === 1 ? "l'utilisateur n'a" : "les utilisateurs n'ont"} ni e-mail ni numéro de téléphone. Contactez l'administrateur du projet Procore pour obtenir l'accès à l'annuaire.`,
  integrationEnabled: 'Intégration activée',
  integrationDisabled: 'Intégration désactivée',
  deliveriesFieldMapping: 'Mappage des champs de livraisons',
  workflowsFieldMapping: 'Mappage des champs de flux de travail',
  selectWorkflowTypeFirst:
    "Veuillez d'abord sélectionner le type de flux de travail",
  deleteWorkflowMapping: 'Supprimer le mappage de flux de travail',
  synchronizationEnabled: 'Synchronisation activée',
  synchronizationDisabled: 'Synchronisation désactivée',
  connecting: 'Connexion...',
  loggedIn: 'Connecté',
  loginToEnableIntegration:
    "Connectez-vous à Procore pour activer l'intégration",
  loginToDisableIntegration:
    "Connectez-vous à Procore pour désactiver l'intégration",
  procoreLoginSuccess: 'Connexion réussie au compte Procore',
  procoreIntegrationEnabled: 'Intégration avec Procore activée',

  procoreFieldMappings: {
    struxhubDeliveryLog: 'Journal des livraisons Struxhub',
    procoreDeliveryLog: 'Journal des livraisons Procore',
    startTimeOfBookingDate: 'Heure de début de la date de réservation',
    time: 'Heure',
    requesterCompany: 'Entreprise du demandeur',
    deliveryFrom: 'Livraison depuis',
    bookingId: 'ID de réservation',
    trackingNumber: 'Numéro de suivi',
    vendorVendorEmailsMaterialsDeliveryNote:
      'Fournisseur, e-mails du fournisseur, matériaux, bon de livraison',
    contents: 'Contenu',
    notesFromDeliveryLog: 'Notes du journal des livraisons',
    comments: 'Commentaires',
    attachedFilesFromDeliveryLog: 'Fichiers joints du journal des livraisons',
    attachments: 'Pièces jointes',
  },

  countryValues: countries,
  timezoneValues: timezones,
  timezoneWithOffsets: (name: string, offsets: string[]) =>
    `${name} (GMT ${offsets.join('/')})`,
  addCustomProjectClosures: 'Ajouter des fermetures de projet personnalisées',
  closureName: 'Nom de la fermeture',
  closureStartDate: 'Date de début de fermeture',
  closureEndDate: 'Date de fin de fermeture',
  loadingTeamMembers: "Chargement des membres de l'équipe",
  loadingMonitoringData: 'Chargement des données de surveillance',
  xTeamMembersSelected: (x: number) =>
    `${x} membre${ending(x)} de l'équipe sélectionné`,
  xCompaniesSelected: (x: number) =>
    `${x} entrepris${ending(x, 'e', 'es')} sélectionné`,
  importExportTeamMembers: "Importer/Exporter les membres de l'équipe",
  teamMembersDropdownStates: {
    downloadTeamMembers: 'Télécharger les utilisateurs (.csv)',
    uploadTeamMembers: 'Téléverser les utilisateurs (.csv)',
    viewCompanyDirectory: "Voir l'annuaire des entreprises",
    connectToProcore: 'Connecter à Procore',
  },
  selectNotInvited: 'Sélectionner non invité',
  selectStatusRequested: 'Sélectionner statut demandé',
  setNotInvited: 'Définir comme non invité',
  addNewProjectMember: 'Ajouter un nouveau membre du projet',
  uploadYourUpdatedProjectMemberListCsvFile:
    'Téléverser votre fichier csv de liste de membres du projet mis à jour.',
  orDragYourFileHere: 'Ou glissez votre fichier ici.',
  uploadYourScheduleFileInP6XerOrExcelXlsxXlsmFormat:
    'Téléversez votre fichier de planning au format P6 (.xer) ou Excel (.xlsx, .xlsm).',
  sendInviteLinkToUser: "Envoyer le lien d'invitation à l'utilisateur",
  addUser: 'Ajouter un utilisateur',
  byUsers: 'Par utilisateurs',
  byWorkflowField: 'Par champ du flux',
  byCompany: 'Par entreprise',
  bicUsers: 'Utilisateurs BIC',
  editUsers: (x: number) => `Éditer l'utilisateur${ending(x)}`,
  userValidationErrors: {
    firstNameRequired: 'Le prénom est requis',
    lastNameRequired: 'Le nom de famille est requis',
    emailRequired: "L'email est requis",
    invalidEmail: "L'email n'est pas valide",
    invalidPhone: "Le numéro de téléphone n'est pas valide",
    emailIsTaken: "L'email est déjà pris par un autre membre du projet",
    phoneIsTaken:
      'Le numéro de téléphone est déjà pris par un autre membre du projet',
    memberWithSuchEmailIsAlreadyAdded:
      'Un utilisateur avec cet email est déjà ajouté au projet',
    memberWithSuchPhoneNumberIsAlreadyAdded:
      'Un utilisateur avec ce numéro de téléphone est déjà ajouté au projet',
    companyRequired: "L'entreprise est requise",
  },
  bulkSaveErrors: {
    invalidEmail: 'Adresse email invalide',
    invalidPhone: 'Numéro de téléphone invalide',
    duplicateRow: "Doublon d'une ligne précédente de ce lot",
    missingName: 'Le prénom et le nom de famille sont requis',
    identifierConflict:
      "L'email et le téléphone appartiennent à des utilisateurs existants différents",
    authDenied: "L'inscription a été rejetée par le service d'authentification",
    authUnavailable:
      "Le service d'authentification est indisponible — réessayez plus tard",
    rowProcessingFailed:
      'Le traitement de la ligne a échoué — contactez le support si cela persiste',
    rowError: (row: number | string, message: string) =>
      `Ligne ${row} : ${message}`,
    namedRowError: (name: string, message: string) => `${name} : ${message}`,
    andXMoreRows: (x: number) => `…et ${x} ligne${ending(x)} de plus.`,
    xRowsFailedToSave: (x: number) =>
      `${x} ligne${ending(x)} n'${
        x === 1 ? 'a' : 'ont'
      } pas pu être enregistrée${ending(x)}`,
    unknownError: 'Erreur inconnue',
    xMembersImported: (x: number) =>
      `${x} membre${ending(x)} importé${ending(x)}`,
  },
  companyValidationErrors: {
    nameRequired: "Le nom de l'entreprise est requis",
    emailRequired: "L'email professionnel est requis",
    invalidEmail: "L'email n'est pas valide",
    invalidPhone: "Le numéro de téléphone professionnel n'est pas valide",
    emailIsTaken: "L'email est déjà pris par une autre entreprise",
    somethingFilledIncorrectly: 'Quelque chose est mal rempli',
    companyTypeIsRequired: "Le type d'entreprise est requis",
    codeRequired: 'Le code est requis',
    codeIncorrectLength:
      'La longueur du code doit être comprise entre 3 et 5 symboles',
    codeIsNotUnique:
      'Une entreprise avec ce code existe déjà, changez la valeur du champ de code pour le rendre unique',
  },
  companyLinkingErrors: {
    anErrorOccurredWhileLinking: `Une erreur s'est produite lors de la liaison`,
    anErrorOccurredWhileUnlinking: `Une erreur s'est produite lors de la dissociation`,
    anErrorOccurredWhileFetchingGlobalStorage: `Une erreur s'est produite lors de la récupération du stockage global`,
  },
  companyMerging: {
    confirmation: (sourceCompaniesText: string, targetCompanyText: string) =>
      `Confirmez-vous la fusion de ${sourceCompaniesText} dans ${targetCompanyText}?`,
  },
  somethingWentWrongDuringAPIInteraction:
    "Quelque chose s'est mal passé pendant l'interaction API",
  signUpLinkInvalid:
    "Ce lien d'inscription est incomplet. Veuillez utiliser le code QR de votre invitation au projet.",
  confirmDeactivationOfTheFollowingXCompanies: (x: number) =>
    `Confirmer la désactivation des entreprises suivantes : ${x} entrepris${ending(
      x,
      'e',
      'es',
    )} ?`,
  confirmActivationOfTheFollowingXCompanies: (x: number) =>
    `Confirmer l'activation des entreprises suivantes : ${x} entrepris${ending(
      x,
      'e',
      'es',
    )} ?`,
  confirmDeletionOfTheFollowingXProjectMembers: (x: number) =>
    `Confirmer la suppression des membres du projet suivants : ${x} membr${ending(
      x,
    )} du projet ?`,
  confirmInvitationOfTheFollowingXProjectMembers: (x: number) =>
    `Confirmer l'invitation des membres du projet suivants : ${x} membr${ending(
      x,
    )} du projet ?`,
  confirmSettingAsNotInvitedOfTheFollowingXProjectMembers: (x: number) =>
    `Confirmer la définition comme non invité des membres du projet suivants : ${x} membr${ending(
      x,
    )} du projet ?`,
  confirmInvitation: "Confirmer l'invitation",
  confirmSettingUsersAsNotInvited:
    'Confirmer la définition des utilisateurs comme non invités',
  confirmDeletion: 'Confirmer la suppression',
  confirmActivation: "Confirmer l'activation",
  confirmDeactivation: 'Confirmer la désactivation',
  confirmDeletionOfTheFollowingXPlannedManpower: (x: number) =>
    `Confirmer la suppression de la main-d'œuvre planifiée suivante : ${x} main-d'œuvre planifié${ending(
      x,
    )} ?`,
  confirmDeletionOfTheFollowingXBidPackages: (x: number) =>
    `Confirmer la suppression des packages d'offres suivants : ${x} packag${ending(
      x,
    )} d'offres ?`,
  confirmDeletionOfActivities: (x: number) =>
    `Êtes-vous sûr de vouloir supprimer ${x} activité${ending(x, 'é', 'és')} ?`,
  confirmDeletionOfEquipment: (x: number) =>
    `Êtes-vous sûr de vouloir supprimer l'équipement sélectionné ?\n\n${x} ${equipment(
      x,
    )} sera supprimé${ending(x, '', 's')}.`,
  confirmDeletionOfSensors: (x: number) =>
    `Êtes-vous sûr de vouloir supprimer les capteurs sélectionnés ?\n\n${x} capteur${ending(
      x,
    )} sera supprimé${ending(x, '', 's')}.`,
  confirmDeletionOfLocations: (x: number) =>
    `Êtes-vous sûr de vouloir supprimer les emplacements sélectionnés ?\n\n${x} emplacement${ending(
      x,
    )} sera supprimé${ending(x, '', 's')}.`,
  confirmDeletionOfStruxTags: (x: number) =>
    `Êtes-vous sûr de vouloir supprimer les StruxTags sélectionnés ?\n\n${x} ${struxTagsCount(
      x,
    )} sera supprimé${ending(x, '', 's')}.`,
  confirmDeletionOfMaterials: (x: number) =>
    `Êtes-vous sûr de vouloir supprimer les matériaux sélectionnés ?\n\n${x} matériau${ending(
      x,
      '',
      'x',
    )} sera supprimé${ending(x, '', 's')}.`,
  confirmDeletionOfResources: (x: number, countLabel: string) =>
    `Êtes-vous sûr de vouloir supprimer les ${countLabel} sélectionnés ?\n\n${x} ${countLabel} sera supprimé${ending(
      x,
      '',
      's',
    )}.`,
  resourceCountLabel: (resourceTypeId: string, x: number) => {
    switch (resourceTypeId) {
      case 'offloading_equipment':
        return equipment(x)
      case 'sensor':
        return sensors(x)
      case 'material':
        return materials(x)
      default:
        return `élément${ending(x)}`
    }
  },
  confirmApprovalOfTheFollowingXProjectMembers: (x: number) =>
    `Confirmer l'approbation des ${x} demande${ending(x)} en ${brandName}?`,
  confirmApproval: "Confirmer l'approbation",
  selectProcoreProject: 'Sélectionner un projet Procore',
  selectProcoreCompany: 'Sélectionner une entreprise Procore',
  noUnit: `Aucune d'unité`,
  noCompanies: 'Aucune entreprise',
  noDirectoryAccess: "Aucun accès à l'annuaire",
  xUsers,
  xUsersShowing: (x: number) => `${x} utilisateurs affiché${ending(x)}`,
  xUsersCount: (x: number) => `${x} Utilisateur${ending(x)}`,
  xTeams: (x: number) => `${x} équipe${ending(x)}`,
  addXUsers: (x: number) => {
    if (!x) {
      return 'Ajouter'
    }
    // 'Ajouter 1 utilisateur' ou 'Ajouter x utilisateurs'
    return `Ajouter ${x} utilisateur${ending(x)}`
  },
  xFiles,
  xFields,
  xFieldsAdded,

  files: 'Fichiers',
  multiLevelScreen: 'Écran multi-niveaux',
  levelsViewsWithZoneSummary: 'Vues des niveaux avec résumé de zone',
  levelsViewsWithCompanySummary: "Vues des niveaux avec résumé d'entreprise",
  gantt: 'Gantt',
  presentationMode: 'Mode présentation',
  play: 'Lecture',
  stop: 'Arrêt',
  turnOn: 'Activer',
  presentationScreens: 'Écrans de présentation',
  xMinutes: (x: number) => `${x} minute${ending(x)}`,
  order_noun: 'Ordre',
  order_number: (x: number) => {
    switch (x) {
      case 1:
        return '1er'
      case 2:
        return '2ème'
      case 3:
        return '3ème'
      default:
        return `${x}ème`
    }
  },
  display: 'Afficher',
  presentationModeDescriptions: {
    multiLevelScreen:
      `Affiche l'activité et les performances de la main-d'œuvre par rapport aux réels par rapport aux prévisions ` +
      'pour tous les niveaux du bâtiment. Fait défiler pour afficher tous les niveaux',
    levelsViewsWithZoneSummary:
      'Parcourt chaque niveau du bâtiment en affichant' +
      `l'activité globale et la performance de la main-d'œuvre pour chaque zone.` +
      `Défile pour afficher les données de chaque zone.`,
    levelsViewsWithCompanySummary:
      'Parcourt chaque niveau du bâtiment en affichant ' +
      `la performance globale de l'entreprise. Fait défiler pour afficher les données de chaque entreprise.`,
    gantt:
      `Affiche la vue Gantt du projet pour l'anticipation actuelle. ` +
      `Défile pour afficher toutes les activités. `,
    deliveries:
      'Affiche le calendrier hebdomadaire des livraisons pour la semaine en cours.',
    logistics: 'Affiche la carte logistique.',
    confirmText:
      'Le mode Présentation vous déconnectera et configurera cet appareil pour la lecture. À' +
      'Quittez le mode présentation, allez dans Paramètres. Vous devrez vous reconnecter une fois que vous aurez ' +
      'quitter le mode présentation.',
    header:
      `L'activation du mode Présentation vous déconnectera et connectera cet appareil ` +
      `en mode présentation uniquement.`,
    periodOfInactivity: `La présentation s'exécutera automatiquement après une période d'inactivité de`,
    stop:
      `L'arrêt du mode présentation vous ramènera à un écran de connexion ` +
      'où vous pouvez vous connecter à vos projets.',
  },
  include: 'Inclure',
  saveConfiguration: 'Sauvegarder la configuration',
  preTaskChecklist: 'Liste de vérification pré-tâche',
  requireDailyInspections: 'Exiger des inspections quotidiennes',
  inspectionFrequency: "Fréquence d'inspection",
  deadLineForRecordingAnInspection:
    "Date limite pour l'enregistrement d'une inspection",
  checklist: 'Liste de contrôle',
  instructions: 'Instructions',
  xOptions: (x: number) => `${x} option${ending(x)}`,
  NA: 'N/D',
  remove: 'Retirer',
  typeOfList: 'Type de liste',
  addAdditionalItem: 'Ajouter un élément supplémentaire',
  enterInstruction: "Entrer l'instruction",
  enterTheXDigitCodeSentTo: (x: number) =>
    `Entrez le code à ${x} chiffres envoyé à`,
  areYouSureWantToDeleteInstructionItem:
    "Êtes-vous sûr de vouloir supprimer l'élément d'instruction ?",
  instructionListTypes: {
    enumerated: 'Énuméré',
    bulleted: 'À puces',
    none: 'Aucun',
  },
  fieldStates: {
    mandatory: 'Obligatoire',
    optional: 'Facultatif',
    hidden: 'Caché',
  },
  revertingProjectData: 'Restauration des données du projet',
  loadingUpdatingProject: 'Chargement/Mise à jour du projet',
  downloadProjectDataCsv: 'Télécharger les données du projet en format .csv',
  exploreYourScheduleInStruxHub: `Explorez votre planning dans ${brandName}`,
  scheduleIsNotUploaded: "Le planning n'est pas téléchargé",
  gettingXerProjects: 'Récupération des projets xer',
  gettingExcelSheets: 'Récupération des feuilles Excel',
  parsingTheScheduleFileStarted: "Début de l'analyse du fichier de planning",
  readingScheduleFile: 'Lecture du fichier de planning',
  extractingScheduleData: 'Extraction des données de planning',
  xEntriesInTheFile: (x: number) =>
    `${x} entrée${ending(x, 'e', 'es')} dans le fichier`,
  savingScheduleDataCollections:
    'Sauvegarde des collections de données de planning',
  xOfYEntriesSaved: (x: number, y: number) =>
    `${x} sur ${y} entrée${ending(y, 'e', 'es')} sauvegardée${ending(
      y,
      'e',
      'es',
    )}`,
  recalculatingStatusUpdates: 'Recalcul des mises à jour de statut',
  savingSchedule: 'Sauvegarde du planning',
  uploadingScheduleFailedAt: 'Échec du téléversement du planning à',
  uploadingScheduleDone: 'Téléversement du planning terminé',
  seeGanttInStruxHub: `Voir le Gantt dans ${brandName}`,
  noActivitiesFound: 'Aucune activité trouvée',
  noPropertiesFoundForForm: 'Aucune propriété trouvée pour ce formulaire',
  noPropertiesMatchSearch: 'Aucune propriété ne correspond à votre recherche',
  continueToNextStep: "Continuer à l'étape suivante",
  thereAreNoProjectsInProvidedFile:
    "Il n'y a pas de projets dans le fichier fourni",
  thereAreNoInstancesToDisplay: (instancesName: string = 'Instances') =>
    `Il n'y a pas de ${instancesName.toLowerCase()} à afficher`,
  scheduleUpdateSummary: 'Résumé de la mise à jour du planning',
  current: 'Actuel',
  upload_noun: 'Téléversement',
  fileName: 'Nom du fichier',
  dateUploaded: 'Date de téléversement',
  uploadScheduleDescriptions: {
    warning: (delta: number) =>
      `Les ${delta} ${activities(delta)} qui ${ending(
        delta,
        'est',
        'sont',
      )} absente${ending(
        delta,
        's',
        's',
      )} dans le planning mis à jour seront retirée${ending(
        delta,
        's',
        's',
      )} de l'affichage. Tout le contenu associé à ce${ending(
        delta,
        's',
        's',
      )} ${delta} ${activities(
        delta,
      )} sera supprimé de ${brandName} également.`,
    chooseProject:
      "Le fichier xer P6 actuel contient des données de plusieurs projets. Veuillez sélectionner le projet à importer. Les données de ce projet doivent correspondre à celles contenues dans d'autres sources (par exemple, le plan d'approvisionnement, la liste des membres de l'équipe).",
    chooseSheet:
      'Les fichiers actuels contiennent plusieurs feuilles. Veuillez sélectionner la feuille à importer.',
    mergeData: 'Mettre à jour le planning actuel.',
    mapFields:
      'Veuillez sélectionner quel champ sera utilisé pour chaque colonne',
  },
  activityId: "ID d'activité",
  activityUniqueId: "ID unique de l'activité",
  activityName: "Nom de l'activité",
  activityCode: "Code d'activité",
  activityStatus: "Statut de l'activité",
  activityPercentComplete: "Pourcentage d'achèvement de l'activité",
  plannedActivityStart: "Début d'activité prévu",
  plannedActivityFinish: "Fin d'activité prévue",
  actualActivityStart: "Début d'activité réel",
  actualActivityFinish: "Fin d'activité réelle",
  resourceId: 'ID de ressource',
  resourceName: 'Nom de la ressource',
  trade: 'Métier',
  assignee: 'Assigné',
  primarySafetyHazard: 'Risque de sécurité primaire',
  secondarySafetyHazard: 'Risque de sécurité secondaire',
  categoryOfVariance: 'Catégorie de variance',
  categoryOfVarianceOpened: 'Catégorie de variance ouverte',
  activityCodeTypes: "Types de codes d'activité",
  resources: 'Ressources',
  uploadAnother: 'Téléverser un autre',
  next: 'Suivant',
  saveAndViewUpdatedGantt: 'Sauvegarder et voir le Gantt mis à jour',
  lastUpload: 'Dernier téléversement',
  companies: 'Entreprises',
  targetCompany: 'Entreprise cible',
  activitiesWithTheFollowingActivityCodesAreExcluded:
    "Les activités avec les codes d'activité suivants sont exclues",
  activityCodesSelected: "Codes d'activité sélectionnés",
  scheduleRestoringConfirmation: 'Confirmation de restauration du planning',
  doYouConfirmRestoringFileFromDate: (file: string, wrappedDate) => [
    `Confirmez-vous la restauration de ${file} à partir de `,
    wrappedDate,
    '?',
  ],
  errorSendingOneTimeCode:
    "Erreur lors de l'envoi du SMS. Veuillez réessayer. Si le problème persiste, veuillez contacter un administrateur.",
  noNonsenseInOneTimeCodeResponse:
    'Numéro de téléphone non trouvé. Veuillez entrer votre numéro de téléphone complet avec indicatif régional.',
  redirectingYouTo: (x: string) => `Vous redirige vers ${x}`,
  doYouWantToSaveChanges: 'Voulez-vous sauvegarder les modifications ?',
  doYouAlreadyHaveAnAccount: 'Avez-vous déjà un compte ?',

  uploadHistory: 'Historique de téléversement',
  restore: 'Restaurer',
  chooseTenant: 'Choisir le locataire',
  chooseAnotherTenant: 'Choisir un autre locataire',
  unableToReachTenant: "Impossible d'accéder au locataire",
  couldNotOpenTenant: (tenant: string) =>
    `Nous n'avons pas pu ouvrir ${tenant}. Vous n'y avez peut-être pas accès, ou la requête a expiré. Choisissez un autre locataire ou déconnectez-vous et réessayez.`,
  theSelectedTenant: 'le locataire sélectionné',
  chooseProject: 'Choisir le projet',
  chooseSheet: 'Choisir la feuille',
  mapFields: 'Mapper les champs',
  xActivities: (x: number) => `${x} ${activities(x)}`,
  xHours,
  locationsWithDeletedParent: (x: number): string =>
    x === 1
      ? '1 emplacement a perdu son parent LBS'
      : `${x} emplacements ont perdu leur parent LBS`,
  locationsWithDeletedParentDescription: (x: number): string =>
    x === 1
      ? "Son parent LBS a été supprimé avant octobre 2026, il n'apparaît donc pas dans la barre latérale des cartes. Vérifiez-le pour le supprimer ou lui attribuer un nouveau parent."
      : "Leur parent LBS a été supprimé avant octobre 2026, ils n'apparaissent donc pas dans la barre latérale des cartes. Vérifiez-les pour supprimer chacun ou lui attribuer un nouveau parent.",
  reviewLocations: (x: number): string =>
    x === 1 ? "Vérifier l'emplacement" : `Vérifier ${x} emplacements`,
  shownInTableBelow: 'Affichés dans le tableau ci-dessous',
  xLocations: (x: number) => `${x} Emplacement${ending(x)}`,
  xObjectsHere: (x: number) => `${x} objet${ending(x)} ici`,
  xObjectsAtThisSpot: (x: number, names: string[]) =>
    `${x} objet${ending(x)} à cet endroit : ${names.join(', ')}`,
  workflowsHereX: (x: number) => `Flux de travail ici (${x})`,
  xAttachments: (x: number) => `${x} Pièce${ending(x)}`,
  xColumns: (x: number) => `${x} colonne${ending(x)}`,
  cancelUpload: 'Annuler le téléversement',
  selectProjectMembersLinkedToX: (x: string, isPlural: boolean) =>
    `Sélectionner le${isPlural ? 's' : ''} membre${
      isPlural ? 's' : ''
    } du projet lié${isPlural ? 's' : ''} à ${x}`,
  selectActivityCodesLinkedToX: (x: string, isPlural: boolean) =>
    `Sélectionner le${isPlural ? 's' : ''} code${
      isPlural ? 's' : ''
    } d'activité lié${isPlural ? 's' : ''} à ${x}`,
  selectionOf1ProjectMemberIsPermitted:
    "La sélection d'un membre du projet est autorisée.",
  selectionOf1ActivityCodeIsPermitted:
    "La sélection d'un code d'activité est autorisée.",
  multipleSelectionsArePermitted: 'Les sélections multiples sont autorisées.',
  excludeSectionsOfTheScheduleOptional:
    'Exclure des sections du planning (facultatif)',
  activityFilterDialogDescriptions: {
    hiddenCodes:
      "Si vous souhaitez masquer certaines sections de votre planning, cochez les codes d'activité correspondant aux activités que vous souhaitez masquer.",
  },
  only: 'Seulement',
  noCompany: 'Aucune entreprise',
  noMessage: 'Aucun message',
  submit_verb: 'Soumettre',
  showAllXActivities: (x: number) =>
    `Afficher toutes les ${x} ${activities(x)}`,
  showXOfYActivities: (x: number, y: number) =>
    `Afficher ${x} sur ${y} ${activities(x)}`,
  verification: 'Vérification',
  xActivitiesAdded: (x: number, wrappedX) => [
    wrappedX,
    ` ${activities(x)} ajoutée${ending(x, '', 's')}.`,
  ],
  seeDetails: 'Voir les détails',
  xTypeActivityCodesAdded: (x: number, wrappedX, type: string) => [
    wrappedX,
    `${type} code d'activité${ending(x)} ajouté${ending(x, '', 's')}.`,
  ],
  activityFilters: "Filtres d'activité",
  scheduleFilters: 'Filtres de planning',
  customFilter: 'Filtre personnalisé',
  filterNameCannotBeEmpty: 'Le nom du filtre ne peut pas être vide',
  suchFilterNameIsAlreadyTaken: 'Ce nom de filtre est déjà utilisé',
  struxHubData: `${brandName} Données`,
  scheduleData: 'Données de planning',
  members: 'Membres',
  projectMembers: 'Membres du projet',
  activityCodes: "Codes d'activité",
  projectMembersSelected: 'Membres du projet sélectionnés',
  renameFilterOptional: 'Renommer le filtre (facultatif)',
  filterName: 'Nom du filtre',
  xCompaniesIdentified: (x: number) =>
    `${x} Entreprise${ending(x, '', 's')} identifiée${ending(x, '', 's')}`,
  xCompaniesShowing: (x: number) =>
    `${x} Entreprise${ending(x, '', 's')} affichée${ending(x, '', 's')}`,
  xCompaniesCount: (x: number) => `${x} Entreprise${ending(x, '', 's')}`,
  xCompanies: (x: number) => `${x} entrepris${ending(x, 'e', 'es')}`,
  activityCodeSelected: "Code d'activité sélectionné",
  responsibleUser: 'Utilisateur responsable',
  responsibilityCode: 'Code de responsabilité',
  responsibilityDescription: 'Description de la responsabilité',
  contractN: 'Contrat n°',
  plannedValueS: 'Valeur prévue ($)',
  uploadAnUpdatedXlsxFile: 'Téléverser un fichier xlsx mis à jour',
  downloadCurrentListAsXlsxFile: 'Télécharger la liste actuelle au format xlsx',
  addNewEntry: 'Ajouter une nouvelle entrée',
  addNewUser: 'Ajouter un nouvel utilisateur',
  newUserCreatedViaSignUp: 'Nouvel utilisateur inscrit',
  newUserAwaitingApproval: "En attente d'approbation",
  previous: 'Précédent',
  activityFiltersDescriptions: {
    confirmDeletionOfX: (x: string) =>
      `Êtes-vous sûr de vouloir supprimer le filtre ${x} ?`,
    title: `${brandName} transforme vos codes d'activité et vos données P6 en filtres pratiques pour que vos équipes puissent facilement trouver leurs activités sur le web et sur mobile. Vous pouvez configurer ces filtres ci-dessous et les mettre à jour à tout moment.`,
    selectProjectMembers:
      'Sélectionnez le(s) membre(s) du projet que vous souhaitez inclure dans ce filtre. Les sélections multiples sont permises.',
    selectActivityCodes:
      "Sélectionnez le(s) code(s) d'activité que vous souhaitez inclure dans ce filtre. Les sélections multiples sont permises.",
    company:
      'Ce filtre permet aux utilisateurs de sélectionner les entreprises responsables des activités de votre projet.',
    level:
      "Ce filtre permet aux utilisateurs de sélectionner les niveaux d'élévation de votre bâtiment.",
    zone: 'Ce filtre permet aux utilisateurs de sélectionner les différentes zones de chaque niveau du bâtiment.',
    activityCode:
      "Ce filtre permet aux utilisateurs de sélectionner des codes supplémentaires d'activités que vous souhaitez suivre.",
    projectMember:
      'Ce filtre permet aux utilisateurs de sélectionner les membres du projet que vous souhaitez suivre.',
    associateCompanies:
      'Veuillez indiquer comment vous associez les entreprises à vos activités dans le planning.',
    resourceCompanies:
      'Les noms des entreprises se trouvent dans le champ Ressource du fichier .xer. Note : ceci est la valeur par défaut si vous avez un planning chargé de ressources.',
    bpCompanies:
      "Les noms des entreprises se trouvent dans un fichier xlsx séparé et associé aux activités via un code d'activité.",
    bpStep1:
      "Sélectionnez le type de code d'activité dans votre fichier .xer qui est associé aux entreprises responsables.",
    bpStep2:
      'Téléversez un fichier Excel contenant au moins les deux en-têtes de colonne suivants : Code de responsabilité et Entreprise.',
  },
  trackingHierarchy: 'Hiérarchie de suivi',
  defaultHierarchies: 'Hiérarchies par défaut',
  defineBandsEnPermissionsForYourTrackingHierarchy:
    'Définissez les bandes et les permissions pour votre hiérarchie de suivi',
  setupDefaultHierarchiesForDifferentViews:
    'Configurez les hiérarchies par défaut pour différentes vues',
  selectBands: 'Sélectionner les bandes',
  selectControlBand: 'Sélectionner la bande de contrôle',
  band: 'Bande',
  addBand: 'Ajouter une bande',
  activityUpdatesOn: 'Mises à jour des activités activées',
  activityUpdatesOff: 'Mises à jour des activités désactivées',
  view_noun: 'Vue',
  wbs: 'Structure de découpage du travail',
  webGantt: 'Gantt Web',
  webSitemap: 'Plan du site Web',
  mobile: 'Mobile',
  table: 'Tableau',
  tablet: 'Tablette',
  hierarchyPreview: 'Aperçu de la hiérarchie',
  unassigned: 'Non assigné',
  photo: 'Photo',
  completed: 'Terminé',
  startEarly: 'Commencer tôt',
  PComplete: '% Complet',
  unassignedActivity: 'Activité non assignée',
  trackingHierarchyDescriptions: {
    title:
      'Configurez votre hiérarchie de suivi ci-dessous pour commencer à suivre votre planning par vos équipes de terrain, par vos sous-traitants, ou par les deux.',
  },
  editCompany: "Éditer l'entreprise",
  addDataToSchedule: 'Ajouter des données au planning',
  plannedCompanyManpower: "Main-d'œuvre prévue de l'entreprise",
  dataToScheduleDescriptions: {
    title: `Avec ${brandName}, vous pouvez rapidement ajouter des données à votre planning en important des données dans des fichiers Excel.`,
  },
  formField: 'Champ de formulaire',
  showField_question: 'Afficher le champ ?',
  mandatory_question: 'Obligatoire ?',
  fieldShown: 'Champ affiché',
  mandatory: 'Obligatoire',
  addNewListElement: 'Ajouter un nouvel élément de liste',
  workflowPreview: 'Aperçu du Workflow',
  setThresholdForDDays: 'Définir le seuil pour ∆ Jours',
  setThresholdForDManpower: "Définir le seuil pour ∆ Main-d'œuvre",
  onTrack: 'Sur la bonne voie',
  watch: 'Surveiller',
  behind: 'En retard',
  dDays: '∆ Jours',
  days: 'Jours',
  percent: 'Pourcent',
  number: 'Nombre',
  daysBehind: 'Jours de retard',
  daysAhead: "Jours d'avance",
  XBehind: '% en retard',
  XAhead: '% en avance',
  under: 'Sous',
  over: 'Sur',
  XUnder: '% sous',
  XOver: '% sur',
  dManpower: "∆ Main-d'œuvre",
  sitemapsDescriptions: {
    title: `Avec ${brandName}, vous pouvez facilement lier votre planning à vos plans de site. Ci-dessous, vous pouvez configurer les valeurs seuils pour les métriques ∆ Jours et ∆ Main-d'œuvre qui sont utilisées pour visualiser les progrès sur le planning et la main-d'œuvre actuelle, respectivement.`,
    configure:
      "Configurer les seuils de statut pour le planning et la main-d'œuvre",
  },
  inbox: 'Boîte de réception',
  unread_2form: 'Non lu',
  read_2form: 'Lu',
  readStatus: 'Statut de notification',
  archived: 'Archivé',
  xArchived: (x: number) => `${x} archivé${x === 1 ? '' : 's'}`,
  xMarkedAsRead: (x: number) =>
    `${x} marqué${x === 1 ? '' : 's'} comme lu${x === 1 ? '' : 's'}`,
  xMarkedAsUnread: (x: number) =>
    `${x} marqué${x === 1 ? '' : 's'} comme non lu${x === 1 ? '' : 's'}`,
  xUnarchived: (x: number) => `${x} désarchivé${x === 1 ? '' : 's'}`,
  requested: 'Demandé',
  changed: 'Modifié',
  scheduled: 'Planifié',
  onSite: 'Sur site',
  denied: 'Refusé',
  passedInspection: 'Inspection réussie',
  failedInspection: 'Inspection échouée',
  done_plural: 'Fait',
  flags: 'Drapeaux',
  workflowSubmitted: 'Workflow soumis',
  workflowApproved: 'Workflow approuvé',
  workflowDone: 'Workflow terminé',
  workflowChanged: 'Workflow modifié',
  workflowDenied: 'Workflow refusé',
  workflowReadyToInspect: 'Workflow prêt à inspecter',
  workflowActivated: 'Workflow activé',
  workflowReviewed: 'Workflow révisé',
  workflowOnSite: 'Workflow sur site',
  workflowFailed: 'Workflow échoué',
  workflowPassed: 'Workflow réussi',
  workflowDeleted: 'Workflow supprimé',
  workflowEnded: 'Workflow terminé',
  workflowFollowed: 'Workflow suivi',
  rfis: 'RFIs',
  scheduleComments: 'Commentaires sur le planning',
  onTime: "À l'heure",
  late: 'En retard',
  performance: 'Performance',
  parameters: 'Paramètres',
  onTimeWindowHr: "Fenêtre à l'heure (hr)",
  showLess: 'Afficher moins',
  showMore: 'Afficher plus',
  all_items: 'Tout',
  my_documents: 'Mes documents',
  my_company: 'Mon entreprise',
  maps: 'Cartes',
  none: 'Aucun',
  all: 'Tous',
  important: 'Important',
  importantTabTooltip:
    'Notifications nécessitant votre attention ou action — inclut les ball-in-court, communications de site et @-mentions.',
  updates: 'Mises à jour',
  updatesTabTooltip:
    'Mises à jour de workflow non marquées comme Importantes — à titre informatif uniquement.',
  batteryLevel: 'Niveau de batterie',
  struxTagState: 'État du StruxTag',
  sensorStateInUseMoving: 'En mouvement',
  sensorStateInUseIdle: 'Au repos',
  sensorStateNoSignal: 'Pas de signal',
  noStruxTagLinked: 'Aucun StruxTag lié',
  noActivitySessions: "Aucune session d'activité",
  activitySessionMoving: 'En mouvement',
  activitySessionIdle: 'Au repos',
  activitySessionOpen: 'Ouverte',
  activitySessionClosed: 'Fermée',
  unArchive: 'Désarchiver',
  archive: 'Archiver',
  leaveConversation: 'Quitter la conversation',
  archiveConversation: 'Archiver la conversation',
  unarchiveConversation: 'Désarchiver la conversation',
  conversationIsArchived: 'Cette conversation est archivée',
  photoMessage: '📷 Photo',
  thisWorkflowIsInProject: (projectName: string) =>
    `Ce Workflow est dans ${projectName}`,
  leaveConversationConfirmation:
    'Êtes-vous sûr de vouloir quitter cette conversation ? Vous en perdrez l’accès.',
  archiveConversationConfirmation:
    'Êtes-vous sûr de vouloir archiver cette conversation ? Elle sera masquée pour tous les membres et deviendra en lecture seule.',
  conversation: 'Conversation',
  reconnecting: 'Reconnexion…',
  noMessagesYet: 'Aucun message pour le moment',
  noMessagesYetSayHello: 'Aucun message pour le moment — dites bonjour…',
  couldNotLoadYourInbox: 'Impossible de charger votre boîte de réception',
  couldNotLoadMessages: 'Impossible de charger les messages',
  couldNotOpenConversation:
    "Impossible d'ouvrir la conversation — vérifiez votre connexion",
  noArchivedConversations: 'Aucune conversation archivée',
  noConversationsFound: 'Aucune conversation trouvée',
  inboxIsEmpty: 'La boîte de réception est vide',
  xMembersYOnline: (members: number, online: number) =>
    `${members} membres, ${online} en ligne`,
  xMembers: (x: number) => `${x} membre${ending(x)}`,
  failedToLeave: 'Impossible de quitter',
  leave_verb: 'Quitter',
  message_noun: 'Message',
  youCanAttachUpToXPhotos: (x: number) =>
    `Vous pouvez joindre jusqu'à ${x} photos à un message.`,
  xFilesCouldNotBeAttached: (x: number) =>
    x > 1
      ? `${x} fichiers n'ont pas pu être joints — images de 15 Mo maximum uniquement.`
      : `1 fichier n'a pas pu être joint — images de 15 Mo maximum uniquement.`,
  notSentTapToRetry: 'Non envoyé — appuyez pour réessayer',
  newChat: 'Nouvelle discussion',
  searchUser: 'Rechercher un utilisateur',
  newGroup: 'Nouveau groupe',
  noPeopleFound: 'Aucune personne trouvée',
  addMembers: 'Ajouter des membres',
  groupDetails: 'Détails du groupe',
  userDetails: "Détails de l'utilisateur",
  groupName: 'Nom du groupe',
  createdByX: (name: string) => `Créé par ${name}`,
  you: 'Vous',
  couldNotCreateGroup: 'Impossible de créer le groupe — réessayez',
  couldNotUpdateGroup: 'Impossible de mettre à jour le groupe — réessayez',
  read: 'Lu',
  unread: 'Non lu',
  wwpColoring: 'Coloration WWP',
  useCSIMaterialCategories: 'Utiliser les catégories de matériaux CSI', // Construction Specifications Institute
  noNotificationsPassedFilters: "Aucune notification n'a passé les filtres",
  replyToActivityFlag: "Répondre au drapeau d'activité",
  activityFlag: "Drapeau d'activité",
  replyToActivityRfi: "Répondre au RFI d'activité",
  activityRfi: "RFI d'activité",
  replyToScheduleComment: 'Répondre au commentaire sur le planning',
  replyToCategoryOfVariance: 'Répondre à la catégorie de variance',
  replyToSafetyHazard: 'Répondre au risque de sécurité',
  scheduleComment: 'Commentaire sur le planning',
  activityUpdate: "Mise à jour d'activité",
  scheduleUpdate: 'Mise à jour du planning',
  safetyHazard: 'Risque de sécurité',
  safetyHazardOpened: 'Risque de sécurité ouvert',
  bulk: 'En masse',
  bulkEdit: 'Édition en masse',
  bulkUpdate: 'Mise à jour en masse',
  bulkEditConfirmQuestion: (fieldCount: number, recordCount: number) =>
    `Modifier ${xFields(fieldCount)} pour ${recordCount} enregistrement${ending(
      recordCount,
    )} ?`,
  replyToActivityUpdate: "Répondre à la mise à jour d'activité",
  activityFollowed: 'Activité suivie',
  deliveryFollowed: 'Livraison suivie',
  replyToScheduleUpdate: 'Répondre à la mise à jour du planning',
  replyToBulkUpdate: 'Répondre à la mise à jour en masse',
  deliveryRequested: 'Livraison demandée',
  deliveryRequestedByLocationClosure: (id: string) =>
    `La livraison (${id}) a été affectée par une fermeture de lieu et doit être reprogrammée`,
  deliveryScheduled: 'Livraison planifiée',
  deliveryOnSite: 'Livraison sur site',
  deliveryPassedInspection: 'Livraison passée inspection',
  deliveryFailedInspection: 'Livraison échouée inspection',
  deliveryInspected: 'Livraison inspectée',
  deliveryDone: 'Livraison effectuée',
  deliveryIncompletelyDone: 'Livraison incomplètement effectuée',
  deliveryUpdated: 'Livraison mise à jour',
  deliveryDeleted: 'Livraison supprimée',
  deliveryDeletedBy: (id: string, user: string, company: string) =>
    `La livraison (${id}) a été supprimée par ${user} chez ${company}`,
  deliveryDenied: 'Livraison refusée',
  deliveryCanceled: 'Livraison annulée',
  deliveryOnHold: 'Livraison en attente',
  deliveryPaused: 'Livraison en pause',
  updateMaterials: 'Mettre à jour les matériaux',
  materialsLoading: 'Chargement des matériaux',
  xMaterials: (count: number) => `${count} Matériau${count > 1 ? 'x' : ''}`,
  xFilesShowing: (count: number) => `${count} fichiers affichés`,
  planned: 'Planifié',
  actual: 'Réel',
  scannerDeactivated: 'Scanner désactivé',
  scheduleUpdated: 'Planning mis à jour',
  passed: 'Réussi',
  failed: 'Échoué',
  scheduling: 'Planification',
  schedule: 'Planning',
  schedule_noun: 'Planning',
  pass_verb: 'Réussir',
  deny_verb: 'Refuser',
  change_verb: 'Modifier',
  accept: 'Accepter',
  fail: 'Échouer',
  incompletelyDone: 'Incomplètement terminé',
  deleted: 'Supprimé',
  deactivated: 'Désactivé',
  activate: 'Activer',
  deactivate: 'Désactiver',
  canceled: 'Annulé',
  siteNotReady: 'Site non prêt',
  materialProblem: 'Problème de matériel',
  laborIssue: "Problème de main-d'œuvre",
  qualityIssue: 'Problème de qualité',
  clarificationNeeded: 'Clarification nécessaire',
  incorrectSpecification: 'Spécification incorrecte',
  materialQuestion: 'Question sur le matériel',
  superintendentNeeded: 'Surintendant nécessaire',
  plusXMore: (x: number) => `+ ${x} de plus`,
  submittalInReview: 'Soumission en cours de révision',
  submittalComplete: 'Soumission complète',
  inFabrication: 'En fabrication',
  fabricationComplete: 'Fabrication terminée',
  inStorage: 'En stockage',
  installed: 'Installé',
  delivery: 'Livraison',
  newRequest: 'Nouvelle demande',
  deleteDelivery: 'Supprimer la livraison',
  yesDeleteDelivery: 'Oui, supprimer la livraison',
  cancelDelivery: 'Annuler la livraison',
  yesCancelDelivery: 'Oui, annuler la livraison',
  changeDate: 'Changer la date',
  yesMoveTheDate: 'Oui, déplacer la date',
  requestIsNotValid: "La demande de livraison n'est pas valide",
  datePassedDeadline: 'La date a dépassé la date limite',
  requestAnyway: 'Demander quand même',
  requestAccessTo: "Demander l'accès à",
  thisRequestHasPassedTheDeadline: 'Cette demande a dépassé la date limite',
  noDeadline: 'Aucune date limite',
  twentyFourHours: '24 heures',
  fortyEightHours: '48 heures',
  seventyTwoHours: '72 heures',
  fourDays: '4 jours',
  fiveDays: '5 jours',
  oneWeek: '1 semaine',
  twoWeeks: '2 semaines',
  oneMonth: '1 Mois',
  twoMonths: '2 Mois',
  threeMonths: '3 Mois',
  sixMonths: '6 Mois',
  twelveMonths: '12 Mois',
  dateAndTime: 'Date et heure',
  startDate: 'Date de début',
  endDate: 'Date de fin',
  finishDate: 'Date de fin',
  addFinishDate: 'Ajouter une date de fin',
  removeFinish: 'Retirer la fin',
  startTime: 'Heure de début',
  endTime: 'Heure de fin',
  nextDay: 'jour suivant',
  selectTime: "Sélectionner l'heure",
  location: 'Emplacement',
  locations: 'Emplacements',
  locationFrom: 'Emplacement de départ',
  locationTo: "Emplacement d'arrivée",
  formType: 'Type de Workflow',
  title: 'Titre',
  noAvailableTemplates: 'Aucun modèle disponible',
  editForm: 'Éditer le workflow',
  contact: 'Contact',
  offloadingEquipment: 'Équipement de déchargement',
  vehicles: 'Véhicules',
  material: 'Matériel',
  materialDeliveryLocation: 'Lieu de livraison du matériel',
  materialsCategory: 'Catégorie de matériaux',
  inspectionRequired: 'Inspection requise',
  supplierAndDriver: 'Fournisseur et conducteur',
  anySpecialInstructions: 'Instructions spéciales ?',
  deliverySitemap: 'Plan du site de livraison',
  gate: 'Porte',
  area: 'Zone',
  fromToDelivery: 'Livraison de A à Z',
  installationZone: "Zone d'installation",
  deliveryVehicleType: 'Type de véhicule de livraison',
  numberOfVehicles: 'Nombre de véhicules',
  numberOfVehicleLicensePlate: "Modèle de véhicule et plaque d'immatriculation",
  equipment: 'Équipement',
  materialResource: 'ressource matérielle',
  struxTags: 'StruxTags',
  registerStruxTag: 'Enregistrer StruxTag',
  register: 'Enregistrer',
  registering: 'Enregistrement...',
  infuseId: 'ID Infuse',
  infuseIdIsRequired: "L'ID Infuse est requis",
  infuseIdMustNotContainSpaces: "L'ID Infuse ne doit pas contenir d'espaces",
  failedToRegisterStruxTag: "Échec de l'enregistrement du StruxTag",
  sharedResources: 'Ressources partagées',
  units: 'Unités',
  inspector: 'Inspecteur',
  vendorEmails: 'emails des fournisseurs',
  driverMobilePhoneNumbers: 'Numéros de téléphone mobile des conducteurs',
  specialInstructions: 'Instructions spéciales',
  linkedActivity: 'Activité liée',
  customActionButton: "Bouton d'action personnalisé",
  building: 'Bâtiment',
  route: 'Itinéraire',
  staging: 'Mise en scène',
  interiorPath: 'Chemin intérieur',
  interiorDoor: 'Porte intérieure',
  bookingDate: 'Date de réservation',
  requesterCompany: 'Entreprise demanderesse',
  duration: 'Durée',
  onsiteContact: 'Contact sur site',
  onsiteContacts: 'Contacts sur site',
  category: 'Catégorie',
  materialNote: 'Note sur le matériel',
  unnamedMaterial: 'Matériel non nommé',
  unnamedCompany: 'Entreprise sans nom',
  required: 'Requis',
  optional: 'Facultatif',
  multipleSelection: 'Sélection multiple',
  adding: 'Ajout',
  findOrAddNewEntry: 'Trouver ou ajouter une nouvelle entrée',
  findEntry: 'Trouver une entrée',
  yes: 'Oui',
  no: 'Non',
  unscheduledDelivery: 'Livraison non planifiée',
  uploadASitemapToShowGatesAndAreas:
    'Téléverser un plan pour montrer les portes et les zones',
  uploadAViewToShowGatesAndAreas:
    'Téléverser une vue pour montrer les portes et les zones',
  uploadASitemapToShowBuilding: 'Téléverser un plan pour montrer le bâtiment',
  buildingAccessMap: "Plan d'accès au bâtiment",
  uploadASitemapToShowGates: 'Téléverser un plan pour montrer les portes',
  gateAccessMap: "Plan d'accès aux portes",
  uploadASitemapToShowEquipment: "Téléverser un plan pour montrer l'équipement",
  equipmentAccessMap: "Plan d'accès à l'équipement",
  zoneAccessMap: "Plan d'accès aux zones",
  uploadASitemapToShowAreas: 'Téléverser un plan pour montrer les zones',
  routeAccessMap: "Plan d'accès à l'itinéraire",
  uploadASitemapToShowRoute: "Téléverser un plan pour montrer l'itinéraire",
  deliveryWorkflow: 'Workflow de livraison',
  workflowMenu: 'Menu de Workflow',
  addANoteToThisDelivery: 'Ajouter une note à cette livraison',
  send: 'Envoyer',
  deliveryDescriptions: {
    confirmDelete:
      'La suppression de cette livraison supprimera la livraison et toutes les notes et données associées du projet.',
    confirmDelete2:
      'Faites cela uniquement si la demande de livraison était une erreur.',
    confirmCancel:
      "Les livraisons annulées resteront sur le calendrier mais seront marquées comme 'Annulées'.",
    confirmCancel2: 'Souhaitez-vous annuler cette livraison ?',
    nonWorkTimeDateChange: (days: number) =>
      `Veuillez déplacer cette livraison. Les demandes doivent être faites ${days} jour${ending(
        days,
      )} ouvrable${ending(
        days,
      )} à l'avance par rapport à la date et à l'heure que vous demandez et à une date-heure de travail`,
    maximumDeliveriesAreScheduled:
      'Le nombre maximum de livraisons est déjà prévu pour cet emplacement',
  },
  loginInfoPageDescriptions: {
    requestSentTitle: 'Demande envoyée',
    requestApprovedTitle: 'Demande approuvée',
    invalidInvitationTitle: "Lien d'invitation invalide",
    somethingWentWrongTitle: "Une erreur s'est produite",
    accountRequestPendingEmail:
      "L'administrateur examine votre demande. Vous recevrez un e-mail d'invitation dès qu'elle sera approuvée. Consultez votre boîte de réception.",
    accountRequestPendingSms:
      "L'administrateur examine votre demande. Vous recevrez un lien d'invitation par SMS dès qu'elle sera approuvée.",
    accessRequestPending:
      "L'administrateur examine votre demande. Vous recevrez une invitation dès qu'elle sera approuvée.",
    accountRequestApproved:
      "Un e-mail d'invitation a été envoyé dans votre boîte de réception. Pour vous connecter, suivez les instructions qu'il contient.",
    goToAvailableProject: 'Aller à un Projet disponible',
    invalidInvitationDescription:
      "Ce lien d'invitation est incomplet ou endommagé. Ouvrez-le à nouveau depuis votre invitation, ou demandez à votre administrateur de vous en envoyer un nouveau.",
    somethingWentWrongDescription:
      "Nous n'avons pas pu traiter votre demande. Réessayez plus tard, ou contactez le support si le problème persiste.",
    expiredInvitationTitle: 'Votre invitation a expiré',
    expiredInvitationDescription: (hours: number) =>
      `Pour des raisons de sécurité, les liens d'invitation expirent. Renvoyez l'invitation pour recevoir un nouveau lien, valable ${xHours(hours)}.`,
    resendMyInvitationLink: "Renvoyer mon lien d'invitation",
    invitationResentTitle: 'Invitation renvoyée',
    invitationResentDescription:
      'Consultez vos e-mails et suivez le nouveau lien.',
  },

  ssoAdminConsentPage: {
    grantedTitle: 'Consentement administrateur accordé',
    grantedDescription: `L'authentification unique Microsoft est désormais activée pour votre organisation. Votre équipe peut se connecter à ${brandName} avec ses comptes Microsoft.`,
    notCompletedTitle: "Le consentement n'a pas été finalisé",
    notCompletedDescription: `Le consentement administrateur n'a pas été accordé. Vous pouvez réessayer le lien de consentement ou contacter le support ${brandName}.`,
    continueToApp: `Continuer vers ${brandName}`,
    backToApp: `Retour à ${brandName}`,
    directory: 'Annuaire',
  },
  assignResponsibilitiesConfirmation:
    "Confirmation de l'attribution des responsabilités",
  assignResponsibilities: 'Attribuer des responsabilités',
  assignToActivities: 'Attribuer aux activités',
  groupByTeam: 'Grouper par équipe',
  noTeam: '[aucune équipe]',
  hashActivities: '# Activités',
  rfi: 'RFI',
  flag: 'Drapeau',
  reply: 'Répondre',
  unspecifiedType: '(Type non spécifié)',
  contractsOrRevisions: 'Contrats/Révisions',
  priorWorkNotComplete: 'Travail préalable non achevé',
  informationNotAvailable: 'Informations non disponibles',
  unclearTaskDescription: 'Description de tâche floue',
  staffNotAvailable: 'Personnel non disponible',
  materialsNotAvailable: 'Matériaux non disponibles',
  noInformationAvailableOnThisMonitoring:
    'Aucune information disponible sur ce suivi',
  conditionsOfSatisfactions: 'Conditions de satisfaction',
  taskSequenceChange: 'Changement de séquence des tâches',
  incorrectTimeEstimate: 'Estimation de temps incorrecte',
  offProjectDemands: 'Demandes hors projet',
  unforeseenConditions: 'Conditions imprévues',
  weather: 'Météo',
  weatherDescriptions: {
    'clear sky': 'Ciel dégagé',
    'few clouds': 'Peu nuageux',
    'scattered clouds': 'Nuages épars',
    'overcast clouds': 'Nuages couverts',
    'broken clouds': 'Nuages fragmentés',
    'light rain': 'Pluie légère',
    'heavy intensity rain': 'Pluie forte',
    'light snow': 'Neige légère',
    'heavy snow': 'Neige forte',
    'shower sleet': 'Pluie verglaçante',
    'freezing rain': 'Pluie verglaçante',
    'very heavy rain': 'Pluie très forte',
    thunderstorm: 'Orage',
    snow: 'Neige',
    mist: 'Brouillard',
  },
  humidity: 'humidité',
  precipitation: 'précipitation',
  temperatureRange: {
    low: 'bas',
    high: 'haut',
    avg: 'moy.',
  },
  fiveDayForecast: 'Prévision sur 5 jours',
  elevatedWork: 'Travail en hauteur',
  materialHandling: 'Manutention de matériaux',
  groundDistribution: 'Distribution au sol',
  craneOrHeavyLift: 'Grue / Levage lourd',
  confinedSpace: 'Espace confiné',
  mobileEquipment: 'Équipement mobile',
  deliver: 'Livrer',
  loto: 'LOTO',
  hotWork: 'Travail à chaud',
  hazardousMaterial: 'Matériau dangereux',
  highVoltage: 'Haute tension',
  highDecibels: 'Décibels élevés',
  lifting: 'Levage',
  closeQuarters: 'Quartiers fermés (<6ft)',
  rfiOpened: 'RFI ouvert',
  flagOpened: 'Drapeau ouvert',
  scheduleCommentOpened: "Commentaire de l'agenda ouvert",
  addANote: 'Ajouter une note',
  addToLog: 'Ajouter au journal',
  emojiPicker: 'Sélecteur d’émojis',
  addPhotoFromLibrary: 'Ajouter une photo depuis la galerie',
  addFile: 'Ajouter un fichier',
  atMentionAUser: 'Mentionner un utilisateur',
  atMentionUserDescription:
    'Pour notifier et ajouter un utilisateur comme contact supplémentaire au workflow',
  start_noun: 'Début',
  end_noun: 'Fin',
  forDate: (date: string) => `Pour ${date}`,
  addANoteToThisStatusUpdate: 'Ajouter une note à cette mise à jour de statut',
  chooseADeliveryTime: 'Choisir un horaire de livraison',
  allZones: 'Toutes les zones',
  allEquipment: "Tout l'équipement",
  selectDate: 'Sélectionner la date',
  selectMonth: 'Sélectionner le mois',
  selectDateRange: 'Sélectionner la plage de dates',
  selectField: 'Sélectionner le champ',
  selectDateAndTime: "Sélectionner la date et l'heure",
  jumpToDate: 'Aller à la date',
  jumpToMap: 'Aller à la carte',
  newDelivery: 'Nouvelle livraison',
  newForm: 'Nouveau Workflow',
  pending: 'En attente',
  confirmChange: 'Confirmer le changement',
  okToProceed: 'OK pour procéder ?',
  proceed: 'Procéder',
  actualStart: 'Début réel',
  updated: 'Mis à jour',
  approved: 'Approuvé',
  pendingApproval: "En attente d'approbation",
  missing: 'Manquant',
  actualFinish: 'Fin réelle',
  xDaysUpdated: (x: number) => `${x} jours mis à jour!`,
  copyToMissing: 'Copier vers manquant',
  copy: 'Copier',
  copiedToClipboard: 'Copié dans le presse-papiers',
  traceId: 'ID de trace',
  copyTraceId: "Copier l'ID de trace",
  copyLinkToDelivery: 'Copier le lien vers la livraison',
  copyLink: 'Copier le lien',
  enabled: 'Activé',
  disabled: 'Désactivé',
  checkInUnscheduledDelivery: "Enregistrement d'une livraison non planifiée",
  paste: 'Coller',
  estimateMissing: 'Estimation manquante',
  activityDetails: "Détails de l'activité",
  assigned: 'Attribué',
  followed: 'Suivi',
  following: 'Suivant',
  notFollowing: 'Ne suit pas',
  predecessors: 'Prédécesseurs',
  successors: 'Successeurs',
  viewTeam: "Voir l'équipe",
  dates: 'Dates',
  plannedStart: 'Début planifié',
  plannedEnd: 'Fin planifiée',
  actualEnd: 'Fin réelle',
  activityLog: "Journal d'activité",
  when: 'Quand',
  date: 'Date',
  time: 'Heure',
  where: 'Où',
  min_shortMinutes: 'min',
  hr_shortHours: 'hr',
  requester: 'Demandeur',
  responsible: 'Responsable',
  subscriber: 'Abonné',
  directAssignments: 'Affectations directes',
  otherSubmissions: 'Autres soumissions',
  onsiteContactPerson: 'Personne de contact sur site',
  onsiteContactPersons: 'Personnes de contact sur site',
  vehicle: 'Véhicule',
  quantity: 'Quantité',
  inspection: 'Inspection',
  thisDeliveryWasNotScheduledInAdvance:
    "Cette livraison n'était pas planifiée à l'avance",
  thisDeliveryWasScheduledForNonWorkTime:
    'Cette livraison était prévue pour un jour ou une heure non ouvrable',
  sitemap: 'Plan du site',
  flagAnIssue: 'Signaler un problème',
  openAnRfi: 'Ouvrir un RFI',
  requestADelivery: 'Demander une livraison',
  reason: 'Raison',
  addReasonForFlag: 'Ajouter une raison pour le drapeau',
  addReasonForRfi: 'Ajouter une raison pour le RFI',
  addReasonForCategoryOfVariance:
    'Ajouter une raison pour la catégorie de variance',
  addReasonForSafetyHazard: 'Ajouter une raison pour le risque de sécurité',
  addANoteToThisActivityLog: "Ajouter une note à ce journal d'activité",
  uploadingImage: "Téléchargement d'image",
  ganttDescriptions: {
    assignXUsersToYActivities: (x: number, y: number) =>
      `Affectation des ${x} membres d'équipe suivants à ${y} activités sélectionnées`,
  },
  actualDatesDescriptions: {
    newStartDateAfterOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Définir ',
      wrapper(newDate),
      ' comme début réel annulera ',
      wrapper(oldDate),
      ` comme début réel et ignorera toutes les mises à jour avant ${newDate}.`,
    ],
    newStartDateBeforeOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Définir ',
      wrapper(newDate),
      ' comme début réel annulera ',
      wrapper(oldDate),
      ' comme début réel et préservera toutes les données associées.',
    ],
    newEndDateAfterOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Définir ',
      wrapper(newDate),
      ' comme fin réelle annulera ',
      wrapper(oldDate),
      ' comme fin réelle et ignorera sa valeur de % achevé.',
    ],
    newEndDateBeforeOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Définir ',
      wrapper(newDate),
      ' comme fin réelle annulera ',
      wrapper(oldDate),
      ` comme fin réelle et ignorera toutes les mises à jour après ${newDate}.`,
    ],
  },
  offloadingEquipmentMaps: (x: number) =>
    `Plan d'équipement de déchargement${ending(x)}`,
  filterOptions: 'Filtrer les options',
  selectOptions: 'Sélectionner les options',
  filterByLBS: 'Filtrer par LBS',
  auto: 'auto',

  level: 'Niveau',
  zone: 'Zone',
  siteLocation: 'Emplacement du site',
  verticalObject: 'Objet vertical',
  manpower: "Main-d'œuvre",
  manPower: "Main-d'œuvre",
  description: 'Description',
  code: 'Code',
  codeVerifying: 'Vérification du code',
  codeResending: 'Renvoi du code',
  resendCode: 'Renvoyer le code',
  contract: 'Contrat',
  plannedValue: 'Valeur prévue',
  bidStatus: "Statut de l'offre",
  street: 'Rue',
  satellite: 'Satellite',
  layers: 'Couches',
  terrain: 'Terrain',
  transit: 'Transit',
  traffic: 'Trafic',
  more: 'Plus',
  mapType: 'Type de carte',
  mapSettings: 'Paramètres de la carte',
  mapDetails: 'Détails de la carte',
  overlays: 'Superpositions',
  labelsDisplay: 'Affichage des étiquettes',

  allowTextAndIconToScale:
    'Permettre le redimensionnement du texte et des icônes',
  allowLabelsToOverlap: 'Permettre le chevauchement des étiquettes',
  showWorkflowPinLabels: 'Afficher les étiquettes des épingles de workflow',
  workflowMarkups: 'Annotations de workflow',
  showForMe: 'Afficher pour moi',
  showAssociationLabels: 'Afficher les étiquettes d’association',
  showWorkflowMarkupsForEveryone:
    'Afficher par défaut pour tous dans ce projet',
  showAdjacent: 'Afficher les adjacentes',
  markupsShortcutHint: 'active/désactive',
  clickAgainToEditPoints: 'Cliquez à nouveau pour modifier les points',
  longPressToRemovePoint: 'Appui long sur un point pour le supprimer',
  finishShaping: 'Terminer la forme',
  discardShape: 'Annuler la forme',
  finishLine: 'Terminer la ligne',
  discardLine: 'Annuler la ligne',
  labels: 'Étiquettes',
  threeDBuildings: 'Bâtiments 3D',
  resetViewport: 'Réinitialiser la vue',
  toggleProjection: 'Changer de projection',
  moreOptions: "Plus d'options",
  showPlan: 'Montrer le plan',
  showWorkflows: 'Montrer les Workflows',
  version: 'Version',
  revision: 'Révision',
  build: 'Build',
  viewName: 'Nom de la vue',
  viewImage: 'Image de la vue',
  create: 'Créer',
  shareToProject: 'Partager avec le projet',
  private: 'Privée',
  listSections: {
    shown: 'Montré',
    hidden: 'Cachée',
  },
  deliveryIdNotProvided: 'ID de livraison non fourni',
  sendingDeliveryTicket: 'Envoi du billet de livraison au conducteur',
  ticketSuccessfullySent: 'Billet envoyé avec succès',
  failedToSendTicket: "Échec de l'envoi du billet",
  invalidDeliveryTicket: 'Billet de livraison invalide',
  deliveryDetails: 'Détails de la livraison',
  onSiteLogistics: 'Logistique sur site',
  showAtEntryGate: "Afficher à l'entrée",
  showAtX: (location: string) => `Afficher à ${location}`,
  deliveryTicket: 'Billet de livraison',
  hasDelivery: 'a une livraison',
  duplicate_verb: 'Dupliquer',
  createFromToDelivery:
    'Créer une livraison de A à Z. La livraison actuelle doit être marquée ≥ [Sur site]',
  duplicateCurrentDelivery: 'Dupliquer la livraison actuelle',
  unsubscribeToDelivery: 'Se désabonner de la livraison',
  subscribeToDelivery: "S'abonner à la livraison",
  sharedDelivery: 'Livraison partagée',
  requiredFieldCannotBeEmpty: 'Le champ requis ne peut pas être vide',
  fieldShouldBeUnique: 'Le champ doit être unique',
  pleaseCorrectErrors: 'Veuillez corriger les erreurs',
  pleaseFillAllFields: 'Veuillez remplir tous les champs',
  companiesUpdated: 'Entreprises mises à jour',
  companyAdded: 'Entreprise ajoutée',
  forms: 'Formulaires',
  formTypes: 'Types de Workflows',

  legacy: 'Héritage',
  deliveryLocations: 'Lieux de livraison',
  cards: 'Cartes',
  columns: 'Colonnes',
  struxhubLabs: `${brandName} labs`,
  mobileConfiguration: 'Configuration mobile',
  additionalDataUpload: 'Téléversement de données supplémentaires',
  onTimeThresholds: 'Seuils de ponctualité',
  qrScannersSetup: 'Configuration des scanners QR',
  businessPhone: 'Téléphone professionnel',
  businessEmail: 'Email professionnel',
  responsibles: 'Responsables',
  responsibleContact: 'Contact responsable',
  dba: 'DBA',
  licenseNumber: 'Numéro de licence',
  deliveryId: 'ID de livraison',
  bookingTime: 'Heure de réservation',
  enterVerificationCodeFromSMS: 'Entrer le code de vérification reçu par SMS',
  getDirections: 'Obtenir des directions',
  showDirectionsOnMap: "Afficher l'itinéraire sur la carte",
  copyGoogleMapsLink: 'Copier le lien Google Maps',
  copyLocationCoordinates: "Copier les coordonnées de l'emplacement",
  moreActions: "Plus d'actions",
  copyFailed: 'Impossible de copier',
  userInviteMessages: {
    oneUserWasInvitedToProject: (userName: string) =>
      `${userName} a été invité au projet`,
    manyUsersWereInvitedToProject: (count: number) =>
      `${xUsers(count)} ont été invités au projet`,
  },
  userEmailLinking: {
    linkYourEmail: 'Liez votre email',
    linkYourEmailAndSetPassword:
      'Liez votre email et définissez un mot de passe',
    intro: `Vous pouvez également utiliser votre e-mail pour vous connecter à ${brandName}. Cette étape est facultative.`,
    setEmailWithPassword: 'Veuillez créer un mot de passe.',
    weakPassword: 'Mot de passe faible. Vérifiez le format valide',
    emailHasBeenLinkedSuccessfully: "L'email a été lié avec succès",
  },

  thereIsNoProject:
    "Il n'y a aucun projet disponible pour votre compte utilisateur. Veuillez contacter l'administrateur",
  noProjectsAvailableTitle: 'Aucun projet disponible',
  repeatOn: 'Répéter le',
  operatorValues: {
    isNot: "N'EST PAS",
    is: 'EST',
    are: 'SONT',
    and: 'ET',
    or: 'OU',
    for: 'POUR',
    assign: 'ATTRIBUER',
    if: 'SI',
  },
  any: "N'importe",
  selectCompany: "Sélectionner l'entreprise",
  selectPermittedCompanies: 'Sélectionner les Entreprises Autorisées',
  selectLocation: "Sélectionner l'emplacement",
  approval: 'Approbation',
  firewatch: 'Surveillance du feu',
  noRules: 'Aucune règle',
  rule: 'Règle',
  xOfY: (x: number, y: number) => `${x} sur ${y}`,
  options: 'Options',
  selectActivityGroups: "Sélectionner les groupes d'activités",
  selectActionType: "Sélectionnez le type d'action",
  numberOfBands: 'Nombre de bandes',
  lbs: 'LBS', // structure de répartition par emplacement
  lbsTag: 'Étiquette LBS',
  lbsTagTooltip:
    'LBS (structure de répartition par emplacement) définit où se trouve une ressource dans le projet. Sélectionnez la ressource associée à la vue cartographique.',
  workBreakdownStructureWbs: 'Structure de découpage du travail (WBS)',
  locationBreakdownStructureLbs: 'Structure de découpage par emplacement (LBS)',
  groupByOptions: 'Options de regroupement',
  showEmptyBandsLevels: 'Afficher les niveaux de bandes vides',
  customGroupCreation: 'Création de groupe personnalisé',
  bandX: (x: number) => `Bande ${x}`,
  custom: 'Personnalisé',
  groupByX: (x: string) => `Grouper par: ${x}`,
  bookingId: 'ID de réservation',
  responsibleCompany: 'Entreprise responsable',
  bookingDateEnTimeOffset: (offset: string) =>
    `Date et heure de réservation (${offset})`,
  durHmm_durationShort: 'Dur. (h:mm)',
  locationStatus: "Statut de l'emplacement",
  closeLocations: 'Fermer les emplacements',
  closeResources: 'Fermer les ressources',
  openLocations: 'Ouvrir les emplacements',
  openResources: 'Ouvrir les ressources',
  selectFields: 'Sélectionner des champs',
  selectProperty: 'Sélectionner une propriété',
  addAction: 'Ajouter une action',
  closureStart: 'Début de la fermeture',
  closureFinish: 'Fin de la fermeture',
  permittedCompany: 'Entreprise autorisée',
  closureWorkflow: 'Workflow de fermeture',
  updateForm: 'Mettre à jour le Workflow',
  formStatus: 'Statut du Workflow',
  formStart: 'Début du Workflow',
  formFinish: 'Fin du Workflow',
  materialName: 'Nom du matériel',
  productCategory: 'Catégorie de produit',
  deliveryMaterialNotes: 'Notes sur les matériaux de livraison',
  vendor: 'Vendeur',
  quantityOnsite: 'Quantité sur site',
  storageArea: 'Zone de stockage',
  requiredOnsiteDate: 'Date requise sur site',
  nextDelivery: 'Prochaine livraison',
  deliveryBookingDate: 'Date de réservation de la livraison',
  bookedQuantity: 'Quantité réservée',
  recordLastUpdated: 'Dernière mise à jour du dossier',
  numObjects: '# Objets',
  showOn: 'Afficher sur',
  noShow: 'Aucune présentation',
  moved: 'Déplacé',
  otherReason: 'Autre raison',
  mostPertinentDefault: 'Le plus pertinent (par défaut)',
  zones: 'Zones',
  gates: 'Portes',
  routes: 'Itinéraires',
  areas: 'Zones',
  levels: 'Niveaux',
  totalX: (x: number) => `Total ${x}`,
  deliveriesEnEquipmentNeedsPerHour:
    'Livraisons et besoins en équipement par heure',
  resetFilters: 'Réinitialiser les filtres',
  saved_activities: 'Enregistré',
  since: 'Depuis',
  lastP6Update: 'Dernière mise à jour P6',
  lastWeek: 'La semaine dernière',
  lastMonth: 'Le mois dernier',
  last6Week: 'Les 6 dernières semaines',
  dateRange: 'Plage de dates',
  dateRangeForRecentlyUpdatedActivities:
    'Plage de dates pour les activités récemment mises à jour',
  myTasks: 'Mes tâches',
  recentlyUpdated_activities: 'Récemment mis à jour',
  activitiesWithSpecificStatus: 'Activités avec un statut spécifique',
  actualizedFromSchedule_activities: 'Actualisées à partir du planning',
  activitiesWithOpenScheduleComment:
    'Activités avec un commentaire ouvert sur le planning',
  notCompleted_activities: 'Non complétées',
  criticalPathActivities: 'Activités du chemin critique',
  flaggedActivities: 'Activités signalées',
  lateActivities: 'Activités en retard',
  activitiesWithOpenRFI: 'Activités avec un RFI ouvert',
  activitiesWithDeliveries: 'Activités avec des livraisons',
  numberOfActivities: "Nombre d'activités",
  seeXActivities: (x: number) => `Voir ${x} ${activities(x)}`,
  type: 'Type',
  types: 'Types',
  assigners: 'Affectateurs',
  items: 'Articles',
  projectMember: 'Membre du projet',
  disableFilter: 'Désactiver le filtre',
  totalDeliveries: 'Total des livraisons',
  totalSitemaps: 'Total des plans de site',
  totalForms: 'Total des Workflows',
  totalAttachments: 'Total des pièces jointes',
  totalReports: 'Total des rapports',
  totalClosures: 'Total des fermetures',
  totalSensors: 'Total des capteurs',
  seeXUsers: (x: number) => `Voir ${xUsers(x)}`,
  totalLogistics: 'Total des Workflows',
  totalMaterials: 'Total des matériaux',
  seeXDeliveries: (x: number) => `Voir ${x} ${deliveries(x)}`,
  xPlans: (x: number) => `${x} ${plans(x)}`,
  seeXObjects: (x: number) => `Voir ${x} Objet${ending(x)}`,
  seeXSitemaps: (x: number) => `Voir ${x} Plan de site${ending(x)}`,
  seeXForms: (x: number) => `Voir ${xForms(x)}`,
  seeXClosures: (x: number) => `Voir ${x} Fermeture${ending(x)}`,
  seeXSensors: (x: number) => `Voir ${x} Capteur${ending(x)}`,
  seeXLogistics: (x: number) => `Voir ${x} workflows`,
  seeXMaterials: (x: number) => `Voir ${x} Matériel${ending(x)}`,
  seeXReports: (x: number) => `Voir ${x} Rapport${ending(x)}`,
  seeXAttachments: (x: number) =>
    `Voir ${x} Pièce${ending(x)} jointe${ending(x)}`,
  currentLookahead: 'Prévision actuelle',
  fullProject: 'Projet complet',
  numberOfDeliveries: 'Nombre de livraisons',
  numberOfWorkflows: 'Nombre de Workflows',
  savedFilters: 'Filtres enregistrés',
  saved_deliveries: 'Enregistré',
  xDeliveries: (x: number) => `${x} ${deliveries(x)}`,
  xWorkflows: (x: number) => `${x} ${workflows(x)}`,
  savedActivitySets: "Ensembles d'activités enregistrés",
  thereAreNoSavedActivityPresets:
    "Il n'y a aucun ensemble d'activités enregistré",
  xForms,
  xFilters: (x: number) => `${x} filtre${ending(x)}`,
  thereAreNoSavedFilters: "Il n'y a aucun filtre enregistré",
  makePublic: 'Rendre public',
  workPackageName: 'Nom du paquet de travail',
  selectedFilters: 'Filtres sélectionnés',
  share: 'Partager',
  shareWithLink: 'Partager avec un lien',
  saveCurrentFilters: 'Enregistrer les filtres actuels',
  saveANewCustomFilter: 'Enregistrer un nouveau filtre personnalisé',
  editACustomFilter: 'Modifier un filtre personnalisé',
  oneDay: 'Un jour',
  oneDayDefaultToday: "Un jour par défaut aujourd'hui",
  week: 'Semaine',
  fixedWeek: 'Semaine fixe',
  fixedTwoWeeks: '2 semaines fixes',
  fixedThreeWeeks: '3 semaines fixes',
  threeWeeks: '3 semaines',
  fourWeeks: '4 semaines',
  fixedFourWeeks: '4 semaines fixes',
  month: 'Mois',
  fixedSixWeeks: '6 semaines fixes',
  sixWeeks: '6 semaines',
  threeMonth: '3 mois',
  year: 'Année',
  other: 'Autre',
  xDateToYDate: (xDate: string, yDate: string) => `${xDate} à ${yDate}`,
  assignUsersOrCategories: 'Attribuer des utilisateurs ou des catégories',
  searchUserCompanyTeamOrRole:
    'Rechercher Utilisateur, Entreprise, Équipe ou Rôle',
  successfullyUpdatedExistingAssignments:
    'Mises à jour des affectations existantes réussies',
  successfullyDeletedAssignments: 'Suppression des affectations réussie',
  approve_verb: 'Approuver',
  readyToInspect: 'Prêt à inspecter',
  toInspect: 'À inspecter',
  notInUse: 'Non utilisé',
  everyXDaysInspection: (x: number) =>
    `Inspection tous les ${x} jour${ending(x)}`,
  everyXWeeksInspection: (x: number) =>
    `Inspection toutes les ${x} semaine${ending(x)}`,
  everyXMonthsInspection: (x: number) =>
    `Inspection tous les ${x} mois${ending(x)}`,
  addInspection: 'Ajouter une inspection',
  finish_noun: 'Fin',
  legend: 'Légende',
  startFinishState: 'État de début/fin',
  intermediateState: 'État intermédiaire',
  action: 'Action',
  ballInCourtStep: 'Étape de la balle au tribunal',
  active: 'Actif',
  struxhubActivityX: (code: string) => `Activité ${brandName} ${code}`,
  openActivity: "Ouvrir l'activité",
  openDelivery: 'Ouvrir la livraison',
  loadingPresentationMode: 'Chargement du mode de présentation',
  xSelected: (x: number) => `Sélection : ${x}`,
  xItemsCount: (x: number) => `${x} Article${ending(x)}`,
  xTypesCount: (x: number) => `${x} Type${ending(x)}`,
  xFormsCount: (x: number) => `${x} Formulaire${ending(x)}`,
  xApplicationsCount: (x: number) => `${x} Application${ending(x)}`,
  xStepsCount: (x: number) => `${x} Étape${ending(x)}`,
  xFieldsCount: (x: number) => `${x} Champ${ending(x)}`,
  xOfYSelected: (x: number, y: number) => `${x} sur ${y} sélectionné`,
  xDeliveriesShowing: (x: number) => `${x} ${deliveries(x)} affiché`,
  xEquipment: (x: number) => `${x} Équipement${ending(x)}`,
  xSensorsShowing: (x: number) =>
    `${x} capteur${ending(x)} affiché${ending(x, '', 's')}`,
  xStruxTags: (x: number) => `${x} ${struxTagsCount(x)}`,
  xResourcesShowing: (x: number, countLabel: string) =>
    `${x} ${countLabel} affiché${ending(x, '', 's')}`,
  assign_verb: 'Assigner',
  subscribe_verb: "S'abonner",
  new: 'Nouveau',
  completedTotal: 'Complété/Total',
  searchByBookingIdOrSubscribers: 'Rechercher par ID de réservation ou abonnés',
  me: 'Moi',
  full_period: 'Complet',
  day: 'Jour',
  calendarView: 'Vue calendrier',
  ganttView: 'Vue Gantt',
  listView: 'Vue liste',
  mapView: 'Vue carte',
  dur_duration: 'Dur',
  loc_locations: 'Loc',
  equip_equipment: 'Équip',
  mat_materials: 'Mat',
  vend_vendor: 'Vend',
  a_actualFlag: 'A',
  unspecified: 'Non spécifié',
  yesUnsubscribe: 'Oui, se désabonner',
  deliveriesListDescriptions: {
    subscribeConfirmMassage: (
      dUnsubscribedCount: number,
      dSelectedCount: number,
    ) =>
      `S'abonner à [${dUnsubscribedCount}] désabonné${ending(
        dUnsubscribedCount,
      )} ${deliveries(
        dUnsubscribedCount,
      )} de [${dSelectedCount}] sélectionné${ending(
        dSelectedCount,
      )} ${deliveries(dSelectedCount)} ?`,
    unsubscribeConfirmMessage: (dSelectedCount: number) =>
      `Vous êtes déjà abonné à tous les [${dSelectedCount}] ${deliveries(
        dSelectedCount,
      )} sélectionné${ending(
        dSelectedCount,
      )}.\\n\\n Peut-être voulez-vous vous désabonner ?`,
  },
  logisticsListDescriptions: {
    subscribeConfirmMassage: (
      dUnsubscribedCount: number,
      dSelectedCount: number,
    ) =>
      `S'abonner à [${dUnsubscribedCount}] désabonné${ending(
        dUnsubscribedCount,
      )} ${workflows(
        dUnsubscribedCount,
      )} de [${dSelectedCount}] sélectionné${ending(
        dSelectedCount,
      )} ${workflows(dSelectedCount)} ?`,
    unsubscribeConfirmMessage: (dSelectedCount: number) =>
      `Vous êtes déjà abonné à tous les [${dSelectedCount}] ${workflows(
        dSelectedCount,
      )} sélectionné${ending(
        dSelectedCount,
      )}.\\n\\n Peut-être voulez-vous vous désabonner ?`,
    areYouWantToDelete:
      'Êtes-vous sûr de vouloir supprimer tous ces workflows sélectionnés?',
    deletionConfirmMsg: (selectedCount: number) =>
      `Vous avez sélectionné - ${selectedCount} ${workflows(selectedCount)}`,
  },
  sitemapsDataIsLoading:
    'Les données des plans de site sont en cours de chargement',
  editingConfirmation: "Confirmation de l'édition",
  selectDataColumn: 'Sélectionner la colonne de données',
  addProperty: 'Ajouter une propriété',
  addFieldForEditing: "Ajouter un champ pour l'édition",
  deliveryBulkEditorDescriptions: {
    warningMessagePart1:
      'Aucune des livraisons sélectionnées ne peut être modifiée selon vos permissions de compte',
    warningMessagePart2:
      "Faites une autre sélection ou contactez l'administrateur",
  },
  failedToEdit: "Échec de l'édition",
  editedXFieldsOnYDeliveries: (x: number, y: number) =>
    `Édité ${x} Champ${ending(x)} sur ${y} ${deliveries(y)}`,
  editXDeliveries_question: (x: number) => `Éditer ${x} ${deliveries(x)}?`,
  onlyXOfYSelectedDeliveriesCanBeChanged: (x: number, y: number) =>
    `Seulement [${x}] de [${y}] ${deliveries(x)} sélectionné${ending(
      x,
    )} peut être modifié, procéder ?`,
  editXFieldsOnYDeliveries: (x: number, y: number) =>
    `Éditer ${x} Champ${ending(x)} sur ${y} ${deliveries(y)}`,
  updateXDeliveries: (x: number) => `Mettre à jour ${x} ${deliveries(x)}`,
  failedToUpdate: 'Échec de la mise à jour',
  signedOff: 'Validé',
  signedOffFor: (companyName: string) => `Validé au nom de ${companyName}`,
  statusUpdated: 'Statut mis à jour',
  statusUpdatedOnXDeliveries: (x: number) =>
    `Statut mis à jour sur ${x} ${deliveries(x)}`,
  updateDeliveryStatus: 'Mettre à jour le statut de la livraison',
  loadingDeliveriesData: 'Chargement des données de livraison',
  loadingFormsData: 'Chargement des données des Workflows',
  form: 'Formulaire',
  log: 'Journal',
  utilization: 'Utilisation',
  controls: 'Contrôles',
  resourceProperties: 'Propriétés de la ressource',
  resourceControls: 'Contrôles de la ressource',
  mapProperties: 'Propriétés de la carte',
  sessions: 'Sessions',
  properties: 'Propriétés',
  map: 'Carte',
  deleteResourceQuestion: 'Voulez-vous vraiment supprimer cette ressource ?',
  weekOfEndDate: 'Semaine de la date de fin',
  companySummary: "Résumé de l'entreprise",
  percentDone: '% Fait',
  percentOnTime: "% À l'heure",
  trucks: 'Camions',
  avgTrucksPerDelivery: 'Moy. camions / Livraison',
  deliveryGantt: 'Gantt de livraison',
  projectSummary: 'Résumé du projet',
  completionRatePercent: 'Taux de complétion (%)',
  completionRate: 'Taux de complétion',
  onTimeRatePercent: "Taux à l'heure (%)",
  onTimeRate: "Taux à l'heure",
  unplanned: 'Non planifié',
  unplannedRate: 'Taux non planifié',
  onTimeStart: "Début à l'heure",
  lateOnTime: "Tard/À l'heure",
  allDay: 'Toute la journée',
  weekly: 'Hebdomadaire',
  monthly: 'Mensuel',
  onTimeLate: "À l'heure/En retard",
  earnedDuration: 'Durée acquise',
  show_verb: 'Montrer',
  showXMore: (x: number) => `Afficher encore ${x}`,
  showActive: 'Montrer actif',
  loadingProjectData: 'Chargement des données du projet',
  updateToday: "Mettre à jour aujourd'hui",
  critical: 'Critique',
  deltaDays: '∆ Jours',
  sumSlip: '∑ Glissement',
  openIssues: 'Problèmes ouverts',
  plannedToday: "Planifié aujourd'hui",
  actualToday: "Réel aujourd'hui",
  deltaManpowerToday: "∆ Main-d'œuvre aujourd'hui",
  deltaManpowerTrend: "Tendance ∆ Main-d'œuvre",
  soon: 'Bientôt',
  numberOfPicks: 'Nombre de choix',
  repeatDelivery: 'Répéter la livraison',
  every_plural: 'Chaque',
  endsOn: 'Se termine le',
  thisDelivery: 'Cette livraison',
  thisAndFutureDeliveries: 'Cette livraison et les suivantes',
  allDeliveries: 'Toutes les livraisons',
  everyXDays: (x: number) => `Tous les ${x} jour${ending(x)}`,
  everyXWeeks: (x: number) => `Toutes les ${x} semaine${ending(x)}`,
  everyXMonths: (x: number) => `Tous les ${x} mois${ending(x)}`,
  xDays: (x: number) => `jour${ending(x)}`,
  xWeeks: (x: number) => `semaine${ending(x)}`,
  xMonths: (x: number) => `mois${ending(x)}`,
  xMaterial: (x: number) => `Matériel #${x}`,
  addMaterial: 'Ajouter du matériel',
  forecast: 'Prévision',
  unknown: 'Inconnu',
  multipleEntries: {
    add_x: (x: string) => `Ajouter ${x}`,
    paste: 'Vous pouvez coller plusieurs entrées',
    typeOrPaste: 'Vous pouvez taper ou coller plusieurs entrées',
    repeatedValue: 'Entrée dupliquée',
  },
  fileType: 'Type de fichier',
  fileExtension: 'Extension de fichier',
  workflowType: 'Type de Workflow',
  workflowTypeLabel: 'Type de Workflow',
  workflowName: 'Nom du Workflow',
  workflowCategory: 'Catégorie de Workflow',
  workflowId: 'ID de Workflow',
  fileCategory: 'Catégorie de fichier',
  uploadedBy: 'Téléversé par',
  uploader: 'Téléverseur',
  source: 'Source',
  sort: 'Trier',
  sortBy: 'Trier par',
  sortDirection: 'Sens du tri',
  setDefaults: 'Définir les valeurs par défaut',
  ascending: 'Croissant',
  descending: 'Décroissant',
  failedToVerifyUploadedFile: 'Échec de la vérification du téléversement',
  searchCompanyOrCompanyType:
    "Rechercher une entreprise ou un type d'entreprise",
  newActivity: 'Nouvelle activité',
  monitoringName: 'Nom du suivi',
  deviceId: "ID de l'appareil",
  device: 'Appareil',
  lastMaturity: 'Maturité dernière',
  lastReading: 'Dernière lecture',
  elapsedTime: 'Temps écoulé (hh:mm)',
  maxElapsedTime: 'Max elapsed time (min)',
  lastTemperature: (temperatureUnit: MaturixTemperatureUnit) =>
    `Dernière température (${FrenchTree.temperatureUnit(temperatureUnit)})`,
  lastStrength: (strengthUnit: MaturixStrengthUnit) =>
    `Dernière résistance (${FrenchTree.strengthUnit(strengthUnit)})`,
  temperatureWithUnit: (temperatureUnit: MaturixTemperatureUnit) =>
    `Température (${FrenchTree.temperatureUnit(temperatureUnit)})`,
  strengthWithUnit: (strengthUnit: MaturixStrengthUnit) =>
    `Résistance (${FrenchTree.strengthUnit(strengthUnit)})`,
  temperatureUnit: (temperatureUnit: MaturixTemperatureUnit): string => {
    switch (temperatureUnit) {
      case MaturixTemperatureUnit.CELCIUS:
        return 'Celsius'
      case MaturixTemperatureUnit.FAHRENHEIT:
        return 'Fahrenheit'
    }
  },
  strengthUnit: (strengthUnit: MaturixStrengthUnit): string => {
    switch (strengthUnit) {
      case MaturixStrengthUnit.KGPERCMSQ:
        return 'kg/cm²'
      case MaturixStrengthUnit.MPA:
        return 'MPa'
      case MaturixStrengthUnit.NPERMMSQ:
        return 'N/mm²'
      case MaturixStrengthUnit.PSI:
        return 'PSI'
    }
  },
  unconfirmed: 'Non confirmé',
  confirmed: 'Confirmé',
  delivering: 'En cours de livraison',
  paused: 'En pause',
  onHold: 'En attente',
  ticketed: 'Billeté',
  recalled: 'Rappelé',
  enRoute: 'En route',
  pouring: 'Coulage',
  delivered: 'Livré',
  orderSummary: 'Résumé de la commande',
  payloadSummary: 'Résumé de la charge utile',
  orderStatus: 'Statut de la commande',
  totalVolume: 'Volume total',
  deliveryLocation: 'Lieu de livraison',
  mixType: 'Type de mélange',
  slump: 'Affaissement',
  spacingOrUnloadTime: 'Espacement ou temps de déchargement',
  pouringMode: 'Mode de coulage',
  elementsToPour: 'Éléments à couler',
  extraCharge: 'Supplément',
  orderedBy: 'Commandé par',
  load: 'Chargement',
  total: 'Total',
  timeOnSite: 'Temps sur site',
  ticket: 'Billet',
  volumeLoaded: 'Volume chargé',
  totalOrderVolume: 'Volume total de la commande',
  additive: 'Additif',
  jobTimes: 'Horaires de travail',
  startLoad: 'Commencer le chargement',
  beginPouring: 'Commencer le coulage',
  washing: 'Lavage',
  totalTimeOnSite: 'Temps total sur site',
  inXHoursAndXMin: (hours: number, minutes: number) =>
    `Dans ${hours}h ${minutes}min`,
  xHoursAndXMinLate: (hours: number, minutes: number) =>
    `${hours}h ${minutes}min en retard`,
  inXMin: (minutes: number) => `Dans ${minutes} min`,
  xMinLate: (minutes: number) => `${minutes} min en retard`,
  isAboutToArrive: "Est sur le point d'arriver",
  projected_time: 'Projeté',
  recurringDelivery: 'Livraison récurrente',
  recurringWorkflow: 'Récurrent',
  endingXDate: (date: string) => `Se termine le ${date}`,
  isQuestionMandatory: 'La question est-elle obligatoire?',
  remainingDuration: 'Durée restante',
  baseUrl: 'URL de base',
  siteId: 'ID du site',
  isActive: 'Est actif',
  syncStartDate: 'Date de début de synchronisation',
  notUniqueActivityCodeMessage: (code: string) =>
    `L'activité avec le code [${code}] existe déjà. Essayez une autre valeur`,
  activityWasUpdated: "L'activité suivante a été mise à jour",
  activityUpdated: 'Activité mise à jour',
  editXFieldsOnYActivities: (x: number, y: number) =>
    `Éditer ${x} Champ${ending(x)} sur ${y} ${activities(y)}`,
  editedXFieldsOnYActivities: (x: number, y: number) =>
    `Édité ${x} Champ${ending(x)} sur ${y} ${activities(y)}`,
  editXActivities_question: (x: number) => `Éditer ${x} ${activities(x)}?`,
  onlyXOfYSelectedActivitiesCanBeChanged: (x: number, y: number) =>
    `Seulement [${x}] de [${y}] ${activities(x)} sélectionné${ending(
      x,
    )} peut être modifié, procéder ?`,
  p6Viewer: 'Visionneuse P6',
  scanner: 'Scanner',
  scannerApp: 'Appli Scanner',
  selectScanner: 'Sélectionner le scanner',
  createNewScanner: 'Créer un nouveau scanner',
  activitySetName: "Nom de l'ensemble d'activités",
  editActivitySet: "Modifier un ensemble d'activités",
  includeXFile: (ext: string) => `Inclure un fichier .${ext}`,
  includeDetailedEachForm: 'Inclure une vue détaillée de chaque Workflow',
  usersSubscribedTo: {
    deliveries: (uCount: number, iCount: number) =>
      `Abonné ${uCount} ${users(uCount)} à ${iCount} ${deliveries(iCount)}`,
    activities: (uCount: number, iCount: number) =>
      `Abonné ${uCount} ${users(uCount)} à ${iCount} ${activities(iCount)}`,
    forms: (uCount: number, iCount: number) =>
      `Abonné ${uCount} ${users(uCount)} à ${iCount} ${workflows(iCount)}`,
  },
  usersUnSubscribedFrom: {
    deliveries: (uCount: number, iCount: number) =>
      `Désabonné ${uCount} ${users(uCount)} à ${iCount} ${deliveries(iCount)}`,
    activities: (uCount: number, iCount: number) =>
      `Désabonné ${uCount} ${users(uCount)} à ${iCount} ${activities(iCount)}`,
    forms: (uCount: number, iCount: number) =>
      `Désabonné ${uCount} ${users(uCount)} à ${iCount} ${workflows(iCount)}`,
  },
  scannerName: 'Nom du scanner',
  scanMaster: 'Maître du scanner',
  scanStationReport: 'Rapport de la station de scanner',
  materialTrackingReport: 'Rapport de suivi des matériaux',
  truckSize: 'Taille du camion',
  cancelationReason: "Raison de l'annulation",
  responsibleCompanies: 'Entreprises responsables',
  noDataAvailable: 'Aucune donnée disponible',
  last7Days: 'Les 7 derniers jours',
  last30Days: 'Les 30 derniers jours',
  last120Days: 'Les 120 derniers jours',
  last365Days: 'Les 365 derniers jours',
  entireProject: 'Projet complet',
  frequencyAndPeriod: 'Fréquence et période',
  activityDataLinking: {
    disableLinkingQuestion: (field: string) =>
      `Le lien avec le champ externe '${field}' sera désactivé. Voulez-vous continuer ?`,
    enableLinkingQuestion: (fieldValue: string, externalFieldValue: string) =>
      `Voulez-vous lier '${fieldValue}' à l'externe '${externalFieldValue}' ? Cela peut affecter d'autres activités.`,
    linkingIsDisabled: 'Le lien est désactivé',
    linkingIsEnabled: 'Le lien est activé',
    linkingDisabled: `Lien désactivé`,
    linkingEnabled: `Lien activé`,
    editWarning: `Les modifications de lien peuvent affecter d'autres activités`,
    bulkEditWarning: `Le lien sera désactivé pour les champs modifiables`,
  },
  materialsUpload: 'Téléversement des matériaux',
  plannedQuantity: 'Quantité planifiée',
  materialsUploading: {
    uploadMaterials: 'Téléverser des matériaux',
    goToTheMaterialsPage: 'Aller à la page des matériaux',
    uploadYourMaterialsFile:
      'Téléversez votre fichier de matériaux au format Excel (.xlsx, .xlsm).',
    thereAreNoSheetsError:
      "Il n'y a pas de feuilles dans le fichier Excel fourni ou une erreur s'est produite lors de la lecture du fichier",
    materialCategoryLvl1: 'Catégorie de matériel (Niveau 1)',
    materialLvl2: 'Matériel (Niveau 2)',
    materialDescription: 'Description du matériel',
    materialPlannedQuantity: 'Quantité de matériel planifiée',
    startingTheUpload: 'Début du téléversement',
    readingMaterialsFile: 'Lecture du fichier de matériaux',
    extractingMaterialsData: 'Extraction des données des matériaux',
    removingOldData: 'Suppression des anciennes données',
    savingMaterialsData: 'Sauvegarde des données des matériaux',
    finishingUpload: 'Fin du téléversement',
    uploadOfMaterialsFailedAt: 'Échec du téléversement des matériaux à',
    uploadWasSuccessful: 'Le téléversement a été réussi',
    pleaseSelectSheetToUpload: (isMultiple: boolean) =>
      `${
        isMultiple ? 'Le fichier actuel contient plusieurs feuilles. ' : ''
      }Veuillez sélectionner une feuille à importer.`,
    makeSureLocationNamesUnique:
      'Assurez-vous que les noms des emplacements sont uniques dans StruxHub',
  },
  materialsUploadHistory: {
    currentMaterials: 'Matériaux actuels',
    projectUsesDefaultMaterials: 'Le projet utilise les matériaux par défaut',
    latestUpload: 'Dernier téléversement',
    materialsRestoringConfirmation:
      'Confirmation de la restauration des matériaux',
    totalMaterialCategoriesAndMaterials:
      'Total des catégories de matériaux et des matériaux',
    restoreDefaultMaterials: 'Restaurer les matériaux par défaut',
    doYouConfirmRestoreToDefaults:
      'Confirmez-vous la restauration aux valeurs par défaut ?',
    restoredSuccessfully: 'Restauré avec succès',
    failedToRestore: 'Échec de la restauration',
  },
  materialsUploadConfirm: {
    confirmDataMerge: 'Confirmer la fusion des données',
    confirmDescription1:
      'Téléverser ce fichier mettra à jour toutes les valeurs modifiées pour les matériaux existants et créera de nouveaux enregistrements pour les nouveaux matériaux.',
    confirmDescription2:
      'Si vous souhaitez supprimer toutes les données des matériaux existantes et les remplacer par les données du fichier de téléversement,',
    confirmDescription3: 'cliquez ici',
    replaceDescription:
      'Voulez-vous vraiment supprimer toutes les données des matériaux existantes et les remplacer par les données du fichier de téléversement ?',
  },
  collapseAll: 'Réduire tout',
  expandAll: 'Étendre tout',
  expandMenu: 'Étendre le menu',
  expandPanel: 'Étendre le panneau',
  collapseMenu: 'Réduire le menu',

  myUserProfile: 'Mon profil utilisateur',
  addTeammates: 'Ajouter des coéquipiers',
  addSummaryColumn: 'Ajouter une colonne de résumé',
  configureColumns: 'Configurer les colonnes',
  summaryColumns: 'Colonnes de résumé',
  receipt: 'Reçu',
  summary: 'Résumé',
  remainingQuantity: 'Quantité restante',
  materialsInDeliveryFormWarning: {
    onlyXMaterialsForForm: (x: number) =>
      `Actuellement seulement ${x} matériau${ending(x, '', 'x')} ${ending(
        x,
        'est',
        'sont',
      )} activé${ending(
        x,
        '',
        's',
      )} pour un formulaire de livraison. Si cela représente une limitation, veuillez contacter`,
    andWeCanTalk: 'et nous pourrons discuter',
  },
  deliverySuccessfullyCreated: 'Livraison créée avec succès',
  xDates,
  materialDescription: 'Description du matériau',
  thisGroupAlreadyExists: 'Ce groupe existe déjà',
  upload: 'Téléverser',
  totalBooked: 'Total réservé',
  totalDelivered: 'Total livré',
  doneDeliveries: 'Livraisons terminées',
  xVendors: (x: number) => `${x} Fournisseur${ending(x)}`,
  thisWeek: 'Cette semaine',
  thisMonth: 'Ce mois-ci',
  youCannotChangeDelivery:
    "Vous ne pouvez pas modifier une livraison tant qu'elle est dans cet état",
  plannedInstallLocation: "Emplacement d'installation prévu",
  plannedDeliveryLocation: 'Emplacement de livraison prévu',
  currentLocation: 'Emplacement actuel',
  procurementID: "ID d'approvisionnement",
  id_short: 'ID',
  readyToInstallQuantity: 'Quantité prête à installer',
  totalOnSiteQuantity: 'Quantité totale sur site',
  toMoveQuantity: 'Quantité à déplacer',
  preciseLocation: 'Emplacement précis',
  editPreciseLocation: "Modifier l'emplacement précis",
  preciseLocationPreviewTooltip:
    "Cet aperçu est utilisé dans le rapport PDF. Ajustez le niveau de zoom pour contrôler l'apparence de la carte.",
  zoomOverviewReportTooltip:
    "Ce niveau de zoom est également utilisé comme image d'Aperçu dans les rapports PDF pour les Emplacements de Flux de Travail.",
  thisLocationIsNotGeolocated: "Cet emplacement n'est pas géolocalisé",
  notGeolocated: 'Non géolocalisé',
  inheritsFrom: 'Hérité de',
  defaultsTo: 'Par défaut sur',
  setPreciseLocation: "Définir l'emplacement précis",
  movePinPosition: "Déplacer la position de l'épingle",
  markupTools: "Outils d'annotation",
  bold: 'Gras',
  italic: 'Italique',
  underline: 'Souligné',
  fill: 'Remplissage',
  border: 'Bordure',
  thickness: 'Épaisseur',
  shortcuts: 'Raccourcis',
  gestures: 'Gestes',
  gesturePointsGroup: 'Points (ligne / polygone)',
  gestureTextGroup: 'Texte',
  gestureMapGroup: 'Carte',
  gestureSelectMarkup: 'Sélectionner une annotation',
  gestureEditPoints: 'Modifier les points (ligne / polygone)',
  gestureDeselect: 'Désélectionner',
  gestureMovePoint: 'Déplacer un point',
  gestureAddPoint: 'Ajouter un point',
  gestureRemovePoint: 'Supprimer un point',
  gestureClosePolygon: 'Fermer en polygone',
  gestureRectangleCircle: 'Rectangle / cercle',
  gestureLineArrow: 'Ligne / flèche',
  gesturePolygonPolyline: 'Polygone / polyligne',
  gesturePenHighlighterEraser: 'Stylo / surligneur / gomme',
  gestureAddText: 'Ajouter du texte',
  gestureEditText: 'Modifier le texte',
  gestureMoveResizeRotate: 'Déplacer / redimensionner / pivoter',
  gestureRepositionPin: 'Repositionner le marqueur',
  gesturePan: 'Déplacer la vue',
  gestureZoom: 'Zoom',
  gestureRotateTilt: 'Pivoter et incliner',
  gestureTap: 'Toucher',
  gestureTapAgain: 'Toucher à nouveau',
  gestureTapMap: 'Toucher la carte',
  gestureDrag: 'Glisser',
  gestureTapMidpoint: 'Toucher un point milieu',
  gestureLongPress: 'Appui long',
  gestureTapFirstPoint: 'Toucher le premier point',
  gestureTapOrDrag: 'Toucher ou glisser',
  gestureDragOrTapEnds: 'Glisser, ou toucher le début puis la fin',
  gestureTapPointsClose: 'Toucher des points ; toucher le premier pour fermer',
  gestureDoubleTap: 'Toucher deux fois',
  gestureDragBodyCornerPin: 'Glisser le corps / coin / marqueur',
  gestureTapMoveThenDrag: 'Toucher Déplacer, puis glisser la carte',
  gesturePinch: 'Pincer',
  gestureTwoFingerDrag: 'Glisser à deux doigts',
  customColor: 'Couleur personnalisée',
  strokeWidth: 'Épaisseur du trait',
  strokeWidthSlider: "Curseur d'épaisseur du trait",
  borderWidth: 'Épaisseur de la bordure',
  borderWidthSlider: "Curseur d'épaisseur de la bordure",
  backgroundColor: "Couleur d'arrière-plan",
  textColor: 'Couleur du texte',
  textSize: 'Taille du texte',
  fontSize: 'Taille de la police',
  moveToolbar: "Déplacer la barre d'outils",
  move: 'Déplacer',
  savePreciseLocationPosition:
    "Enregistrer la position de l'emplacement précis ?",
  dontSave: 'Ne pas enregistrer',
  dragTheMapToChangeLocationPinPosition:
    "Faites glisser la carte pour modifier la position de l'épingle",
  addMarkups: 'Ajouter des annotations',
  markUpPhoto: 'Annoter la photo',
  photoMarkupLoadFailed: 'Impossible de charger cette photo',
  untitledWorkflow: 'Flux de travail sans titre',
  fromDrawing: 'À partir du dessin',
  continueWorkflow: 'Continuer le flux de travail…',
  collapse: 'Réduire',
  expand: 'Développer',
  createWorkflow: 'Créer un flux de travail',
  holdToCreateWorkflow: 'Créer un flux de travail',
  holdToCreateWorkflowHint:
    'Appuyez longuement n’importe où sur la carte pour placer une épingle et démarrer un flux de travail.',
  gotIt: 'Compris',
  enterWorkflowTitle: 'Saisir un titre',
  discard: 'Abandonner',
  discardWorkflowDraft: 'Abandonner le brouillon de flux de travail ?',
  discardWorkflowDraftQuestion:
    "Vous perdrez l'épingle, la saisie du formulaire et toute annotation dessinée.",
  workflowTitleSubmitted: (name: string) => `${name} soumis`,
  userStamp: "Tampon d'utilisateur",
  userStampTooltip:
    "Inclut le nom de l'utilisateur signataire avec la signature.",
  inventoryTracking: 'Champs de suivi',
  inventoryTrackingTooltip:
    "Lorsqu'il est activé, ajoute des champs de quantité et d'emplacement pour suivre l'inventaire des ressources matérielles.",
  moveToMyLocation: 'Aller à ma position',
  noCoordinatesAvailable: 'Aucune coordonnée disponible',
  geolocationPermissionDenied:
    "Permission de géolocalisation refusée. Veuillez activer l'accès à la localisation dans les paramètres de votre navigateur.",
  geolocationPositionUnavailable:
    'Les informations de localisation ne sont pas disponibles. Veuillez vérifier les paramètres de localisation de votre appareil.',
  geolocationTimeout:
    'La demande de localisation a expiré. Veuillez réessayer.',
  geolocationError:
    "Une erreur s'est produite lors de l'obtention de votre localisation. Veuillez réessayer.",
  dismiss: 'Fermer',
  pictograms: {
    names: {
      building: 'Bâtiment',
      level: 'Niveau',
      zone: 'Zone',
      area: 'Secteur',
      route: 'Itinéraire',
      door: 'Porte',
      gate: 'Portail',
      shaft: 'Gaine',
      'loading-zone': 'Zone de chargement',
      'restrooms-men': 'Toilettes hommes',
      entrance: 'Entrée',
      exit: 'Sortie',
      'access-a': 'Accès A',
      'access-b': 'Accès B',
      'access-c': 'Accès C',
      'access-d': 'Accès D',
      'access-e': 'Accès E',
      'access-f': 'Accès F',
      'no-access-1': 'Accès interdit 1',
      'no-access-2': 'Accès interdit 2',
      'no-access-3': 'Accès interdit 3',
      'pedestrian-walkway': 'Passage piéton',
      elevator: 'Ascenseur',
      stairs: 'Escalier',
      parking: 'Parking',
      'bike-parking': 'Parking vélos',
      'buggy-parking': 'Parking voiturettes',
      'hi-vis-apparel': 'Vêtements de signalisation haute visibilité',
      'safety-helmet': 'Casque de sécurité',
      'safety-gloves': 'Gants de sécurité',
      'safety-boots': 'Chaussures de sécurité',
      'not-required-all': 'Non obligatoire : tous',
      'not-required-apparel': 'Non obligatoire : vêtements',
      'not-required-helmet': 'Non obligatoire : casque',
      'not-required-gloves': 'Non obligatoire : gants',
      'not-required-boots': 'Non obligatoire : chaussures',
      'visitor-check-in': 'Accueil des visiteurs',
      'admin-office': 'Bureau administratif',
      'electrical-room': 'Local électrique',
      restrooms: 'Toilettes',
      'restrooms-women': 'Toilettes femmes',
      'break-area': 'Zone de pause',
      'hydration-station': "Point d'eau potable",
      'smoking-area': 'Zone fumeurs',
      canteen: 'Cantine',
      break: 'Pause',
      'wash-area': 'Zone de lavage',
      dumpster: 'Benne',
      'brand-oes': 'OES Equipment',
      'brand-sunbelt': 'Sunbelt Rentals',
      'brand-united-rentals': 'United Rentals',
      'brand-equipmentshare': 'EquipmentShare',
      'brand-feco': 'First Equipment Company',
      deliveries: 'Livraisons',
      'construction-vehicles': 'Engins de chantier',
      trailer: 'Remorque',
      'security-checkpoint': 'Poste de contrôle de sécurité',
      'temporary-water': 'Eau temporaire',
      'equipment-storage': "Stockage d'équipements",
      'material-boxes': 'Stockage de matériaux : cartons',
      'material-wood': 'Stockage de matériaux : bois',
      'material-steel-1': 'Stockage de matériaux : acier 1',
      'material-plumbing': 'Stockage de matériaux : plomberie',
      'material-steel-2': 'Stockage de matériaux : acier 2',
      'construction-debris-1': 'Déchets de chantier 1',
      'construction-debris-2': 'Déchets de chantier 2',
      'information-area': "Point d'information",
      'site-map': 'Plan du site',
      'first-aid': 'Premiers secours',
      aed: 'Défibrillateur automatisé externe',
      'covid-checkpoint': 'Contrôle sanitaire',
      'fire-extinguisher': 'Extincteur',
      'muster-point': 'Point de rassemblement',
      'exit-running': 'Sortie (personnage qui court)',
      cleanroom: 'Salle blanche / habillage',
      'icra-zone': 'Zone ICRA',
      'negative-pressure': 'Pression négative',
      'esd-zone': 'Zone ESD',
      'no-photography': 'Photos interdites',
      'no-drones': 'Drones interdits',
      'fall-hazard': 'Risque de chute / bord ouvert',
      'silica-dust': 'Silice / poussière',
      noise: 'Bruit',
      'overhead-load': 'Charge suspendue / rayon de grue',
      excavation: 'Fouille',
      'hot-work': 'Travaux par point chaud',
      'confined-space': 'Espace confiné',
      'arc-flash': 'Haute tension / arc électrique',
      stop: 'Stop',
      yield: 'Cédez le passage',
      'speed-limit': 'Limitation de vitesse',
      'one-way': 'Sens unique',
      flagger: 'Signaleur',
      'truck-crossing': 'Passage de camions',
      'fire-lane': 'Voie pompiers — laisser libre',
      'concrete-washout': 'Bac de lavage béton',
      'storm-drain': 'Protection des avaloirs',
      'erosion-control': "Contrôle de l'érosion",
      'wheel-wash': 'Lave-roues',
      'spill-kit': 'Kit antipollution',
      scaffold: 'Échafaudage',
      'swing-stage': 'Nacelle suspendue',
      'mast-climber': 'Plateforme sur mât',
      eyewash: 'Douche oculaire / de sécurité',
      standpipe: 'Colonne sèche / tuyau',
      'fire-hydrant': 'Poteau incendie',
      'alarm-pull-station': "Déclencheur manuel d'alarme",
      'crane-zone': 'Zone de grue / levage',
      'brand-herc-rentals': 'Herc Rentals',
    },
  },
  iconPicker: {
    library: 'Bibliothèque',
    companyLogos: "Logos d'entreprises",
    selectIcon: 'Sélectionner une icône',
    xIcons: (x: number) => `${x} Icône${ending(x)}`,
    noResults: 'Aucune icône trouvée',
    libraryIconColor:
      "La couleur s'applique aux libellés, aux étiquettes et aux calendriers, mais pas à l'icône, qui a ses propres couleurs",
  },
  formMaterialSubFields: {
    materialHasQuantityAndLocation:
      'Le matériel a des champs Quantité et Emplacement que vous pouvez également ajouter/modifier ci-dessous.',
    clickToEnableQuantity: 'Cliquez ici si vous souhaitez activer la quantité.',
    clickToEnableLocation:
      "Cliquez ici si vous souhaitez activer l'emplacement.",
  },
  newFormType: 'Nouveau type de Workflow',
  materialTransferId: 'ID de transfert de matériel',
  materialTransferDate: 'Date de transfert de matériel',
  newTransfer: 'Nouveau transfert',
  doneTransfers: 'Transferts terminés',
  totalTransferred: 'Total transféré',
  xTransfers: (x: number) => `${x} Transfert${ending(x)}`,
  youCanSelectMultipleOptions: 'Vous pouvez sélectionner plusieurs options',

  updateStatus: "Mettre à jour l'État",

  review_verb: 'Réviser',
  reviewed: 'Révisé',
  activated: 'Activé',
  submitted: 'Soumis',
  workflowIsDoneOrDenied: (isDenied: boolean) =>
    `Le Workflow est ${isDenied ? 'refusé' : 'terminé'}`,
  workflowIsEnded: 'Le Workflow est terminé',
  request_noun: 'Demande',
  review_noun: 'Revoir',
  recurringInspection: 'Inspection récurrente',
  assignAsBicWhenWorkflowMatches:
    'Assigner comme Ball-in-Court lorsque le Workflow correspond à',
  thisWorkflowStepIsAssignedByRules:
    'Cette étape du Workflow est assignée selon les règles suivantes',
  adminsCanAdvanceWorkflow:
    'Les administrateurs peuvent faire avancer le Workflow indépendamment des utilisateurs assignés.',
  completedBy: 'Complété par',
  anyAssignedUser: "N'importe quel utilisateur assigné",
  allAssignedUsers: 'Tous les utilisateurs assignés',
  allAssignedCompanies: 'Toutes les entreprises assignées',
  allAssignedUsersAndCompanies: 'Tous les utilisateurs/entreprises assignés',
  oneUserPerRule: 'Un utilisateur par règle',
  anyAssignedUserDescription:
    'Le premier utilisateur assigné qui complète cette étape fait avancer le Workflow.',
  allAssignedUsersDescription:
    "Chaque utilisateur assigné, et une personne de chaque entreprise assignée, doit compléter cette étape avant que le Workflow n'avance.",
  oneUserPerRuleDescription:
    "Un utilisateur de chaque règle doit compléter cette étape avant que le Workflow n'avance.",
  assignees: 'Personnes assignées',
  optionalInParentheses: '(facultatif)',
  workflowCompany: 'Entreprise du Workflow',
  completeForWhichRule: 'Compléter pour quelle règle ?',
  countsTowardOneRule:
    "Vous êtes assigné par plus d'une règle à cette étape. Votre complétion compte pour l'une d'entre elles.",
  ruleName: 'Nom de la règle',
  allUsersMustCompleteDisabledTooltip:
    'Ajoutez une règle utilisateur BIC pour activer cette option.',
  xOfYComplete: (x: number, y: number) => `${x} sur ${y} terminé${ending(x)}`,
  noFieldsSubmitted: 'Aucun champ soumis',
  submittedAt: 'Soumis le',
  xRules: (x: number) => `${x} règle${ending(x)}`,
  step: 'Étape',
  stepCompletion: "Achèvement de l'étape",
  bicUser: 'BIC utilisateur',
  formSection: 'Section du Workflow',
  actions: 'Actions',
  frequency: 'Fréquence',
  deadline: 'Date limite',
  automaticStart: 'Démarrage automatique',
  addStep: 'Ajouter une étape',

  workflowConfDescr: {
    mustContainApproval: `Le Workflow avec une étape de demande doit contenir une étape d'approbation`,
    cannotBeTheLastStep: (stepName: string) =>
      `${stepName} ne peut pas être la dernière étape, ajoutez-en une autre ou supprimez celle-ci.`,
    workflowStartsOnDate:
      'Le Workflow démarre automatiquement à la date de début.',
    automaticallyStart:
      'Démarrer automatiquement le workflow à la date de début',
    replaceWithStep: (stepType: string): string =>
      `Remplacez par l'étape ${stepType}`,
    replaceWithStart: `Remplacez par l'étape de démarrage`,
    deleteWorkflowStep: 'Supprimez l’étape du Workflow',
    removeStepDialogMsg: 'Voulez-vous supprimer cette étape du Workflow?',
    toastUnsavedMsg:
      'Les modifications que vous avez apportées ont été réinitialisées',
    recurringHasConstr:
      'L’étape d’inspection récurrente comporte des contraintes:',
    toggleDialogMsg: (isManualStart: boolean): string =>
      `Le remplacer par une étape de démarrage ${
        isManualStart ? 'Manuel' : 'Automatique'
      }?`,
    selectStepMsg:
      'Sélectionnez une étape du Workflow pour ajouter/modifier des champs',
  },
  workflowConfTooltips: {
    bicBlockFirstLine: `Affecter un ou plusieurs utilisateurs du Workflow en tant que BIC pour cette étape. Les utilisateurs BIC sont responsables de la complétion de l'étape avant que le Workflow ne puisse avancer.`,
    bicBlockSecondLine: `Les règles peuvent être utilisées pour affecter des utilisateurs dynamiquement en fonction des champs StruxHub, tels que l'emplacement, l'équipement ou l'entreprise.`,
  },
  xWorkflowsShowing: (x: number) => `${x} ${workflows(x)} affichés`,
  workflowTableText: {
    seeDescr: 'Voir les descriptions des étapes',
    stepCategory: `Catégorie d'étape du Workflow`,
    workflowStep: 'Étape du Workflow',
    constraints: 'Contraintes',
    bicText: `Pouvez-vous préciser l'utilisateur du ballon sur le terrain?`,
    multipleText: 'Pouvez-vous avoir plusieurs instances dans le Workflow?',
    submissionDescr: `Étape de lancement du Workflow pour soumettre
      un formulaire qui ne nécessite pas d'approbation ultérieure.`,
    submissionConstr: 'Peut uniquement lancer un Workflow.',
    requestDescr:
      'Lancement de l’étape du Workflow pour demander une approbation.',
    requestConstr:
      'Peut uniquement lancer un Workflow. Doit avoir une étape d’approbation ultérieure.',
    reviewDescr:
      'Étape d’avancement générale d’un Workflow pouvant être utilisé pour les pré-approbations.',
    approvalDescr: `Approbation d'une demande de Workflow pouvant
      être attribuée à un ou plusieurs utilisateurs spécifiques.`,
    approvalConstr: 'Doit avoir une étape de demande précédente.',
    startDescr:
      'Démarrage d’un Workflow initié en cliquant sur un bouton «Démarrer».',
    autoStartDescr: `Début d'un Workflow lancé automatiquement à la date de début du Workflow.`,
    onSiteDescr: `Utilisé pour indiquer l'arrivée sur place d'un véhicule de livraison.`,
    readyToInspectDescr:
      'Étape intermédiaire du Workflow utilisée pour informer les inspecteurs.',
    inspectionDescr: 'Étape d’inspection générale.',
    recurringDescr: 'Inspections récurrentes à une fréquence spécifiée.',
    recurringConstr:
      'Doit être directement précédé d’une étape de démarrage ou de démarrage automatique.',
    finishDescr: `Achèvement d'un Workflow marqué en cliquant sur un bouton «Terminé».`,
  },
  workflowSteps: 'Étapes du Workflow',
  workflowDiagram: 'Diagramme de Workflow',
  workflowStepConfigurator: `Configurateur d'étape de Workflow`,
  configurator: 'Configurateur',
  restoreToLastSavedState: 'Restaurer au dernier état enregistré',
  endOfTheDay: 'Fin de la journée',
  everyX_withoutNum: (x: number) => `Tous les${ending(x, '', '')}`,
  submission: 'Soumission',
  initiation: 'Initiation',

  deleteWorkflow: 'Supprimer le Workflow',
  workflowDeletionDialogDescr: {
    confirmDelete:
      'La suppression de ce Workflow supprimera le Workflow ainsi que toutes les notes et données associées du projet.',
    confirmDelete2:
      'Faites ceci uniquement si la demande de Workflow était une erreur.',
  },
  draftWorkflowDeletionDialogDescr: {
    confirmDelete:
      'Cela supprimera définitivement le Workflow en brouillon du projet.',
  },
  emailPdfReport: 'Rapport PDF par courrier électronique',
  unsubscribe: 'Se désabonner',
  followFormMenuDescr: {
    toReceive: 'Pour recevoir des notifications de Workflow',
    toStop: 'Pour ne plus recevoir de notifications de Workflow',
  },
  workflowDeleteMenuDescr: {
    isDone: 'Le Workflow est terminé, vous ne pouvez pas le supprimer.',
    onlyFor: `Uniquement disponible pour les administrateurs,
      responsables du Workflow, demandeurs, cessionnaires`,
  },
  configureTableColumns: 'Configurer les colonnes du tableau',
  searchProperties: 'Rechercher des propriétés',
  shownInTable: 'Montré dans le tableau',
  hiddenInTable: 'Caché dans le tableau',
  visibleLayers: 'Couches visibles',
  hiddenLayers: 'Couches cachées',
  finishingUpload: 'Finalisation du téléchargement',
  processingTheUpload: 'Traitement du téléchargement',
  uploadSucceeded: 'Téléchargement réussi',
  uploadFailed: 'Échec du téléchargement',
  replaced: 'Remplacé',
  pleaseWaitThisMayTakeAWhile:
    'Veuillez patienter, cela peut prendre un certain temps',
  aNewerVersionOfThisFileWasUploaded:
    'Une version plus récente de ce fichier a été téléchargée.',
  somethingWentWrongPleaseTryAgain:
    'Une erreur s’est produite — veuillez réessayer.',
  freezeColumns: 'Geler les colonnes ci-dessus',
  restoreDefaults: 'Restaurer par défaut',
  rememberChoice: 'Se souvenir de mon choix',
  successfullyDeletedForms: (x: number) =>
    `Supprimé avec succès ${x} ${workflows(x)}`,
  failedToDelete: 'Échec de la suppression',
  failedToArchive: "Échec de l'archivage",
  failedToUnarchive: 'Échec du désarchivage',
  failedToLoadUsageData:
    "Échec du chargement des données d'utilisation. Veuillez réessayer.",
  retry: 'Réessayer',
  cannotDeleteDoneForms: 'Impossible de supprimer les Workflows terminés',
  cannotDeleteSystemFormsTooltip:
    'La suppression des Forms par défaut est interdite',
  getMobileNativeApp: "Obtenez l'application",
  getMobileNativeApp_long: "Obtenez l'application mobile StruxHub",
  getAppOnGoogle: 'Disponible sur Google Play',
  getAppOnApple: 'Téléchargez dans l’App Store',
  oneTimePopup: {
    title: 'Téléchargez notre nouvelle application mobile!',
    body: 'Recevez des mises à jour en temps réel et accomplissez plus de travail sur le terrain avec StruxHub.',
    dismiss: "D'accord, compris!",
  },

  enterX: (x: string) => `Entrer ${x}`,
  selectX: (x: string) => `Sélectionner ${x}`,
  aboutX: (x: string) => `À propos de ${x}`,
  selectNestedItems: 'Sélectionner les éléments imbriqués',
  deselectNestedItems: 'Désélectionner les éléments imbriqués',
  createNewX: (x: string) => `Créer ${x}`,
  editX: (x: string) => `Modifier ${x}`,
  hoursUnit: 'heures',
  enterProjectCode: 'Entrez le code du projet',
  somethingWentWrongTryAgainLater:
    "Une erreur s'est produite, réessayez plus tard !",
  unsubscribedSuccessfully: 'Désabonnement réussi!',
  exitScanner: 'Quitter le scanner',
  deleteScanner: 'Supprimer le scanner',
  editScannerText: 'Modifier le scanner',
  shouldDeleteScanner: 'Devrait supprimer le scanner',
  startScanner: 'Activer le scanner',
  queue: "File d'attente",
  startQueue: "Démarrer la file d'attente",
  addedToQueue: "Ajouté à la file d'attente",
  startSession: 'Démarrer la session',
  allowedUsers: 'Utilisateurs admis',
  badgeDescription: 'Description du badge',
  makeItTimed:
    "Faites-en un scanner 'Chronométré' pour les utilisateurs. " +
    'Scan-Start : Scan-Stop = Total Time',
  limitAccess:
    "Limiter l'accès à des utilisateurs, équipes ou entreprises 'Badgés' spécifiques",
  endRideConfirm: 'Etes-vous sûr de vouloir terminer le trajet?',
  changeModeConfirm: 'Etes-vous sûr de vouloir changer le mode de conduite?',
  startSessionConfirm: 'Êtes-vous sûr de vouloir commencer la session?',
  isScannerTimedByDay:
    'La minuterie SE TERMINE automatiquement à la fin de la journée de travail',
  endRide: 'Fin du trajet',
  scanTo: 'Scanner vers',
  activeUsers: 'Utilisateurs actifs:',
  scannerActive: 'Scanner actif',
  scannerInactive: 'Scanner inactif',
  pauseScanner: 'Pause du scanner',
  resumeScanner: 'Reprendre un scanner',
  endSessionForAll: 'Fin de la séance pour tous',
  formName: 'Type de Workflow',
  undo: 'Défaire',
  redo: 'Refaire',
  icon: 'Icône',
  color: 'Couleur',
  parent: 'Parent',
  lbsParent: 'Parent LBS',
  lbsParentDescription:
    'La LBS (structure de répartition des emplacements) définit où une Ressource se situe dans le Projet. Sélectionnez l’Emplacement Parent de cette Ressource.',
  newLocationNeedsParent: 'Un nouvel emplacement nécessite un parent LBS.',
  locationTypeNotUsableInWorkflows:
    "Ce type d'emplacement ne peut pas encore être utilisé dans un Workflow.",
  uncreatableLocationTypesSkipped:
    'Emplacements ignorés : ce projet ne peut pas créer leur type.',
  locationTypesStillLoading:
    "Les types d'emplacement sont en cours de chargement. Réessayez dans un instant.",
  locationTypesFailedToLoad:
    "Impossible de charger les types d'emplacement. Rechargez la page pour réessayer.",
  uncreatableLocationType: "Ce projet ne peut pas créer ce type d'emplacement.",
  showInApp: `Afficher dans l'application`,
  allowCreation: 'Autoriser la création',
  activeNotActive: 'Actif / Non actif',
  notActive: 'Non actif',
  allowCreationInfo: 'Active ou désactive la création de ce type de workflow.',
  creationAccess: 'Accès à la création',
  creationAccessInfo:
    'Définit qui peut créer ce type de workflow.\nTout le monde : tout membre du projet.\nAdministrateurs uniquement : réservé aux administrateurs.',
  adminsOnly: 'Administrateurs uniquement',
  everyone: 'Tout le monde',
  blockRequestsForNonWorkTimes: 'Blocage des demandes de temps non travaillé',
  general: 'Générale',
  steps: 'Étapes',
  views: 'Vues',
  confirmation: 'Confirmation',
  deletionOfDefaultForms:
    'La suppression des Workflows par défaut est interdite',
  theFollowingEntitiesWereUpdated: 'Les entités suivantes ont été mises à jour',
  globeViews: 'Vues du globe',
  whiteboards: 'Tableaux blancs',
  whiteboard: 'Tableau blanc',
  georeferenced: 'Géoréférencé',
  nonGeoreferenced: 'Non géoréférencé',
  areYouSureDeleteMapView: 'Êtes-vous sûr de vouloir supprimer cette carte ?',
  areYouSureDeleteTileset:
    'Êtes-vous sûr de vouloir supprimer ce jeu de tuiles ?',
  areYouSureDeleteRemoteTileset:
    'Êtes-vous sûr de vouloir supprimer ce jeu de tuiles distant ?',
  addRemoteTileset: 'Ajouter un jeu de tuiles distant',
  fileProperties: 'Propriétés du fichier',
  shapeProperties: 'Propriétés de la forme',
  createLocationWithThisShape: 'Créer un emplacement avec cette forme',
  latitude: 'Latitude',
  longitude: 'Longitude',
  zoom: 'Zoom',
  zoomIn: 'Zoom avant',
  zoomOut: 'Zoom arrière',
  parts: 'Parties',
  geometryType: 'Type de géométrie',
  noMapViewsConfigured:
    'Aucune vue de carte configurée pour ce projet pour le moment.',
  noMapViewsMatchingX: (x: string) =>
    `Aucune vue de carte ne correspond à « ${x} ».`,
  mapViewsManager: 'Gestionnaire des vues de carte',
  selectMapView: 'Sélectionner une vue de carte',
  xMapViews: (x: number) => `${x} vues de carte`,
  xOfYMapViews: (visible: number, total: number) =>
    `${visible} sur ${total} vues de carte`,
  createdAt: 'Créé le',
  byLBS: 'Par LBS',
  byLocation: 'Par localisation',
  byType: 'Par type',
  collapseOneLevel: 'Réduire d’un niveau',
  expandOneLevel: 'Développer d’un niveau',
  previousMapView: 'Vue de carte précédente',
  nextMapView: 'Vue de carte suivante',
  closeMapViews: 'Fermer les vues de carte',
  mapViewsBrowseAndSelect:
    'Parcourez et sélectionnez une vue de carte pour le projet.',
  selectAssociatedResource: 'Sélectionner la ressource associée',
  selectPermittedResources: 'Sélectionner les ressources autorisées',
  selectResources: 'Sélectionner les ressources',
  openActionsForX: (x: string) => `Ouvrir les actions pour ${x}`,
  openX: (x: string) => `Ouvrir ${x}`,
  openAMap: 'Ouvrir une carte',
  backToX: (x: string) => `Retour à ${x}`,
  selectMapViewX: (x: string) => `Sélectionner la vue de carte ${x}`,
  available: 'Disponible',
  useMapLabels: 'Afficher les noms de lieux',
  notifiedUsers: 'Utilisateurs notifiés',
  selectNotificationUsers: `Sélectionnez les utilisateurs qui recevront une notification après l'achèvement de cette étape`,
  wouldYouEnableNotificationsForSteps:
    'Souhaitez-vous activer les notifications pour ce champ dans toutes les étapes du Workflow?',
  allowNotificationsFor: (fieldName: string) =>
    `Autoriser les notifications pour ${fieldName}`,
  notifyFor: 'Notifier pour',
  selectWorkflowSteps: 'Sélectionner les étapes du Workflow',
  allSteps: 'Toutes les étapes',
  workflowBookingDeadline: 'Date limite de réservation des Workflows',
  workflowDeadlineDescr: `Les Workflows doivent être demandés avant la date limite.
  Définissez combien de jours à l'avance ci-dessous.
  Le commutateur peut être configuré pour bloquer la demande tardive des Workflows.
  L'utilisateur recevra un avertissement et devra choisir un jour plus éloigné dans le temps`,
  workflowsMustBeRequested: `Les Workflows doivent être demandés à l'avance`,
  workflowWasntRequestedInAdvance: (interval: string) =>
    `Ce Workflow n'a pas été demandé${
      interval ? ` ${interval}` : ''
    } à l'avance`,
  workflowDateTimeBlocked: `La date et l'heure du Workflow sont bloquées`,
  pleaseMoveThisWorkflow: (days: number) =>
    `Veuillez déplacer ce Workflow. Les demandes doivent être faites ${days} jour${ending(
      days,
    )} ouvrable${ending(
      days,
    )} à l'avance par rapport à la date et à l'heure que vous demandez et à une date-heure de travail`,
  requestsMustBeInAdvance: (days: number) =>
    `Les demandes doivent être faites ${days} jour${ending(
      days,
    )} ouvrable${ending(
      days,
    )} à l'avance par rapport à la date et à l'heure que vous demandez`,
  hide: 'Masquer',
  warnUsers: 'Avertir les utilisateurs',
  blockUsers: 'Bloquer les utilisateurs',
  duplicateCurrentWorkflow: 'Dupliquer le Workflow actuel',
  mapNotifications: 'Notifications de carte',
  olderWorkflowCantBeDuplicate:
    'La version antérieure du Workflow ne peut pas être dupliquée.',
  invalidNumberFormat: 'Format de numéro invalide',

  restrooms: 'Toilettes',
  canteen: 'Réfectoire',
  toilet: 'Toilette',
  breakText: 'Pause',
  dumpster: 'Poubelle',
  elevator: 'Ascenseur',
  electricalRoom: 'Salle électrique',
  access: 'Accès',
  washArea: 'Zone de lavage',
  medicalArea: 'Zone médicale',
  musterPoint: 'Point de rassemblement',
  parking: 'Parking',
  smokingArea: 'Zone fumeurs',
  stairway: 'Escalier',
  covidCheckpoint: 'Point de contrôle Covid',
  breakArea: 'Zone de pause',
  walkway: 'Passage piéton',
  trailer: 'Bungalow de chantier',
  informationPoints: "Points d'information",
  glyphNames: {
    truck: 'Camion',
    'concrete-mixer-truck': 'Camion toupie',
    'equipment-storage': "Stockage d'équipement",
    'material-storage-boxes': 'Stockage : cartons',
    'material-storage-wood': 'Stockage : bois',
    'material-storage-steel': 'Stockage : acier',
    'material-storage-pipes': 'Stockage : tuyaux',
    'material-lifting-steel': "Levage d'acier",
    scaffolding: 'Échafaudage',
    'swing-stage': 'Nacelle suspendue',
    'personnel-lift': 'Élévateur de personnes',
    info: 'Info',
    'site-map': 'Plan du chantier',
    'log-out': 'Sortie',
    'no-entry': 'Entrée interdite',
    stop: 'Stop',
    'no-pedestrians': 'Piétons interdits',
    'no-access': 'Accès interdit',
    'security-checkpoint-window': 'Poste de sécurité',
    'visitor-check': 'Accueil des visiteurs',
    'high-visibility-vest': 'Gilet haute visibilité',
    'safety-helmet': 'Casque de sécurité',
    'safety-gloves': 'Gants de sécurité',
    'safety-toe-boots': 'Chaussures de sécurité',
    'ppe-not-required': 'EPI non requis',
    'no-vest': 'Sans gilet',
    'no-helmet': 'Sans casque',
    'no-gloves': 'Sans gants',
    'no-boots': 'Sans chaussures',
    'cleanroom-gowning': 'Salle blanche / habillage',
    'icra-zone': 'Zone ICRA',
    'negative-pressure': 'Pression négative',
    'esd-zone': 'Zone ESD',
    'no-photography': 'Photos interdites',
    'no-drones': 'Drones interdits',
    'fall-hazard': 'Risque de chute',
    'silica-dust': 'Silice / poussière',
    noise: 'Bruit',
    'confined-space': 'Espace confiné',
    'energized-equipment': 'Équipement sous tension',
    'bike-parking': 'Parking vélos',
    'utility-cart': 'Chariot utilitaire',
    'loading-zone': 'Zone de chargement',
    'fire-lane': 'Voie pompiers',
    'flagger-ahead': 'Signaleur',
    'one-way': 'Sens unique',
    'speed-limit': 'Limitation de vitesse',
    trash: 'Poubelle',
    'admin-office': 'Bureau administratif',
    'hand-wash-station': 'Poste de lavage des mains',
    'spill-kit': 'Kit antipollution',
    'erosion-control': "Contrôle de l'érosion",
    'storm-drain-inlet-protection': 'Protection des avaloirs',
    'concrete-washout': 'Bac de lavage béton',
    'wheel-wash': 'Lave-roues',
    'restroom-men': 'Toilettes hommes',
    'restroom-women': 'Toilettes femmes',
    sofa: 'Salon',
    hydration: 'Hydratation',
    'heart-pulse': 'DAE',
    eyewash: 'Douche oculaire',
    'fire-extinguisher': 'Extincteur',
    'fire-hydrant': 'Poteau incendie',
    'fire-alarm-button': "Déclencheur d'alarme",
    'standpipe-hose': 'Colonne sèche / tuyau',
    flame: 'Travaux par point chaud',
    excavator: 'Pelle mécanique',
    'overhead-load': 'Charge suspendue',
    'brand-oes': 'OES Equipment',
    'brand-sunbelt': 'Sunbelt Rentals',
    'brand-united-rentals': 'United Rentals',
    'brand-equipmentshare': 'EquipmentShare',
    'brand-feco': 'First Equipment Company',
    'brand-herc-rentals': 'Herc Rentals',
  },
  informationPointTypes: {
    general_information_point: "Point d'information général",
    access: 'Accès',
    exit: 'Sortie',
    restricted_area: "Zone d'accès restreint",
    security_checkpoint: 'Poste de contrôle de sécurité',
    ppe_zone: 'Zone EPI',
    controlled_environment: 'Environnement contrôlé',
    site_rules: 'Règles du chantier',
    hazard_zone: 'Zone dangereuse',
    confined_space: 'Espace confiné',
    energized_equipment: 'Équipement sous tension',
    walkway: 'Passage piéton',
    parking: 'Parking',
    loading: 'Chargement',
    traffic_control: 'Gestion de la circulation',
    waste: 'Déchets',
    stormwater_erosion_control: 'Eaux pluviales et érosion',
    elevated_access: 'Accès en hauteur',
    site_office: 'Bureau de chantier',
    trailer: 'Bungalow de chantier',
    electrical_room: 'Salle électrique',
    utilities: 'Réseaux',
    restrooms: 'Toilettes',
    break_area: 'Zone de pause',
    canteen: 'Réfectoire',
    hydration_station: "Point d'eau potable",
    handwash_station: 'Poste de lavage des mains',
    smoking_area: 'Zone fumeurs',
    medical_area: 'Zone médicale',
    fire_protection: 'Protection incendie',
    muster_point: 'Point de rassemblement',
  },
  informationPointGroups: {
    general: 'Général',
    entry_restrictions: 'Accès et restrictions',
    hazards: 'Dangers',
    site_logistics: 'Logistique de chantier',
    offices_utilities: 'Bureaux et réseaux',
    restrooms_breaks: 'Sanitaires et repos',
    emergency: 'Urgence et secours',
  },
  informationPointPicker: {
    title: 'Placer un point',
    byType: 'Par type',
    byIcon: 'Par icône',
    xTypes: (x: number) => `${x} Type${ending(x)}`,
  },

  shaft: 'Arbre',
  crane: 'Grue',
  hoist: 'Palan',
  aerialLift: 'Élévateur aérien',
  forkLift: 'Chariot élévateur',
  gradall: 'Gradall',
  allFormsSharingCalendarEnabled:
    'Activation du partage de tous les Workflows vers le calendrier',
  allDeliveriesSharingCalendarEnabled:
    'Activation du partage de toutes les livraisons vers le calendrier',
  allFormsSharingNotificationEnabled:
    'Activation du partage de tous les Workflows vers les notifications',
  allDeliveriesSharingNotificationEnabled:
    'Activation du partage de toutes les diffusions vers les notifications',

  workflowTitle: 'Titre du Workflow',
  workflowStatus: 'Statut du Workflow',

  openWorkflow: 'Ouvrir le Workflow',
  attachmentPhotoAdded: 'Pièce jointe/Photo a été ajoutée',
  attachment: 'Pièce',
  sharingAllFormsToCalendarAndNotificationsEnabled:
    'Activation du partage de tous les Workflows vers le calendrier et les notifications',
  sharingAllDeliveriesToCalendarAndNotificationsEnabled:
    'Activation du partage de toutes les diffusions vers le calendrier et les notifications',
  fromRules: 'À partir des règles',
  calendarLinkInputText:
    'Vous pouvez fournir ce lien vers des applications de calendrier tierces telles que Google Calendar, TeamUp, Outlook, etc.',
  enableIcal: 'Activer iCalendar',
  generate: 'Générer',
  ended: 'Terminée',
  externalWorkflowSharing: 'Partage de Workflow externe',
  addOtherStxCalendarsToProject:
    "Ajouter d'autres calendriers StruxHub au projet",

  shareBasedOnSettingsIn: 'Partage basé sur le paramètre dans',
  ifSelected: 'Si sélectionné',
  onlyWorkflowsSatisfyingModuleRulesWillBeShared: (module: string) =>
    `, seuls les Workflows respectant les règles du module « ${module} » seront partagés.`,
  onlyDeliveriesSatisfyingModuleRulesWillBeShared: (module: string) =>
    `, seules les Livraisons respectant les règles du module « ${module} » seront partagées.`,
  from: 'De',
  to: 'À',
  _to: 'à',
  shareAllForms: 'Partager tous les Workflows',
  shareAllDeliveries: 'Partager toutes les livraisons',
  formSharing: 'Partage de Workflow',
  deliverySharing: 'Partage de livraison',
  chooseDate: 'Choisir la date',
  executeAutomaticFinish: 'Exécuter la fin automatique',
  atAnyStep: "À n'importe quelle étape",
  onlyAt: 'Uniquement à',
  onlyAtOrAfter: 'Uniquement à ou après',
  youHaveUnsavedChanges: 'Vous avez des modifications non enregistrées.',
  unsavedChangesDiscarded:
    'Toutes les modifications non enregistrées seront perdues.',
  stay: 'Restez',
  continue: 'Continuer',
  workflowMessageAdded: 'Message de Workflow ajouté',
  createdBy: 'Créé par',
  categoryCreatedByStruxHub: 'Catégorie créée par StruxHub',
  autoFinishWorkflowOnFinishDate:
    'Terminer automatiquement le Workflow à la date de fin',
  linkForProjectCopiedToClipboard: (projectCode: string) =>
    `Le lien pour le projet ${projectCode} a été copié dans le presse-papiers !`,
  invitedSuccessfully: 'Invitation envoyée avec succès !',
  unsubscriberSuccessfully: 'Désabonnement réussi !',
  provideLink: 'Veuillez fournir un lien.',
  youCannotSubscribeToYourOwnProject:
    'Vous ne pouvez pas vous abonner à votre propre projet.',
  fetchingEventsOnBackground: 'Récupération des événements en arrière-plan...',
  importedSuccessfully: 'Importé avec succès !',
  thisProjectIsAlreadyInvited: 'Ce projet a déjà été invité.',
  confirmDeleteSubscriber: (subscriberCode: string) =>
    `Êtes-vous sûr de vouloir supprimer le projet ${subscriberCode} ?`,
  sharedFromWithName: companyName => `Partagé depuis ${companyName}`,
  sharedFrom: `Partagé depuis`,
  enableRestrictions: 'Activer les restrictions par codes d’invitation',
  notProvided: 'Non fourni',
  shared: 'Partagé',
  generateAndCopyLinkForStxProjects:
    'Générer et copier le lien pour les projets StruxHub',
  inviteStruxProjectsByItsCode:
    'Inviter d’autres projets StruxHub par leur code',
  invitedProjectCodes: 'Codes des projets invités',
  youDontHaveAnyInviteesYet: 'Vous n’avez pas encore d’invités.',
  subscribeToOtherStruxHubProjects: 'S’abonner à d’autres projets StruxHub',
  subscribeToExternalProjects: 'S’abonner à des projets externes',
  pastUrlFromOtherProject: 'Collez l’URL d’un autre projet',
  yourSubscriptions: 'Vos abonnements',
  youDoNotHaveAccessToProject: 'Vous n’avez pas accès au projet',
  youDontHaveAnySubscriptionsYet: 'Vous n’avez pas encore d’abonnements.',
  youSubscribedToNExternalProjects: (n: number) =>
    `Vous êtes abonné à ${n} projet${ending(n)} externe${ending(n)}`,
  visibility: 'Visibilité',
  inviteLink: 'Lien d’invitation',
  sharedProjectName: 'Nom du projet partagé',
  dontShare: 'Ne pas partager',
  disableAccess: 'Désactiver l’accès',
  shareAllDeliveriesWithinSpecificDate:
    'Partager toutes les livraisons dans une période spécifique',
  shareAllFormsWithinSpecificDate:
    'Partager tous les Workflows dans une période spécifique',
  areYouSureYouWantToDisableAccess:
    'Êtes-vous sûr de vouloir désactiver l’accès?',
  deleteSubscription: 'Supprimer l’abonnement',
  allExternalProjectsWillLosePermission:
    'Tous les projets externes qui utilisent ce lien comme abonnement à votre projet perdront la permission de consulter vos Workflows.',
  areYouSureYouWantToDeleteSubscription:
    'Êtes-vous sûr de vouloir supprimer l’abonnement au projet?',
  inThisCaseYouWillNotBeAbleToSeeAnyWorkflowsFromThisProject:
    'Dans ce cas, vous ne pourrez plus voir aucun Workflow de ce projet',
  sharingSettings: 'Paramètres de partage',
  shareIcalCalendarLink: 'Partager le lien iCal',
  shareToExternalProject: 'Partagé vers un projet externe',
  generateShareableLink: `Générer un lien partageable pour permettre aux propriétaires de projets Struxhub externes de voir vos Workflows en fonction des paramètres de partage que vous avez configurés.`,
  generateShareableCalendarLink: `Générer un lien partageable pour permettre aux calendriers externes
  (Apple Calendar, Google Calendar, etc.) de voir vos Workflows en fonction des paramètres de partage que vous avez configurés.`,
  subscribeShareableLink: `Collez un lien partageable pour obtenir des données à partir d'un projet Struxhub externe.`,
  ifTheProjectIsEnabled:
    'Si le commutateur est activé, le projet externe pourra voir vos Workflows partagés en fonction des paramètres de partage que vous avez configurés.',
  ifTheProjectIsDisabled:
    'Si le commutateur est désactivé, le projet externe ne pourra plus voir aucun Workflow partagé.',
  import: 'Importer',
  preparingUpload: 'Préparation du téléchargement...',
  uploadEquipmentFile:
    "Téléchargez votre fichier d'équipement au format Excel (.xlsx, .xlsm) ou CSV (.csv).",
  importComplete: 'Importation terminée',
  created: 'Créé :',
  skippedValues: 'Certaines Propriétés sont restées vides',
  skippedValuesDescription:
    "Toutes les lignes ont été créées, mais les Propriétés ci-dessous n'ont pas pu être associées à des enregistrements existants et ont été laissées vides.",
  xPropertiesSkipped: (n: number) =>
    `${xProperties(n)} ignorée${n === 1 ? '' : 's'}`,
  mapColumnsToProperties: 'Mapper les colonnes aux propriétés',
  id: 'ID',
  selectIdColumn: 'Sélectionner la colonne ID',
  selectTitleColumn: 'Sélectionner la colonne titre',
  selectColumn: 'Sélectionner la colonne',
  template: 'Modèle',
  selectTemplate: 'Sélectionner le modèle',
  loadingProperties: 'Chargement des propriétés...',
  equipmentProperties: (n: number) => `Propriétés de l'équipement (${n})`,
  materialProperties: (n: number) => `Propriétés du matériau (${n})`,
  locationProperties: (n: number) => `Propriétés de l'emplacement (${n})`,
  inviteCodeCantBeEmpty: 'Le code d`nvitation ne peut pas être vide',
  goToTheProject: 'Aller au projet',
  locationsFrom: 'Emplacements de départ',
  locationsTo: 'Emplacements d’arrivée',
  goToLocation: (locationName: string) => `Aller à ${locationName}`,
  callTo: (name: string) => `Appeler ${name}`,
  mailTo: (name: string) => `Envoyer un email à ${name}`,
  goToTheBuilding: 'Aller au bâtiment',
  selectWorkflowType: 'Sélectionnez le type de Workflow',
  createNew: 'Créer un nouveau',
  defaultDuration: 'Durée par défaut',
  maximumDuration: 'Durée maximale',
  maxDurationError: (maxDuration: string) =>
    `La durée entre « début » et « fin » dépasse la limite maximale autorisée de ${maxDuration}`,
  nonWorkDayOrDays: 'Jour/s non ouvré/s',
  nonWorkHours: 'Heures non ouvrées',
  failedToSave: "Échec de l'enregistrement",
  failedToSaveChangesTryAgain:
    "Échec de l'enregistrement des modifications. Veuillez réessayer.",
  failedToDeleteFormsTryAgain:
    'Échec de la suppression des formulaires. Veuillez réessayer.',

  filterType: 'Filtrer le type',
  workWeek: 'Semaine de travail',
  maximumDurationTime: 'Temps de durée maximale',
  defaultDurationTime: 'Temps de durée par défaut',
  allowRepeatingWorkflow: 'Autoriser la répétition du Workflow',
  maximumRepeatingDuration: 'Durée maximale de répétition',
  iCalSharingSettings: 'Paramètres de partage iCal',
  nothing: 'rien',
  if: 'Si',
  thenShow: 'alors afficher',
  showAllSettings: 'Afficher tous les paramètres',
  hideAllSettings: 'Masquer tous les paramètres',
  addDependentField: 'Ajouter un champ dépendant',
  addField: 'Ajouter un champ',
  alreadyAdded: 'Déjà ajouté',
  item: 'Élément',
  addAssociatedField: "Ajouter un champ d'élément",
  associatedFields: "Champs d'élément",
  xAssociatedFields: (x: number) =>
    `${x} Champ${ending(x)} d'élément ajouté${ending(x)}`,
  addTableColumn: 'Ajouter une colonne au tableau',
  tableColumns: 'Colonnes du tableau',
  xTableColumns: (x: number) => `${x} Colonne${ending(x)} de tableau`,
  newRule: 'Nouvelle règle',
  inactive: 'Inactif',
  repeatWorkflow: 'Répéter le Workflow',
  thisWorkflow: 'Ce Workflow',
  showOnSourceMap: 'Afficher sur la carte source',
  thisWorkflowIsPinnedOnADifferentMapView:
    'Ce Workflow est épinglé sur une autre vue de carte',
  thisWorkflowIsHiddenOnTheMap: 'Ce Workflow est masqué sur la carte',
  switchedToMapViewX: (name: string) =>
    `Basculé vers la vue cartographique ${name}`,
  thisWorkflowIsNotShownOnThisMapView:
    "Ce Workflow n'est pas affiché sur cette vue de carte",
  thisWorkflowHasNoPinOnAnyMapView:
    "Ce Workflow n'a de repère sur aucune vue de carte",
  thisAndFutureWorkflows: 'Ce Workflow et les futurs',
  usedInMaterialsTableCalculations:
    'Utilisé dans les calculs de table des matériaux',
  usedToSpecifySourceLocationInMaterialsTable:
    'Utilisé pour spécifier la source de l’emplacement dans la table des matériaux',

  note: 'Note',
  dependentFieldsTooltip: {
    title: 'Champs dépendants',
    description: `Créez des règles pour afficher ou masquer d’autres champs
    en fonction de la valeur sélectionnée dans ce champ.`,
  },
  dynamicFiltersTooltip: {
    title: 'Filtres dynamiques',
    description:
      'Créez des filtres pour afficher dynamiquement les options basées sur les valeurs et conditions des champs.',
  },
  selection: 'Sélection',
  selectionSettings: 'Paramètres de sélection',
  conditionalFilters: 'Filtres conditionnels',
  xConditionalFilters: (x: number) =>
    `${x} Filtre${ending(x)} conditionnel${ending(x)}`,
  addConditionalFilter: 'Ajouter un filtre conditionnel',
  conditionalFields: 'Champs conditionnels',
  xConditionalFields: (x: number) =>
    `${x} Champ${ending(x)} conditionnel${ending(x)}`,
  addConditionalField: 'Ajouter un champ conditionnel',
  selectionTooltip:
    'Définissez comment les utilisateurs ajoutent des éléments \u2014 créer, sélectionner, ou les deux. Définissez le tri, le regroupement et les filtres par défaut.',
  itemFieldsTooltip:
    'Capturez des détails supplémentaires par élément. Chaque élément devient une ligne ; ces champs sont ses colonnes.',
  conditionalFiltersTooltip:
    "Filtrez la liste des éléments disponibles en fonction des valeurs d'autres champs du formulaire.",
  conditionalFieldsTooltip:
    "Afficher des champs supplémentaires uniquement lorsqu'un élément spécifique est sélectionné.",
  cardView: 'Vue carte',
  lastUpdatedBy: 'Dernière mise à jour par',
  deleteFormsConfirm: (x: number) =>
    `Êtes-vous sûr de vouloir supprimer ${x} formulaire${ending(x)} ?`,
  removeSelected: 'Supprimer sélectionné',
  addOption: 'Ajouter une option',
  optionName: "Nom de l'option",
  removed: 'supprimé',
  editProperty: 'Modifier la propriété',
  createNewProperty: 'Créer une nouvelle propriété',
  deleteProperty: 'Supprimer la propriété',
  archiveProperty: 'Archiver la propriété',
  unarchiveProperty: 'Désarchiver la propriété',
  typeToConfirm: 'Tapez pour confirmer',
  propertyUsedByXResources: (x: number) =>
    `Cette Propriété est utilisée par ${x} Ressource${ending(x)}.`,
  deletePropertyConfirmation: (count: number) =>
    count > 0
      ? `La suppression supprimera définitivement cette Propriété et toutes ses données de ces Ressources. Tapez le nombre de Ressources associées ou "Delete" pour confirmer.`
      : "Cette Propriété n'est utilisée par aucune Ressource. Elle sera définitivement supprimée.",
  archivePropertyConfirmation: (count: number) =>
    count > 0
      ? `L'archivage supprimera cette Propriété de l'utilisation active sur ces Ressources. Cela peut être annulé. Tapez le nombre de Ressources associées ou "Archive" pour confirmer.`
      : "Cette Propriété n'est utilisée par aucune Ressource. Elle sera archivée.",
  propertyDetails: 'Détails de la propriété',
  propertyPreview: 'Aperçu',
  propertyPreviewUnavailable: 'Aperçu indisponible',
  configurePropertyBeforePreview:
    "Configurez les détails de la propriété avant d'afficher l'aperçu.",
  managedRelationPreviewHelp:
    'Cette relation peut être gérée après la création de la ressource.',
  calculatedPropertyPreviewHelp:
    'Cette valeur est calculée automatiquement et ne peut pas être modifiée.',
  unsupportedPropertyPreviewHelp:
    'Ce type de propriété ne peut pas être prévisualisé.',
  backToDetails: 'Retour aux détails',
  textSettings: 'Paramètres de texte',
  enableMultilineInput: 'Activer la saisie multiligne',
  internalName: 'Nom interne',
  internalNameCannotBeChanged: 'Le nom interne ne peut pas être modifié',
  dropdownOptionsSettings: 'Paramètres des options déroulantes',
  newOption: 'Nouvelle option',
  xOptionsAdded: (x: number) => `${x} option${ending(x)} ajoutée${ending(x)}`,
  allowUsersToCreateNewOptions:
    "Permettre à l'utilisateur de créer de nouvelles options",
  allowUsersToSelectExistingWorkflows:
    'Allow Users to select existing workflows',
  allowUsersToCreateNewWorkflow: 'Allow Users to create new workflow',
  selectExisting: 'Sélectionner existant',
  noResultsFound: 'Aucun résultat trouvé',
  allowUsersToSelectExisting: (label: string) =>
    `Permettre aux utilisateurs de sélectionner des ${label} existants`,
  allowUsersToCreateNew: (label: string) =>
    `Permettre aux utilisateurs de créer de nouveaux ${label}`,
  allowToSelect: 'Autoriser la sélection',
  activityFieldRestrictedTooltip:
    'Le champ Activité est limité aux types de flux de travail Activité uniquement.',
  cantSeeResource: (resource: string) => `Vous ne voyez pas ${resource} ?`,
  openNonWorkflowResources: (resource: string) =>
    `Ouvrir ${resource} hors ressources de flux`,
  alsoManageFromControls:
    'Vous pouvez également effectuer ces modifications depuis la',
  resourceControlsPage: (resource: string) => `page Contrôles ${resource}`,
  enablingResourcesHint: (resource: string) =>
    `Ces ${resource} ne sont pas encore des Ressources de flux. L’enregistrement active leur commutateur « WR » et les autorise pour ce champ, ainsi que pour tout champ défini sur « N'importe ${resource} ».`,
  anyWorkflowTypes: 'Tous les types de flux',
  allowNoneSelection: "Autoriser la sélection 'Aucun'",
  saveChanges: 'Enregistrer les modifications',
  noIconsFoundByYourRequest: 'Aucune icône trouvée par votre requête',
  generalWorkflow: 'Workflow général',
  ganttChart: 'Diagramme de Gantt',
  activity: 'Activité',
  automation: 'Automatisation',
  craneBooking: 'Réservation de grue',
  deliveryBooking: 'Réservation de livraison',
  environmental: 'Environnemental',
  airMitigation: "Mitigation de l'air",
  eco: 'ECO',
  authorizedVehicleAccess: 'Accès véhicule autorisé',
  fireExtinguisherPermit: 'Permis extincteur de feu',
  heavyEquipmentUsageLog: "Journal d'utilisation de matériel lourd",
  ladderPermit: 'Permis échelle',
  surfacePenetrationPermit: 'Permis de pénétration de surface',
  interiorConcretePenetrationPermit: 'Permis de pénétration intérieure/béton',
  aerialLiftInspection: "Inspection de l'élévateur aérien",
  forkliftRoomWorkPermit: 'Permis de travail de chariot élévateur',
  materialTransfer: 'Transfert de matériaux',
  procurementTracking: 'Suivi des achats',
  quality: 'Qualité',
  qualityControlInspection: 'Inspection de contrôle de qualité',
  report: 'Rapport',
  timesheet: 'Feuille de temps',
  safety: 'Sécurité',
  energizedRoomWorkPermit: 'Permis de travail de salle énergizée',
  hotWorkPermit: 'Permis de travail chaud',
  jobHazardAnalysis: 'Analyse des risques du travail',
  siteCommunication: 'Communication du site',
  closureRequest: 'Demande de fermeture',
  siteAnnouncement: 'Annonce du site',
  comingSoon: 'Bientôt disponible',
  incorrectDateOrTime: 'Date ou heure incorrecte',
  invalidSettings: 'Paramètres invalides',
  imageDimensionsCanNotBeGreaterThan: (
    maxWidth?: number,
    maxHeight?: number,
  ) => {
    if (maxWidth && maxHeight) {
      return `Les dimensions de l'image ne peuvent pas être supérieures à ${maxWidth}x${maxHeight}`
    } else if (maxWidth) {
      return `La largeur de l'image ne peut pas être supérieure à ${maxWidth}px`
    } else if (maxHeight) {
      return `La hauteur de l'image ne peut pas être supérieure à ${maxHeight}px`
    }
    return "Dimensions d'image invalides"
  },
  fileSizeCanNotBeGreaterThan: (maxFileSize: string) =>
    `La taille du fichier ne peut pas être supérieure à ${maxFileSize}`,
  controlledAccessZone: "Zone d'accès contrôlé",
  guardrailRemovalPermit: 'Permis de retrait de garde-corps',
  roofAccessPermit: "Permis d'accès au toit",
  smartFormImport: 'Importation intelligente de Workflow',
  smartFormImportDescription:
    'Téléchargez un fichier PDF ou image et l’IA détectera et mappera automatiquement les champs.',
  dragAndDropFile: 'Glisser-déposer un fichier',
  or: 'ou',
  chooseFile: 'Choisir un fichier',
  fileSupportText: 'Supports PDF, JPG, PNG files',
  importFile: 'Importer un fichier',
  workingOnIt: '🛠️ Nous travaillons dessus.',
  detectingHeaders: '🔎 Détection des en-têtes…',
  findingFields: '📑 Recherche des champs…',
  scanningCheckboxes: '☑️ Détection des cases à cocher…',
  organizingWorkflowSteps: '🗂️ Organisation des étapes de Workflow…',
  aligningWithStruxHubSchema: '🧩 Alignement avec le schéma StruxHub…',
  doubleCheckingFieldTypes: '🛡️ Double-vérification des types de champs…',
  tidyingUpYourForm:
    '🧹 Nettoyage de votre formulaire (plus propre que le bureau d’une remorque).',
  sharpeningDigitalPencils: '✏️ Affûtage des crayons numériques…',
  sortingThingsIntoTheRightBins: '🏗️ Tri des choses dans les bons bins…',
  moveFieldsIntoDifferentWorkflowStepsAfterImport:
    '💡 Vous pouvez déplacer les champs dans différentes étapes de workflow après l’import.',
  addWorkflowStepsForApprovalsInspectionsAndStartTimes:
    '💡 Vous pouvez ajouter des étapes de workflow pour les approvals, inspections et les heures de début.',
  routeWorkflowStepsToBallInCourtUsersWithConditionalLogic:
    '💡 Vous pouvez router des étapes de workflow vers les utilisateurs de balle en justice avec une logique conditionnelle.',
  notifyWorkflowMembersAfterSpecificSteps:
    '💡 Vous pouvez notifier les membres du workflow après des étapes spécifiques.',
  triggerResourceClosureAfterSpecificWorkflowStep:
    '💡 Vous pouvez déclencher une fermeture de ressource après une étape de workflow spécifique.',
  linkWorkflowToAnotherWorkflow:
    '💡 Vous pouvez lier un workflow à un autre workflow via le champ «Workflow lié».',
  createTemplatesFromImportedFormsToReuseAcrossProjects:
    '💡 Vous pouvez créer des modèles à partir de formulaires importés pour les réutiliser sur les projets.',
  assignResponsibilityForWorkflowStepsToSpecificCompanies:
    '💡 Vous pouvez assigner la responsabilité des étapes de workflow à des entreprises spécifiques.',
  rollUpFormDataIntoExportableReports:
    '💡 Vous pouvez récupérer les données du formulaire dans des rapports exportables.',
  attachDocumentsAndPhotosToAnyWorkflow:
    '💡 Vous pouvez joindre des documents et des photos à n’importe quel workflow.',
  safetyFirstNoHardHatRequiredWhileWeProcessYourForm:
    '🦺 Sécurité en premier: aucun casque requis pendant que nous traitons votre formulaire.',
  ourScannersLiftFormsFasterThanALifterLiftsSteel:
    '🚜 Nos scanners lèvent les formulaires plus rapidement que les grues lèvent le fer.',
  everSeenPaperworkMoveThisFastOnAJobsiteNeitherHaveWe:
    '🏃 Avez-vous déjà vu le papier bouger aussi vite sur un chantier? Ni nous.',
  importingFormsNoRFIsRequired:
    '📨 Importation de formulaires — aucun RFIs requis.',
  dontWorryThisWontTakeAsLongAsAConcretePour:
    '🏗️ Ne vous en faites pas, cela ne prendra pas autant de temps qu’un coulage de béton.',
  processingFormsFasterThanFindingTheRightWrenchInYourToolbox:
    '🔧 Traitement des formulaires plus rapidement que la recherche de la bonne clé dans votre boîte à outils.',
  proTipThisIsTheOnlyTimePaperworkWillDoTheWorkForYou:
    '🤓 Conseil: c’est la seule fois où le papier fera le travail pour vous.',
  scanningWithoutThePaperCuts: '✋ Détection… sans les coupes de papier.',
  ourImportToolDoesntNeedAForemanYellingAtIt:
    '📣 Notre outil d’importation n’a pas besoin de dire à un chef de chantier qu’il faut importé.',
  almostThereUnlikeThatElevatorInstallation:
    '🚧 Presque là — contrairement à l’installation d’un ascenseur.',
  wrappingThingsUp: '🎁 Mise en place des derniers éléments…',
  puttingFinishingTouchesOnYourForm:
    '✅ Mettant les derniers éléments sur votre formulaire…',
  justOneMoreCheck: '📦 Une dernière vérification…',
  workflowWasSuccessfullyImported: (workflowName: string) =>
    `🎉 ${workflowName} a été importé avec succès!`,
  somethingWentWrongWhileImportingFile:
    'Quelque chose s’est mal passé lors de l’importation de votre fichier',
  importInProgress: 'Une importation est déjà en cours',
  viewWorkflow: 'Voir le workflow',
  enableMaximumDuration: 'Activer la durée maximale',
  enableDefaultDuration: 'Activer la durée par défaut',
  minute_plural: (x: number) => `Minute${ending(x)}`,
  hour_plural: (x: number) => `Heure${ending(x)}`,
  day_plural: (x: number) => `Jour${ending(x)}`,
  week_plural: (x: number) => `Semaine${ending(x)}`,
  month_plural: () => `Mois`,
  durationCannotBeGreaterThan: `La durée ne peut pas être supérieure à`,
  saveAsDraft: 'Enregistrer en brouillon',
  draftSavedSuccessfully:
    'Brouillon enregistré avec succès. Toutes les modifications ont été enregistrées.',
  createdXofYWorkflows: (created: number, total: number) =>
    `${created} sur ${total} workflows créés`,
  skippedWorkflows: 'Ignorés',
  unknownLocation: 'Emplacement inconnu',
  blockedByClosure: 'bloqué par fermeture à',
  blockedByProjectClosed: "bloqué par l'intervalle de fermeture du projet",
  blockedByNonWorkingDay: 'jours non ouvrables',
  blockedByOperatingHours: "en dehors des heures d'ouverture de",
  reservedForOtherCompanies: "Réservé à d'autres entreprises",
  discardedUnsavedDrawing:
    'Votre dessin non enregistré a été supprimé car la carte a été fermée',
  failedToCreateWorkflow:
    'Impossible de créer le Workflow. Veuillez réessayer.',
  failedToCreateRecurringWorkflows:
    'Échec de la création des workflows récurrents. Veuillez réessayer ou contacter le support',
  unavailableDraftWorkflowsSelected:
    'Indisponible: Un ou plusieurs Workflows sélectionnés sont en statut brouillon',
  draft_noun: 'Brouillon',
  linkedWorkflows: 'Workflows liés',
  addLinkedWorkflow: 'Ajouter un workflow lié',
  dateTime: 'Date et Heure',
  file: 'Fichier',
  allWorkflow_s: 'Tous les flux de travail',
  appliedTo: (count: number) => `Appliqué à (${count})`,
  closuresConflictTitle: 'Conflicts de Fermetures',
  closuresConflictMessage:
    'Les emplacements sélectionnés ont des fermetures en conflit avec les dates du Workflow. Les emplacements en conflit ont été automatiquement supprimés. Veuillez vérifier votre formulaire et ajuster si nécessaire.',
  closuresConflictRecurringMessage:
    'Certaines occurrences récurrentes tombent sur des dates fermées. Vous pouvez continuer la création du Workflow récurrent ou revoir et ajuster vos sélections.',
  selectWorkflowTypes: 'Sélectionner les Types de Flux de Travail',

  resourcePropertyText: 'Texte',
  resourcePropertyNumber: 'Nombre',
  resourcePropertySelect: 'Menu déroulant',
  resourcePropertyHierarchy: 'Hiérarchie',
  resourcePropertyCheckbox: 'Case à cocher',
  resourcePropertyDatetime: 'Date/Heure',
  resourcePropertyRelation: 'Relation',
  resourcePropertyLocation: 'Emplacement',
  resourcePropertyRollup: 'Champ de synthèse',
  resourcePropertyComputed: 'Champ calculé',
  resourcePropertyUser: 'Utilisateur',
  resourcePropertyCompany: 'Entreprise',
  resourcePropertyFile: 'Fichier',
  uploadFiles: 'Télécharger des fichiers',
  uploadFile: 'Télécharger un fichier',
  attachFile: 'Joindre un fichier',
  attachFiles: 'Joindre des fichiers',
  camera: 'Caméra',
  maxFilesReached: 'Nombre maximum de fichiers atteint',
  fileTooLarge: (maxMB: number) =>
    `Le fichier est trop volumineux (max ${maxMB} Mo)`,
  sitemapsTooLargeForMobile:
    "Certains plans de site sont trop volumineux pour être affichés dans l'application. Ouvrez StruxHub dans votre navigateur pour les consulter.",
  fileTypeNotAllowed: "Le type de fichier n'est pas autorisé",
  pdfPageImagesCouldNotBeRendered:
    "Certaines images de cette page PDF n'ont pas pu être affichées et sont absentes de la carte importée.",
  associatedProperties: 'Propriétés associées',
  appliedToColumn: 'Appliqué à',
  xResourceObjects: (count: number, resourceTypeId: string) => {
    const noun =
      (
        {
          offloading_equipment: "d'équipement",
          material: 'matériel',
          sensor: 'capteur',
        } as Record<string, string>
      )[resourceTypeId] ?? ''
    return `${count} Objet${ending(count)} ${noun}`
  },
  deleteSelected: 'Supprimer sélectionné',
  selectASingleWorkflowToDuplicate:
    'Sélectionner un flux de travail pour dupliquer',
  subscribeSelected: 'S’abonner aux sélectionnés',
  assignSelected: 'Assigner aux sélectionnés',
  doYouWantToDeleteSelectedWorkflows:
    'Voulez-vous supprimer les flux de travail sélectionnés ?',
  allDataRelatedToSelectedWorkflowsWillBeDeleted:
    'Toutes les données liées aux flux de travail sélectionnés seront supprimées définitivement et ne pourront pas être récupérées.',
  statuses: 'statuts',
  updateSelectedWorkflowsStatusTo:
    'Mettre à jour le statut des Workflows sélectionnés vers :',
  youAreGoingToUpdate: 'Vous êtes sur le point de mettre à jour le',
  _for: 'pour',
  updateXWorkflows: (x: number) => `Mettre à jour ${x} Workflow${ending(x)}`,
  subscribeUsers: 'Abonner les utilisateurs',
  subscribed: 'Abonné',
  noUsersHaveSubscribedYet: 'Aucun utilisateur n’a encore été abonné',
  thisUserHasSubscribedToOnlySomeOfTheWorkflowsYouSelected:
    'Cet utilisateur a souscrit à seulement certains des workflows que vous avez sélectionnés.',
  subscribedTo: 'Abonné à:',
  notSubscribedTo: 'Non abonné à:',
  signature: 'Signature',
  signaturePreview: 'Aperçu de la signature',
  draw: 'Dessiner',
  typeHere: 'Tapez ici',
  addTextHere: 'Ajoutez du texte ici',
  signHere: 'Signez ici',
  editStyle: 'Modifier le style',
  clickToAddSignature: 'Cliquez pour ajouter une signature',
  newUserSignupWorkflow: "Flux d'inscription des nouveaux utilisateurs",
  whiteListDomains: 'Domaines de la liste blanche',
  whiteListDomainsTooltip:
    "Lorsqu'un domaine est ajouté à la liste blanche, il permet à tous les utilisateurs avec des adresses e-mail vérifiées de ces domaines de rejoindre votre projet StruxHub sans être spécifiquement invités.",
  whiteListDomainsTooltipNote:
    "REMARQUE : Ne mettez pas en liste blanche les domaines de messagerie génériques comme @gmail.com, @outlook.com, @iCloud.com. Cela permettrait à des utilisateurs non vérifiés d'accéder à votre projet.",
  usersCanCreateAccountByLink:
    'Les utilisateurs peuvent créer un compte via ce lien :',
  orSnappingThisQRCode: 'Ou en scannant ce QR Code :',
  print: 'Imprimer',
  noPermissionToCreateWorkflowType:
    "Désolé ! Vous n'avez pas les autorisations nécessaires pour créer ce type de flux de travail",
  approvalConfiguration: "Configuration d'approbation",
  sendBicApprovalTo: "Envoyer l'approbation BIC à",
  bicApprovalTooltip:
    "Ces utilisateurs reçoivent une notification d'approbation ball-in-court lorsque cette règle s'applique.",
  allProjectCoordinators: 'Tous les coordinateurs du projet',
  selectApprovers: 'Sélectionner les approbateurs',
  addFilter: 'Ajouter un filtre',
  onlyWhen: 'Seulement quand :',
  selectCompanyType: "Sélectionner le type d'entreprise",
  deleteRule: 'Supprimer la règle',
  deleteView: 'Supprimer la vue',
  deleteResource: 'Supprimer la ressource',
  deleteTag: "Supprimer l'étiquette",
  deleteItem: "Supprimer l'élément",
  deleteField: 'Supprimer le champ',
  deleteFieldQuestion: 'Êtes-vous sûr de vouloir supprimer le champ ?',
  deleteViewQuestion: (name: string) =>
    `Êtes-vous sûr de vouloir supprimer la vue ${name} ?`,
  removeUser: "Supprimer l'utilisateur",
  xAllowedFormsHaveBeenDeleted: (x: number) =>
    `${x} Formulaire${ending(x)} autorisé${ending(x)} ${ending(
      x,
      'a',
      'ont',
    )} été supprimé${ending(x)}`,
  confirmResourceControlChangesTitle:
    'Mettre à jour les contrôles de la ressource ?',
  resourceControlChangeAffectsWorkflows: (count: number) =>
    `Cette ressource est encore utilisée par ${count} type${ending(
      count,
    )} de workflow. Cela la retirera de chacun :`,
  cascadeConditionalFieldsRemoved: (count: number) =>
    `${count} champ${ending(count)} conditionnel${ending(
      count,
    )} supprimé${ending(count)}`,
  cascadeConditionalFiltersRemoved: (count: number) =>
    `${count} filtre${ending(count)} conditionnel${ending(
      count,
    )} supprimé${ending(count)}`,
  cascadeNoLongerSelectableInFields: (count: number) =>
    `Non sélectionnable dans ${count} champ${ending(count)}`,
  cascadeNoLongerSelectableInConditionalFilters: (count: number) =>
    `Non sélectionnable dans ${count} filtre${ending(
      count,
    )} conditionnel${ending(count)}`,
  cascadeNoLongerSelectableInConditionalFields: (count: number) =>
    `Non sélectionnable dans ${count} champ${ending(
      count,
    )} conditionnel${ending(count)}`,
  cascadeRemovedFromExclusion: (count: number) =>
    `Retirée de ${count} liste${ending(count)} d'exclusion`,
  cascadeOtherReferencesRemoved: 'Autres références supprimées',
  couldNotCheckAffectedWorkflows:
    'Impossible de vérifier les workflows concernés. Veuillez réessayer.',
  someResourcesCouldNotBeDisabled: (count: number) =>
    `Impossible de désactiver ${count} ressource${ending(
      count,
    )}. Veuillez réessayer.`,
  someResourcesCouldNotBeUpdated: (count: number) =>
    `Impossible de mettre à jour ${count} ressource${ending(
      count,
    )}. Veuillez réessayer.`,
  projectLocationDeletionProhibited:
    'La suppression de Project Location est interdite',
  projectLocationControlEditProhibited: (controlName: string) =>
    `La modification de « ${controlName} » pour Project Location est interdite`,
  projectLocationBulkEditProhibited:
    "L'édition en masse de Project Location est interdite",
  selectionHasResourcesYouCannotChange:
    "Les ressources ne peuvent être modifiées ou supprimées que par les administrateurs et leurs propriétaires (utilisateur responsable ou membres de l'entreprise responsable)",
  outsideHours: 'Hors horaires',
  closedStatus: 'Fermé',
  tooLong: 'Trop long',
  fullyBooked: 'Complet',
  reserved: 'Réservé',
  unavailable: 'Indisponible',
  closedOnWeekdays: (weekday: string) => `Fermé le ${weekday}`,
  closedOnX: (day: string) => `Fermé le ${day}`,
  onlyOpenXToY: (opens: string, closes: string) =>
    `Ouvert uniquement ${opens} – ${closes}`,
  opensAtX: (time: string) => `Ouvre à ${time}`,
  closesAtX: (time: string) => `Ferme à ${time}`,
  closesAtXReopensAtY: (closes: string, reopens: string) =>
    `Ferme à ${closes}, rouvre à ${reopens}`,
  hasBreakDuringThisBooking: 'Fait une pause pendant cette réservation',
  xToYEveryDay: (start: string, end: string) =>
    `${start} – ${end} tous les jours`,
  allDayX: (day: string) => `Toute la journée le ${day}`,
  xMinutesDuration: (x: number) => `${x} minute${ending(x)}`,
  xHoursDuration: (x: number) => `${x} heure${ending(x)}`,
  xDaysDuration: (x: number) => `${x} jour${ending(x)}`,
  xWeeksDuration: (x: number) => `${x} semaine${ending(x)}`,
  xMonthsDuration: (x: number) => `${x} mois`,
  limitIsX: (limit: string) => `Limite : ${limit}`,
  limitIsOneWorkingDay: 'Limite : un jour ouvré',
  limitIsOneWorkWeek: 'Limite : une semaine de travail',
  thisBookingIsX: (length: string) => `Cette réservation dure ${length}.`,
  listWithXMore: (items: string[], moreCount: number) =>
    moreCount
      ? `${items.join(', ')} et ${moreCount} autre${ending(moreCount)}`
      : items.join(' et '),
  aBooking: 'une réservation',
  takenByX: (bookings: string) => `Occupé par ${bookings}`,
  takenByXAtY: (booking: string, time: string) =>
    `Occupé par ${booking}, ${time}`,
  bookingWithTime: (booking: string, time: string) => `${booking} : ${time}`,
  noRoomAtThisTime: 'Plus de place à cet horaire',
  takesXBookingsAtATime: (x: number) =>
    `Accepte ${x} réservation${ending(x)} à la fois.`,
  fullyBookedOnAllXRepeats: (total: number) =>
    `Complet sur les ${total} répétitions.`,
  fullyBookedOnXOfYRepeats: (failing: number, total: number, first: string) =>
    `Complet sur ${failing} des ${total} répétitions, la première le ${first}.`,
  forX: (companies: string) => `Pour ${companies}`,
  forOtherCompanies: "Pour d'autres entreprises",
  cantBeBooked: 'Réservation impossible',
  affectsAllXRepeats: (total: number) => `Concerne les ${total} répétitions.`,
  affectsXOfYRepeats: (failing: number, total: number, first: string) =>
    `Concerne ${failing} des ${total} répétitions, la première le ${first}.`,
  projectHoursHeading: 'Heures de travail du projet :',
  usesProjectHoursHeading: 'Utilise les heures de travail du projet :',
  hoursHeading: "Heures d'ouverture :",
  noneSet: 'aucune définie',
  notRequestedXAhead: (deadline: string) =>
    `Non demandé ${deadline} à l'avance`,
  notRequestedInAdvance: "Non demandé à l'avance",
  outsideWorkingHours: 'En dehors des heures de travail',
  youCanStillApprove:
    'Vous pouvez quand même approuver. Assurez-vous que le chantier peut le permettre.',
  youCanStillSubmit:
    'Vous pouvez quand même soumettre. Votre approbateur verra ces remarques.',
  approveAnyway: 'Approuver quand même',
  submitAnyway: 'Soumettre quand même',
  xMore: (x: number) => `${x} de plus`,
}

export default FrenchTree
